import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Header from "../component/Header";
import Sidebar from "../component/Sidebar";
import ChartCard from "../component/ChartCard";
import "../css/sidebar.css";
import "../css/setTargetModal.css";
import { NavLink } from "react-router-dom";
import { Tooltip, Whisper } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { Placeholder } from "rsuite";
import LogoApple from "../images/company-logos/apple.svg";
import LogoIgne from "../images/company-logos/igne.svg";
import LogoTesla from "../images/company-logos/tesla.svg";
import LogoJNJ from "../images/company-logos/jnj.svg";
import ArrowTrendUp from "../images/arrow-trend-up.svg";
import { motion, AnimatePresence } from "framer-motion";
import { SelectPicker, Message } from "rsuite";
import { toast } from "react-toastify";
import ArrowTrendDown from "../images/arrow-trend-down.svg";
import iconPie from "../images/iconPie.svg";
import iconGraph from "../images/iconGraph.svg";
import iconMargin from "../images/iconMargin.svg";
import iconLineChart from "../images/iconLineChart.svg";
import iconWalet from "../images/iconWalet.svg";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import MarketDepthIcon from "../component/MarketDepthIcon";
import TablePageSizeSelect from "../component/TablePageSizeSelect";
import { Columns3 } from "lucide-react";
import { EllipsisVertical, ArrowUp, ArrowDown, Pin, PinOff, Expand } from "lucide-react";
import { TbTarget } from "react-icons/tb";
import SetTargetModal from "../component/SetTargetModal";
import { Modal } from "bootstrap";
import { getCurrentLanguage, t, translateColumnLabel } from "../i18n";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import fivePaisaMarketDepthStore from "../services/fivePaisaMarketDepthStore";
import angelOneWebSocketService, {
  buildAngelInstrumentFields,
  getAngelExchangeTypeFromHolding,
} from "../services/angelOneWebSocketService";
import growwLivePriceService from "../services/growwLivePriceService";
import angelOneMarketDepthService, {
  normalizeAngelMarketDepth,
} from "../services/angelOneMarketDepthService";


import {
  placeOrderViaPms,
  normalizePlaceOrderResponse,
  isFivePaisaBroker,
  runPlaceOrderAcceptedFlow,
} from "../services/placeOrderService";
import {
  selectActiveBrokerStats,
  useBrokerStatisticsStore,
} from "../stores/brokerStatisticsStore";
import { createPortal } from "react-dom";
import StockCandleModal from "../component/StockCandleModal";
import MarketDepthPopover from "../component/MarketDepthPopover";


const GROW_POLL_SOURCE = "portfolio";

const CANDLE_CHART_BROKERS = new Set(["5paisa", "angelone"]);

const supportsCandleChart = (brokerType) =>
  CANDLE_CHART_BROKERS.has(String(brokerType || "").trim().toLowerCase());

const toPmsBrokerFlag = (brokerType) => {
  const normalized = String(brokerType || "").trim().toLowerCase();
  if (normalized === "grow" || normalized === "5paisa" || normalized === "angelone") {
    return normalized;
  }
  return "5paisa";
};

function Portfolio(props) {
  return <PortfolioContent {...props} />;
}

const PORTFOLIO_PMS_COLUMN_KEY_TO_TABLE_COL = {
  name: "Name",
  current_price: "Current Price",
  trade_price: "Trade Price",
  quantity: "Quantity",
  tp_q: "Trade Price",
  profit_loss: "Profit/Loss",
  profit_pct: "Profit %",
  profit_percent: "Profit %",
  pl_percent: "Profit %",
  today_high_low: "TodaysHigh",
  todays_high: "TodaysHigh",
  todays_low: "TodaysLow",
  week_52_high_low: "52WeekHigh",
  week_52_high: "52WeekHigh",
  week_52_low: "52WeekLow",
  high_52_week: "52WeekHigh",
  low_52_week: "52WeekLow",
  initial_target_price: "Initial Price",
  initial_price: "Initial Price",
  target_price: "Target Price",
  target: "Target %",
  target_percent: "Target %",
  volume: "Volume",
};

const PORTFOLIO_TABLE_COL_LABEL_ALIASES = {
  "TP/Q": "Trade Price",
  "Today's High/Low": "TodaysHigh",
  "Today High/Low": "TodaysHigh",
  "52 Week High/Low": "52WeekHigh",
  "Profit/Loss": "Profit/Loss",
  "Initial Price / Target Price": "Initial Price",
  Target: "Target %",
};

const PORTFOLIO_TABLE_COL_TO_PMS_COLUMN_KEY = {
  ...Object.fromEntries(
    Object.entries(PORTFOLIO_PMS_COLUMN_KEY_TO_TABLE_COL).map(
      ([apiKey, tableCol]) => [tableCol, apiKey],
    ),
  ),
  TodaysHigh: "today_high_low",
  "52WeekHigh": "week_52_high_low",
  "Initial Price": "initial_target_price",
  "Target %": "target",
  "Profit %": "profit_pct",
};

function mapPortfolioPmsColumnToTableCol(columnKey, columnLabel) {
  const rawColumnKey = String(columnKey || "").trim();
  const key = String(columnKey || "").toLowerCase().trim();
  if (PORTFOLIO_PMS_COLUMN_KEY_TO_TABLE_COL[key]) {
    return PORTFOLIO_PMS_COLUMN_KEY_TO_TABLE_COL[key];
  }
  const label = String(columnLabel || "").trim();
  if (PORTFOLIO_TABLE_COL_LABEL_ALIASES[label]) {
    return PORTFOLIO_TABLE_COL_LABEL_ALIASES[label];
  }
  const known = new Set([
    "Name",
    "Profit %",
    "Trade Price",
    "Current Price",
    "Quantity",
    "Profit/Loss",
    "52WeekHigh",
    "52WeekLow",
    "Initial Price",
    "Target Price",
    "Target %",
    "TodaysHigh",
    "TodaysLow",
    "Volume",
    "Actions",
  ]);
  if (known.has(label)) return label;
  if (rawColumnKey) return rawColumnKey;
  return null;
}

function buildPortfolioTableColumnOrderFromPms(columnsPayload) {
  if (!Array.isArray(columnsPayload) || columnsPayload.length === 0)
    return null;
  const sorted = [...columnsPayload].sort(
    (a, b) => Number(a.sort_order) - Number(b.sort_order),
  );
  const out = [];
  const seen = new Set();
  for (const c of sorted) {
    const selected =
      c.is_selected === "1" || c.is_selected === 1 || c.is_selected === true;
    const active =
      c.is_active === "1" || c.is_active === 1 || c.is_active === true;
    if (!selected || !active) continue;
    const col = mapPortfolioPmsColumnToTableCol(c.column_key, c.column_label);
    if (!col || col === "Actions") continue;
    if (seen.has(col)) continue;
    seen.add(col);
    out.push(col);
  }
  if (!out.includes("Actions")) out.push("Actions");
  return out.length ? out : null;
}

function normalizeAdditionalColumnKey(key) {
  return String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function resolveAdditionalColumnValue(additionalData, colKey) {
  if (!additionalData || typeof additionalData !== "object") return undefined;
  if (additionalData[colKey] !== undefined) return additionalData[colKey];
  const targetKey = normalizeAdditionalColumnKey(colKey);
  if (!targetKey) return undefined;
  for (const [k, v] of Object.entries(additionalData)) {
    if (normalizeAdditionalColumnKey(k) === targetKey) return v;
  }
  return undefined;
}

function PortfolioContent(props) {
  // All initialization and Declaration
  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";
  const [tabName, setTabName] = useState("Portfolio");
  const [progress, setProgress] = useState(0);
  const [holdings, setHoldings] = useState([]);
  const searchInputRef = useRef(null);

  // WebSocket related state variables
  const [, setConnected] = useState(false);
  const [liveUpdateTrigger, setLiveUpdateTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // Market depth state (stores the latest order book for subscribed stock)
  const [marketDepth, setMarketDepth] = useState([]);
  const [currentMDModal, setcurrentMDModal] = useState(null);

  const [isHovered1, setIsHovered1] = useState(false);
  const [isHovered2, setIsHovered2] = useState(false);
  const [isHovered3, setIsHovered3] = useState(false);
  const [isHovered4, setIsHovered4] = useState(false);
  const portfolioStats = useBrokerStatisticsStore(selectActiveBrokerStats);
  const portfolioStatsReady = useBrokerStatisticsStore((state) => state.activeBrokerReady);

  // Inside your component
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const tr = (key) => t(key, currentLanguage);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLanguage(event?.detail?.language || getCurrentLanguage());
    };
    window.addEventListener("app-language-change", handleLanguageChange);
    return () =>
      window.removeEventListener("app-language-change", handleLanguageChange);
  }, []);

  const [orderType, setOrderType] = useState("Market Order");
  const [isPreview, setIsPreview] = useState(false);
  const OrderType = [
    { value: "Market Order", label: tr("marketOrder") },
    { value: "Limit Order", label: tr("limitOrder") },
  ];

  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
  const activeBrokerFlag = toPmsBrokerFlag(
    sharedData?.broker_type || localStorage.getItem("broker_type") || "5paisa",
  );
  const isAngelOneBroker = activeBrokerFlag === "angelone";

  const getOrderLivePriceCacheKey = (order) => {
    if (!order) return "";
    if (isAngelOneBroker) {
      return String(
        order?.token || order?.Token || order?.scripcode || order?.ScripCode || "",
      ).trim();
    }
    return (
      order?.ScripCode ||
      order?.scripcode ||
      (order?.Exch === "N" ? order?.NseCode : order?.BseCode) ||
      ""
    );
  };

  const broker_type = String(
    localStorage.getItem("broker_type") || sharedData?.broker_type || "",
  ).toLowerCase();


  // Refs for caching live data and WebSocket listeners
  const wsStatusUnsubRef = useRef(null);
  const wsPriceUnsubRef = useRef(null);
  const wsErrorUnsubRef = useRef(null);
  const mdStoreUnsubRef = useRef(null);
  const angelWsPriceUnsubRef = useRef(null);
  const angelWsErrorUnsubRef = useRef(null);
  const angelPortfolioSubscriptionsRef = useRef([]);
  const angelMdServiceUnsubRef = useRef(null);

  const [showMarketDepth, setShowMarketDepth] = useState(false);
  const [marketDepthPosition, setMarketDepthPosition] = useState({
    top: 0,
    left: 0,
  });
  const marketDepthButtonRef = useRef(null);
  const marketDepthPopoverRef = useRef(null);

  // Include click-outside useEffect + handleMarketDepthClick function

  const livePriceCache = useRef({});
  const liveHighCache = useRef({});
  const liveLowCache = useRef({});
  const livePCloseCache = useRef({});
  const liveDiffCache = useRef({});
  const liveChangePercentageCache = useRef({});
  const liveVolumeCache = useRef({});
  const livePLCache = useRef({});
  const currentMDModalRef = useRef(null);
  const portfolioFetchIdRef = useRef(0);
  const lastPortfolioFetchRunRef = useRef({ key: "", at: 0 });

  const [columns, setColumns] = useState([
    "Name",
    "Profit %",
    "Trade Price",
    "Current Price",
    "Quantity",
    "Profit/Loss",
    "52WeekHigh",
    "52WeekLow",
    "Initial Price",
    "Target Price",
    "Target %",
    "Actions",
  ]);

  // const [columns, setColumns] = useState([
  //     { key: "Name", label: "Name" },
  //     { key: "ProfitPct", label: "Profit %" },
  //     { key: "volume", label: "Volume" },
  //     { key: "TradePrice", label: "Trade Price" },
  //     { key: "CurrentPrice", label: "Current Price" },
  //     { key: "Qty", label: "Quantity" },
  //     { key: "PnL", label: "Profit/Loss" },
  //     { key: "Exchange", label: "Exchange" },
  //     { key: "High52W", label: "52WeekHigh" },
  //     { key: "Low52W", label: "52-Week Low" },
  //     { key: "PETTM", label: "P/E Ratio (TTM)" },
  //     { key: "Actions", label: "" },
  //   ]);

  const [buySellModalDetails, setBuySellModalDetails] = useState({
    FullName: "",
    Symbol: "",
    Type: "Buy", // hardcoded for buy function, will create separate for sell
    OrderQty: 1, // default 1
    LimitPrice: 0, // default 0
    order: null, // complete order object for future use
  });
  const [growwTpinErrorText, setGrowwTpinErrorText] = useState("");

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    const number = parseFloat(num);
    if (isNaN(number)) return "-";

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Drag-N-Drop
  // Add these in your component
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [columnOrder, setColumnOrder] = useState(columns); // start with columns prop or state

  const [updatePortfolioTrigger, setUpdatePortfolioTrigger] = useState(0); // start with columns prop or state

  const [targetDetails, setTargetDetails] = useState();
  const [targetReasonModalData, setTargetReasonModalData] = useState({
    stockName: "",
    reason: "",
    details: "",
  });

  const defaultColumns = [
    "Name",
    "Current Price",
    "Trade Price",
    "Profit %",
    "Quantity",
    "Profit/Loss",
    "52WeekHigh",
    "52WeekLow",
    "Initial Price",
    "Target Price",
    "Target %",
    "TodaysHigh",
    "TodaysLow",
  ];

  // Additional Columns
  // const [additionalColumnConfig, setAdditionalColumnConfig] = useState([
  //   // { colName: "52WeekHigh", UIName: "52 Week High", colStatus: false },
  //   // { colName: "52WeekHigh", UIName: "52 Week High", colStatus: false },
  //   { colName: "52WeekHighDate", UIName: "52 Week High Date", colStatus: false },
  //   // { colName: "52WeekLow", UIName: "52 Week Low", colStatus: false },
  //   { colName: "52WeekLowDate", UIName: "52 Week Low Date", colStatus: false },
  //   { colName: "analyst_recommendation_weighted_avg", UIName: "Analyst Recommendation Weighted Avg", colStatus: false },
  //   { colName: "asset_turnover", UIName: "Asset Turnover", colStatus: false },
  //   { colName: "assets_growth_1y", UIName: "Assets Growth 1 Year", colStatus: false },
  //   { colName: "assets_growth_3y", UIName: "Assets Growth 3 Years", colStatus: false },
  //   { colName: "assets_growth_5y", UIName: "Assets Growth 5 Years", colStatus: false },
  //   { colName: "assetTurnoverAnnual", UIName: "Annual Asset Turnover", colStatus: false },
  //   { colName: "assetTurnoverTTM", UIName: "Trailing Asset Turnover", colStatus: false },
  //   { colName: "avgVolume10days", UIName: "Average Volume 10 Days", colStatus: false },
  //   { colName: "avgVolume30days", UIName: "Average Volume 30 Days", colStatus: false },
  //   { colName: "beta", UIName: "Beta", colStatus: false },
  //   { colName: "beta_1y", UIName: "Beta 1 Year", colStatus: false },
  //   { colName: "bookvalue_pershare", UIName: "Book Value Per Share", colStatus: false },
  //   { colName: "bookValuePerShareAnnual", UIName: "Annual Book Value Per Share", colStatus: false },
  //   { colName: "bookValuePerShareQuarterly", UIName: "Quarterly Book Value Per Share", colStatus: false },
  //   { colName: "cash_to_debt_annual", UIName: "Annual Cash to Debt", colStatus: false },
  //   { colName: "cashFlowPerShareAnnual", UIName: "Annual Cash Flow Per Share", colStatus: false },
  //   { colName: "cashFlowPerShareQuarterly", UIName: "Quarterly Cash Flow Per Share", colStatus: false },
  //   { colName: "cashPerSharePerShareAnnual", UIName: "Annual Cash Per Share", colStatus: false },
  //   { colName: "cashPerSharePerShareQuarterly", UIName: "Quarterly Cash Per Share", colStatus: false },
  //   { colName: "cashRatio_annual", UIName: "Annual Cash Ratio", colStatus: false },
  //   { colName: "change1D", UIName: "1 Day Change", colStatus: false },
  //   { colName: "change1DPercent", UIName: "1 Day Change Percent", colStatus: false },
  //   { colName: "change1M", UIName: "1 Month Change", colStatus: false },
  //   { colName: "change1MPercent", UIName: "1 Month Change Percent", colStatus: false },
  //   { colName: "change1Y", UIName: "1 Year Change", colStatus: false },
  //   { colName: "change1YPercent", UIName: "1 Year Change Percent", colStatus: false },
  //   { colName: "change3Y", UIName: "3 Year Change", colStatus: false },
  //   { colName: "change3YPercent", UIName: "3 Year Change Percent", colStatus: false },
  //   { colName: "change5Y", UIName: "5 Year Change", colStatus: false },
  //   { colName: "change5YPercent", UIName: "5 Year Change Percent", colStatus: false },
  //   { colName: "close", UIName: "Close Price", colStatus: false },
  //   { colName: "company_country", UIName: "Company Country", colStatus: false },
  //   { colName: "company_symbol", UIName: "Company Symbol", colStatus: false },
  //   { colName: "compliantRatio", UIName: "Compliant Ratio", colStatus: false },
  //   { colName: "country", UIName: "Country", colStatus: false },
  //   { colName: "created_at", UIName: "Created At", colStatus: false },
  //   { colName: "currency", UIName: "Currency", colStatus: false },
  //   { colName: "current_ratio_annual", UIName: "Annual Current Ratio", colStatus: false },
  //   { colName: "current_ratio_quarterly", UIName: "Quarterly Current Ratio", colStatus: false },
  //   { colName: "currentAssets", UIName: "Current Assets", colStatus: false },
  //   { colName: "currentAssets_year", UIName: "Current Assets Year", colStatus: false },
  //   { colName: "currentDividendYieldTTM", UIName: "Dividend Yield TTM", colStatus: false },
  //   { colName: "currentEv_freeCashFlowAnnual", UIName: "Annual EV Free Cash Flow", colStatus: false },
  //   { colName: "currentEv_freeCashFlowTTM", UIName: "EV Free Cash Flow TTM", colStatus: false },
  //   { colName: "currentPrice", UIName: "Current Price", colStatus: false },
  //   { colName: "currentRatioAnnual", UIName: "Annual Current Ratio", colStatus: false },
  //   { colName: "currentRatioQuarterly", UIName: "Quarterly Current Ratio", colStatus: false },
  //   { colName: "debt_to_ebitda", UIName: "Debt to EBITDA", colStatus: false },
  //   { colName: "debt_to_equity_annual", UIName: "Annual Debt to Equity", colStatus: false },
  //   { colName: "debt_to_equity_quarterly", UIName: "Quarterly Debt to Equity", colStatus: false },
  //   { colName: "depreciationAmortization", UIName: "Depreciation & Amortization", colStatus: false },
  //   { colName: "dividend_payment_freq", UIName: "Dividend Payment Frequency", colStatus: false },
  //   { colName: "dividend_per_share", UIName: "Dividend Per Share", colStatus: false },
  //   { colName: "dividendPerShareAnnual", UIName: "Annual Dividend Per Share", colStatus: false },
  //   { colName: "dividendPerShareTTM", UIName: "Dividend Per Share TTM", colStatus: false },
  //   { colName: "dividendYieldIndicatedAnnual", UIName: "Annual Dividend Yield Indicated", colStatus: false },
  //   { colName: "doubtful_revenue_percent", UIName: "Doubtful Revenue Percent", colStatus: false },
  //   { colName: "ebit", UIName: "EBIT", colStatus: false },
  //   { colName: "ebit_annual", UIName: "Annual EBIT", colStatus: false },
  //   { colName: "ebit_estimate_annual", UIName: "Annual EBIT Estimate", colStatus: false },
  //   { colName: "ebit_estimate_quarterly", UIName: "Quarterly EBIT Estimate", colStatus: false },
  //   { colName: "ebit_quarterly", UIName: "Quarterly EBIT", colStatus: false },
  //   { colName: "ebitda_annual", UIName: "Annual EBITDA", colStatus: false },
  //   { colName: "ebitda_estimate_annual", UIName: "Annual EBITDA Estimate", colStatus: false },
  //   { colName: "ebitda_estimate_quarterly", UIName: "Quarterly EBITDA Estimate", colStatus: false },
  //   { colName: "ebitda_growth_1y", UIName: "EBITDA Growth 1 Year", colStatus: false },
  //   { colName: "ebitda_growth_3y", UIName: "EBITDA Growth 3 Years", colStatus: false },
  //   { colName: "ebitda_growth_5y", UIName: "EBITDA Growth 5 Years", colStatus: false },
  //   { colName: "ebitda_margin", UIName: "EBITDA Margin", colStatus: false },
  //   { colName: "ebitda_quarterly", UIName: "Quarterly EBITDA", colStatus: false },
  //   { colName: "ebitdPerShareAnnual", UIName: "Annual EBITD Per Share", colStatus: false },
  //   { colName: "ebitdPerShareTTM", UIName: "EBITD Per Share TTM", colStatus: false },
  //   { colName: "ebitPerShare", UIName: "EBIT Per Share", colStatus: false },
  //   { colName: "ebitPerShare_annual", UIName: "Annual EBIT Per Share", colStatus: false },
  //   { colName: "ebitPerShare_quarterly", UIName: "Quarterly EBIT Per Share", colStatus: false },
  //   { colName: "employeeTotal", UIName: "Total Employees", colStatus: false },
  //   { colName: "enterpriseValue", UIName: "Enterprise Value", colStatus: false },
  //   { colName: "eps_estimate_annual", UIName: "Annual EPS Estimate", colStatus: false },
  //   { colName: "eps_estimate_quarterly", UIName: "Quarterly EPS Estimate", colStatus: false },
  //   { colName: "eps_growth5Y", UIName: "EPS Growth 5 Years", colStatus: false },
  //   { colName: "eps_growth_1y", UIName: "EPS Growth 1 Year", colStatus: false },
  //   { colName: "eps_growth_3Y", UIName: "EPS Growth 3 Years", colStatus: false },
  //   { colName: "epsAnnual", UIName: "Annual EPS", colStatus: false },
  //   { colName: "epsAvg", UIName: "Average EPS", colStatus: false },
  //   { colName: "epsBasicExclExtraItemsTTM", UIName: "Basic EPS Excluding Extraordinary Items TTM", colStatus: false },
  //   { colName: "epsGrowth3Y", UIName: "EPS Growth 3 Years", colStatus: false },
  //   { colName: "epsGrowth5Y", UIName: "EPS Growth 5 Years", colStatus: false },
  //   { colName: "epsGrowthQuarterlyYoy", UIName: "EPS Quarterly Growth YoY", colStatus: false },
  //   { colName: "epsGrowthTTMYoy", UIName: "EPS TTM Growth YoY", colStatus: false },
  //   { colName: "epsNormalizedAnnual", UIName: "Annual Normalized EPS", colStatus: false },
  //   { colName: "epsTTM", UIName: "EPS Trailing Twelve Months", colStatus: false },
  //   { colName: "equity_to_assets_annual", UIName: "Equity to Assets Annual", colStatus: false },
  //   { colName: "equity_to_assets_quarterly", UIName: "Equity to Assets Quarterly", colStatus: false },
  //   { colName: "ev_ebit", UIName: "EV to EBIT", colStatus: false },
  //   { colName: "ev_ebitda", UIName: "EV to EBITDA", colStatus: false },
  //   { colName: "ev_fcf", UIName: "EV to Free Cash Flow", colStatus: false },
  //   { colName: "ev_revenue", UIName: "EV to Revenue", colStatus: false },
  //   { colName: "exchange", UIName: "Exchange", colStatus: false },
  //   { colName: "exchange_country", UIName: "Exchange Country", colStatus: false },
  //   { colName: "exchange_symbol", UIName: "Exchange Symbol", colStatus: false },
  //   { colName: "fcfMargin", UIName: "Free Cash Flow Margin", colStatus: false },
  //   { colName: "free_cashflow_margin_annual", UIName: "Annual Free Cash Flow Margin", colStatus: false },
  //   { colName: "free_cashflow_margin_quarterly", UIName: "Quarterly Free Cash Flow Margin", colStatus: false },
  //   { colName: "gross_margin_annual", UIName: "Annual Gross Margin", colStatus: false },
  //   { colName: "grossIncome_annual", UIName: "Annual Gross Income", colStatus: false },
  //   { colName: "grossIncome_quarterly", UIName: "Quarterly Gross Income", colStatus: false },
  //   { colName: "grossMarginAnnual", UIName: "Annual Gross Margin", colStatus: false },
  //   { colName: "grossMarginTTM", UIName: "Trailing Gross Margin", colStatus: false },
  //   { colName: "halal_rating", UIName: "Halal Rating", colStatus: false },
  //   { colName: "halal_revenue_percent", UIName: "Halal Revenue Percent", colStatus: false },
  //   { colName: "high", UIName: "High Price", colStatus: false },
  //   { colName: "id", UIName: "ID", colStatus: false },
  //   { colName: "industry", UIName: "Industry", colStatus: false },
  //   { colName: "insiderOwnership", UIName: "Insider Ownership", colStatus: false },
  //   { colName: "institutionOwnership", UIName: "Institution Ownership", colStatus: false },
  //   { colName: "interestbearing_debt_percent", UIName: "Interest Bearing Debt Percent", colStatus: false },
  //   { colName: "interestBearingAssetsRatio", UIName: "Interest Bearing Assets Ratio", colStatus: false },
  //   { colName: "interestBearingDebtRatio", UIName: "Interest Bearing Debt Ratio", colStatus: false },
  //   { colName: "intrestbearing_asset_percent", UIName: "Interest Bearing Asset Percent", colStatus: false },
  //   { colName: "inventory_turnover", UIName: "Inventory Turnover", colStatus: false },
  //   { colName: "inventoryTurnoverAnnual", UIName: "Annual Inventory Turnover", colStatus: false },
  //   { colName: "ipoDate", UIName: "IPO Date", colStatus: false },
  //   { colName: "isin", UIName: "ISIN", colStatus: false },
  //   { colName: "isMainTicker", UIName: "Is Main Ticker", colStatus: false },
  //   { colName: "long_term_debt_growth_1y", UIName: "Long-Term Debt Growth 1 Year", colStatus: false },
  //   { colName: "long_term_debt_growth_3y", UIName: "Long-Term Debt Growth 3 Years", colStatus: false },
  //   { colName: "long_term_debt_growth_5y", UIName: "Long-Term Debt Growth 5 Years", colStatus: false },
  //   { colName: "longTermDebt_equityAnnual", UIName: "Annual Long Term Debt to Equity", colStatus: false },
  //   { colName: "longTermDebt_equityQuarterly", UIName: "Quarterly Long Term Debt to Equity", colStatus: false },
  //   { colName: "longtermDebtTotalAsset_annual", UIName: "Annual Longterm Debt to Total Asset", colStatus: false },
  //   { colName: "low", UIName: "Low Price", colStatus: false },
  //   { colName: "marketcap", UIName: "Market Capitalization", colStatus: false },
  //   { colName: "marketCap_change_3y", UIName: "Market Cap Change 3 Years", colStatus: false },
  //   { colName: "marketCapChange1y", UIName: "Market Cap Change 1 Year", colStatus: false },
  //   { colName: "marketCapChange3y", UIName: "Market Cap Change 3 Years", colStatus: false },
  //   { colName: "marketCapClassification", UIName: "Market Cap Classification", colStatus: false },
  //   { colName: "musaffa_esg_ranking", UIName: "Musaffa ESG Ranking", colStatus: false },
  //   { colName: "musaffa_esg_score", UIName: "Musaffa ESG Score", colStatus: false },
  //   { colName: "musaffaHalalRating", UIName: "Musaffa Halal Rating", colStatus: false },
  //   { colName: "musaffaIndustry", UIName: "Musaffa Industry", colStatus: false },
  //   { colName: "musaffaSector", UIName: "Musaffa Sector", colStatus: false },
  //   { colName: "net_income_annual", UIName: "Annual Net Income", colStatus: false },
  //   { colName: "net_income_quarterly", UIName: "Quarterly Net Income", colStatus: false },
  //   { colName: "netinterest_coverage", UIName: "Net Interest Coverage", colStatus: false },
  //   { colName: "netInterestCoverageAnnual", UIName: "Annual Net Interest Coverage", colStatus: false },
  //   { colName: "netInterestCoverageTTM", UIName: "Net Interest Coverage TTM", colStatus: false },
  //   { colName: "netprofit_margin", UIName: "Net Profit Margin", colStatus: false },
  //   { colName: "netProfitMarginAnnual", UIName: "Annual Net Profit Margin", colStatus: false },
  //   { colName: "netProfitMarginTTM", UIName: "Net Profit Margin TTM", colStatus: false },
  //   { colName: "next_earnings_date", UIName: "Next Earnings Date", colStatus: false },
  //   { colName: "nonCompliantRatio", UIName: "Non-Compliant Ratio", colStatus: false },
  //   { colName: "nothalal_revenue_percent", UIName: "Non-Halal Revenue Percent", colStatus: false },
  //   { colName: "of_insider_buys", UIName: "Insider Buys Percent", colStatus: false },
  //   { colName: "of_insider_sells", UIName: "Insider Sells Percent", colStatus: false },
  //   { colName: "of_shares_buys", UIName: "Shares Buys Percent", colStatus: false },
  //   { colName: "of_shares_sells", UIName: "Shares Sells Percent", colStatus: false },
  //   { colName: "open", UIName: "Open Price", colStatus: false },
  //   { colName: "OperatingIncome_annual", UIName: "Annual Operating Income", colStatus: false },
  //   { colName: "operatingMargin", UIName: "Operating Margin", colStatus: false },
  //   { colName: "operatingMarginAnnual", UIName: "Annual Operating Margin", colStatus: false },
  //   { colName: "operatingMarginTTM", UIName: "Operating Margin TTM", colStatus: false },
  //   { colName: "payoutRatioTTM", UIName: "Payout Ratio TTM", colStatus: false },
  //   { colName: "pb_ratio_annual", UIName: "Annual PB Ratio", colStatus: false },
  //   { colName: "pb_ratio_quarterly", UIName: "Quarterly PB Ratio", colStatus: false },
  //   { colName: "pbAnnual", UIName: "Annual PB", colStatus: false },
  //   { colName: "pbQuarterly", UIName: "Quarterly PB", colStatus: false },
  //   { colName: "pc", UIName: "Price to Cash Flow", colStatus: false },
  //   { colName: "pcfShareTTM", UIName: "PCF Per Share TTM", colStatus: false },
  //   { colName: "peAnnual", UIName: "Annual PE", colStatus: false },
  //   { colName: "peBasicExclExtraTTM", UIName: "Basic PE Excluding Extraordinary TTM", colStatus: false },
  //   { colName: "peExclExtraAnnual", UIName: "Annual PE Excluding Extraordinary", colStatus: false },
  //   { colName: "peExclExtraTTM", UIName: "PE Excluding Extraordinary TTM", colStatus: false },
  //   { colName: "peInclExtraTTM", UIName: "PE Including Extraordinary TTM", colStatus: false },
  //   { colName: "peNormalizedAnnual", UIName: "Annual Normalized PE", colStatus: false },
  //   { colName: "peTTM", UIName: "PE TTM", colStatus: false },
  //   { colName: "pfcfShareAnnual", UIName: "Annual PFCF Per Share", colStatus: false },
  //   { colName: "pfcfShareTTM", UIName: "PFCF Per Share TTM", colStatus: false },
  //   { colName: "pfcp_ratio_annual", UIName: "Annual PFCP Ratio", colStatus: false },
  //   { colName: "preTaxMargin", UIName: "Pre-Tax Margin", colStatus: false },
  //   { colName: "pretaxMarginAnnual", UIName: "Annual Pre-Tax Margin", colStatus: false },
  //   { colName: "previous_close", UIName: "Previous Close", colStatus: false },
  //   { colName: "price_tangiblebook_value_annual", UIName: "Annual Price Tangible Book Value", colStatus: false },
  //   { colName: "price_tangiblebook_value_quarterly", UIName: "Quarterly Price Tangible Book Value", colStatus: false },
  //   { colName: "price_to_tangible_book_ratio_annual", UIName: "Annual Price to Tangible Book Ratio", colStatus: false },
  //   { colName: "price_to_tangible_book_ratio_quarterly", UIName: "Quarterly Price to Tangible Book Ratio", colStatus: false },
  //   { colName: "priceChange1M", UIName: "Price Change 1 Month", colStatus: false },
  //   { colName: "priceChange1MPercent", UIName: "Price Change 1 Month Percent", colStatus: false },
  //   { colName: "priceChange1W", UIName: "Price Change 1 Week", colStatus: false },
  //   { colName: "priceChange1WPercent", UIName: "Price Change 1 Week Percent", colStatus: false },
  //   { colName: "priceChange3M", UIName: "Price Change 3 Months", colStatus: false },
  //   { colName: "priceChange3MPercent", UIName: "Price Change 3 Months Percent", colStatus: false },
  //   { colName: "priceChange6M", UIName: "Price Change 6 Months", colStatus: false },
  //   { colName: "priceChange6MPercent", UIName: "Price Change 6 Months Percent", colStatus: false },
  //   { colName: "priceChangeYTD", UIName: "Price Change Year to Date", colStatus: false },
  //   { colName: "priceChangeYTDPercent", UIName: "Price Change Year to Date Percent", colStatus: false },
  //   { colName: "priceLastUpdated", UIName: "Price Last Updated", colStatus: false },
  //   { colName: "priceTargetMedian", UIName: "Price Target Median", colStatus: false },
  //   { colName: "priceTargetUpside", UIName: "Price Target Upside", colStatus: false },
  //   { colName: "ps_annual_ratio", UIName: "Annual Price to Sales Ratio", colStatus: false },
  //   { colName: "psAnnual", UIName: "Annual Price to Sales", colStatus: false },
  //   { colName: "psTTM", UIName: "Price to Sales TTM", colStatus: false },
  //   { colName: "ptbvAnnual", UIName: "Annual Price to Tangible Book Value", colStatus: false },
  //   { colName: "ptbvQuarterly", UIName: "Quarterly Price to Tangible Book Value", colStatus: false },
  //   { colName: "questionableRatio", UIName: "Questionable Ratio", colStatus: false },
  //   { colName: "quick_ratio_annual", UIName: "Annual Quick Ratio", colStatus: false },
  //   { colName: "quick_ratio_quarterly", UIName: "Quarterly Quick Ratio", colStatus: false },
  //   { colName: "quickRatioAnnual", UIName: "Annual Quick Ratio", colStatus: false },
  //   { colName: "quickRatioQuarterly", UIName: "Quarterly Quick Ratio", colStatus: false },
  //   { colName: "ranking", UIName: "Ranking", colStatus: false },
  //   { colName: "receivablesTurnoverTTM", UIName: "Receivables Turnover TTM", colStatus: false },
  //   { colName: "recommendationWeightedAverage", UIName: "Recommendation Weighted Average", colStatus: false },
  //   { colName: "revenue_annual", UIName: "Annual Revenue", colStatus: false },
  //   { colName: "revenue_estimate_annual", UIName: "Annual Revenue Estimate", colStatus: false },
  //   { colName: "revenue_estimate_quarterly", UIName: "Quarterly Revenue Estimate", colStatus: false },
  //   { colName: "revenue_quarterly", UIName: "Quarterly Revenue", colStatus: false },
  //   { colName: "revenueGrowth1Y", UIName: "Revenue Growth 1 Year", colStatus: false },
  //   { colName: "revenueGrowth3Y", UIName: "Revenue Growth 3 Years", colStatus: false },
  //   { colName: "revenueGrowth5Y", UIName: "Revenue Growth 5 Years", colStatus: false },
  //   { colName: "revenueGrowthQuarterlyYoy", UIName: "Quarterly Revenue Growth YoY", colStatus: false },
  //   { colName: "revenueGrowthTTMYoy", UIName: "Revenue Growth TTM YoY", colStatus: false },
  //   { colName: "revenuePerShareAnnual", UIName: "Annual Revenue Per Share", colStatus: false },
  //   { colName: "revenuePerShareTTM", UIName: "Revenue Per Share TTM", colStatus: false },
  //   { colName: "revenueShareGrowth5Y", UIName: "Revenue Share Growth 5 Years", colStatus: false },
  //   { colName: "roa5Y", UIName: "Return on Assets 5 Years", colStatus: false },
  //   { colName: "roaQuarterly", UIName: "Return on Assets Quarterly", colStatus: false },
  //   { colName: "roaRfy", UIName: "Return on Assets Rfy", colStatus: false },
  //   { colName: "roaTTM", UIName: "Return on Assets TTM", colStatus: false },
  //   { colName: "ROE", UIName: "Return on Equity", colStatus: false },
  //   { colName: "roe5Y", UIName: "Return on Equity 5 Years", colStatus: false },
  //   { colName: "roeQuarterly", UIName: "Return on Equity Quarterly", colStatus: false },
  //   { colName: "roi", UIName: "Return on Investment", colStatus: false },
  //   { colName: "roiAnnual", UIName: "Annual Return on Investment", colStatus: false },
  //   { colName: "rotc_annual", UIName: "Annual Return on Tangible Capital", colStatus: false },
  //   { colName: "salesPerShare_annual", UIName: "Annual Sales Per Share", colStatus: false },
  //   { colName: "sector", UIName: "Sector", colStatus: false },
  //   { colName: "sharesOutStanding", UIName: "Shares Outstanding", colStatus: false },
  //   { colName: "sharia_compliance", UIName: "Sharia Compliance", colStatus: false },
  //   { colName: "shariahCompliantStatus", UIName: "Shariah Compliant Status", colStatus: false },
  //   { colName: "status", UIName: "Status", colStatus: false },
  //   { colName: "surprisePercent", UIName: "Earnings Surprise Percent", colStatus: false },
  //   { colName: "tangibleBookValuePerShareAnnual", UIName: "Annual Tangible Book Value Per Share", colStatus: false },
  //   { colName: "tangibleBookValuePerShareQuarterly", UIName: "Quarterly Tangible Book Value Per Share", colStatus: false },
  //   { colName: "targetMedian", UIName: "Target Median Price", colStatus: false },
  //   { colName: "ticker", UIName: "Ticker", colStatus: false },
  //   { colName: "total_debt_growth_1y", UIName: "Total Debt Growth 1 Year", colStatus: false },
  //   { colName: "total_debt_growth_3y", UIName: "Total Debt Growth 3 Years", colStatus: false },
  //   { colName: "total_debt_growth_5y", UIName: "Total Debt Growth 5 Years", colStatus: false },
  //   { colName: "total_value_bought", UIName: "Total Value Bought", colStatus: false },
  //   { colName: "total_value_sold", UIName: "Total Value Sold", colStatus: false },
  //   { colName: "totalDebt_totalEquityAnnual", UIName: "Annual Total Debt to Equity", colStatus: false },
  //   { colName: "totalDebt_totalEquityQuarterly", UIName: "Quarterly Total Debt to Equity", colStatus: false },
  //   { colName: "totalReturn_1M", UIName: "Total Return 1 Month", colStatus: false },
  //   { colName: "totalReturn_1W", UIName: "Total Return 1 Week", colStatus: false },
  //   { colName: "totalReturn_1Y", UIName: "Total Return 1 Year", colStatus: false },
  //   { colName: "totalReturn_3M", UIName: "Total Return 3 Months", colStatus: false },
  //   { colName: "totalReturn_3Y", UIName: "Total Return 3 Years", colStatus: false },
  //   { colName: "totalReturn_5Y", UIName: "Total Return 5 Years", colStatus: false },
  //   { colName: "totalReturn_6M", UIName: "Total Return 6 Months", colStatus: false },
  //   { colName: "totalReturn_YTD", UIName: "Total Return Year to Date", colStatus: false },
  //   { colName: "updated_at", UIName: "Updated At", colStatus: false },
  //   { colName: "usdMarketCap", UIName: "USD Market Capitalization", colStatus: false },
  //   { colName: "viewCount", UIName: "View Count", colStatus: false },
  //   { colName: "volume", UIName: "Volume", colStatus: false }
  // ])

  const [additionalColumnConfig, setAdditionalColumnConfig] = useState([
    { colName: "52WeekHighDate", UIName: "52 Week High Date", colStatus: false },
    { colName: "52WeekLowDate", UIName: "52 Week Low Date", colStatus: false },
    { colName: "volume", UIName: "Volume", colStatus: false },
    { colName: "avgVolume10days", UIName: "Average Volume 10 Days", colStatus: false },
    { colName: "avgVolume30days", UIName: "Average Volume 30 Days", colStatus: false },
    { colName: "marketcap", UIName: "Market Capitalization", colStatus: false },
    { colName: "usdMarketCap", UIName: "USD Market Capitalization", colStatus: false },
    { colName: "peTTM", UIName: "PE TTM", colStatus: false },
    { colName: "peAnnual", UIName: "Annual PE", colStatus: false },
    { colName: "pbAnnual", UIName: "Annual PB", colStatus: false },
    { colName: "epsTTM", UIName: "EPS Trailing Twelve Months", colStatus: false },
    { colName: "epsAnnual", UIName: "Annual EPS", colStatus: false },
    { colName: "dividendPerShareTTM", UIName: "Dividend Per Share TTM", colStatus: false },
    { colName: "dividendPerShareAnnual", UIName: "Annual Dividend Per Share", colStatus: false },
    { colName: "currentDividendYieldTTM", UIName: "Dividend Yield TTM", colStatus: false },
    { colName: "sector", UIName: "Sector", colStatus: false },
    { colName: "industry", UIName: "Industry", colStatus: false },
    { colName: "company_symbol", UIName: "Company Symbol", colStatus: false },
    { colName: "exchange", UIName: "Exchange", colStatus: false },
    { colName: "change1D", UIName: "1 Day Change", colStatus: false },
    { colName: "change1DPercent", UIName: "1 Day Change Percent", colStatus: false },
    { colName: "bookValuePerShareAnnual", UIName: "Annual Book Value Per Share", colStatus: false },
    { colName: "beta", UIName: "Beta", colStatus: false },
    { colName: "ipoDate", UIName: "IPO Date", colStatus: false },
    { colName: "employeeTotal", UIName: "Total Employees", colStatus: false },
    // { colName: "id", UIName: "ID", colStatus: false },
    { colName: "isin", UIName: "ISIN", colStatus: false },
    { colName: "ticker", UIName: "Ticker", colStatus: false },
    { colName: "created_at", UIName: "Created At", colStatus: false },
    { colName: "updated_at", UIName: "Updated At", colStatus: false },
    { colName: "currency", UIName: "Currency", colStatus: false },
    { colName: "country", UIName: "Country", colStatus: false },
    { colName: "company_country", UIName: "Company Country", colStatus: false },
    { colName: "exchange_country", UIName: "Exchange Country", colStatus: false },
    { colName: "exchange_symbol", UIName: "Exchange Symbol", colStatus: false },
    { colName: "analyst_recommendation_weighted_avg", UIName: "Analyst Recommendation Weighted Avg", colStatus: false },
    { colName: "ranking", UIName: "Ranking", colStatus: false },
    { colName: "recommendationWeightedAverage", UIName: "Recommendation Weighted Average", colStatus: false },
    { colName: "assets_growth_1y", UIName: "Assets Growth 1 Year", colStatus: false },
    { colName: "assets_growth_3y", UIName: "Assets Growth 3 Years", colStatus: false },
    { colName: "assets_growth_5y", UIName: "Assets Growth 5 Years", colStatus: false },
    { colName: "cashFlowPerShareAnnual", UIName: "Annual Cash Flow Per Share", colStatus: false },
    { colName: "cashFlowPerShareQuarterly", UIName: "Quarterly Cash Flow Per Share", colStatus: false },
    { colName: "cashPerSharePerShareAnnual", UIName: "Annual Cash Per Share", colStatus: false },
    { colName: "cashPerSharePerShareQuarterly", UIName: "Quarterly Cash Per Share", colStatus: false },
    { colName: "bookValuePerShareQuarterly", UIName: "Quarterly Book Value Per Share", colStatus: false },
    { colName: "currentAssets", UIName: "Current Assets", colStatus: false },
    { colName: "currentAssets_year", UIName: "Current Assets Year", colStatus: false },
    { colName: "dividendYieldIndicatedAnnual", UIName: "Annual Dividend Yield Indicated", colStatus: false },
    { colName: "doubtful_revenue_percent", UIName: "Doubtful Revenue Percent", colStatus: false },
    { colName: "ebit_estimate_annual", UIName: "Annual EBIT Estimate", colStatus: false },
    { colName: "ebitdPerShareAnnual", UIName: "Annual EBITD Per Share", colStatus: false },
    { colName: "ebitdPerShareTTM", UIName: "EBITD Per Share TTM", colStatus: false },
    { colName: "enterpriseValue", UIName: "Enterprise Value", colStatus: false },
    { colName: "epsBasicExclExtraItemsTTM", UIName: "Basic EPS Excluding Extraordinary Items TTM", colStatus: false },
    { colName: "epsGrowth3Y", UIName: "EPS Growth 3 Years", colStatus: false },
    { colName: "epsGrowth5Y", UIName: "EPS Growth 5 Years", colStatus: false },
    { colName: "epsGrowthQuarterlyYoy", UIName: "EPS Quarterly Growth YoY", colStatus: false },
    { colName: "epsGrowthTTMYoy", UIName: "EPS TTM Growth YoY", colStatus: false },
    { colName: "epsNormalizedAnnual", UIName: "Annual Normalized EPS", colStatus: false },
    { colName: "equity_to_assets_annual", UIName: "Equity to Assets Annual", colStatus: false },
    { colName: "equity_to_assets_quarterly", UIName: "Equity to Assets Quarterly", colStatus: false },
    { colName: "ev_fcf", UIName: "EV to Free Cash Flow", colStatus: false },
    { colName: "halal_revenue_percent", UIName: "Halal Revenue Percent", colStatus: false },
    { colName: "insiderOwnership", UIName: "Insider Ownership", colStatus: false },
    { colName: "institutionOwnership", UIName: "Institution Ownership", colStatus: false },
    { colName: "interestBearingAssetsRatio", UIName: "Interest Bearing Assets Ratio", colStatus: false },
    { colName: "interestBearingDebtRatio", UIName: "Interest Bearing Debt Ratio", colStatus: false },
    { colName: "intrestbearing_asset_percent", UIName: "Interest Bearing Asset Percent", colStatus: false },
    { colName: "long_term_debt_growth_1y", UIName: "Long-Term Debt Growth 1 Year", colStatus: false },
    { colName: "long_term_debt_growth_3y", UIName: "Long-Term Debt Growth 3 Years", colStatus: false },
    { colName: "long_term_debt_growth_5y", UIName: "Long-Term Debt Growth 5 Years", colStatus: false },
    { colName: "longTermDebt_equityAnnual", UIName: "Annual Long Term Debt to Equity", colStatus: false },
    { colName: "longTermDebt_equityQuarterly", UIName: "Quarterly Long Term Debt to Equity", colStatus: false },
    { colName: "marketCap_change_3y", UIName: "Market Cap Change 3 Years", colStatus: false },
    { colName: "marketCapClassification", UIName: "Market Cap Classification", colStatus: false },
    { colName: "musaffa_esg_ranking", UIName: "Musaffa ESG Ranking", colStatus: false },
    { colName: "musaffa_esg_score", UIName: "Musaffa ESG Score", colStatus: false },
    { colName: "musaffaHalalRating", UIName: "Musaffa Halal Rating", colStatus: false },
    { colName: "musaffaIndustry", UIName: "Musaffa Industry", colStatus: false },
    { colName: "musaffaSector", UIName: "Musaffa Sector", colStatus: false },
    { colName: "net_income_annual", UIName: "Annual Net Income", colStatus: false },
    { colName: "net_income_quarterly", UIName: "Quarterly Net Income", colStatus: false },
    { colName: "netProfitMarginAnnual", UIName: "Annual Net Profit Margin", colStatus: false },
    { colName: "netProfitMarginTTM", UIName: "Net Profit Margin TTM", colStatus: false },
    { colName: "next_earnings_date", UIName: "Next Earnings Date", colStatus: false },
    { colName: "nothalal_revenue_percent", UIName: "Non-Halal Revenue Percent", colStatus: false },
    { colName: "operatingMarginAnnual", UIName: "Annual Operating Margin", colStatus: false },
    { colName: "operatingMarginTTM", UIName: "Operating Margin TTM", colStatus: false },
    { colName: "payoutRatioTTM", UIName: "Payout Ratio TTM", colStatus: false },
    { colName: "pbQuarterly", UIName: "Quarterly PB", colStatus: false },
    { colName: "pc", UIName: "Price to Cash Flow", colStatus: false },
    { colName: "pcfShareTTM", UIName: "PCF Per Share TTM", colStatus: false },
    { colName: "peBasicExclExtraTTM", UIName: "Basic PE Excluding Extraordinary TTM", colStatus: false },
    { colName: "peExclExtraAnnual", UIName: "Annual PE Excluding Extraordinary", colStatus: false },
    { colName: "peExclExtraTTM", UIName: "PE Excluding Extraordinary TTM", colStatus: false },
    { colName: "peInclExtraTTM", UIName: "PE Including Extraordinary TTM", colStatus: false },
    { colName: "peNormalizedAnnual", UIName: "Annual Normalized PE", colStatus: false },
    { colName: "pfcfShareAnnual", UIName: "Annual PFCF Per Share", colStatus: false },
    { colName: "pfcfShareTTM", UIName: "PFCF Per Share TTM", colStatus: false },
    { colName: "pretaxMarginAnnual", UIName: "Annual Pre-Tax Margin", colStatus: false },
    { colName: "price_tangiblebook_value_annual", UIName: "Annual Price Tangible Book Value", colStatus: false },
    { colName: "price_tangiblebook_value_quarterly", UIName: "Quarterly Price Tangible Book Value", colStatus: false },
    { colName: "price_to_tangible_book_ratio_annual", UIName: "Annual Price to Tangible Book Ratio", colStatus: false },
    { colName: "price_to_tangible_book_ratio_quarterly", UIName: "Quarterly Price to Tangible Book Ratio", colStatus: false },
    { colName: "priceChange1M", UIName: "Price Change 1 Month", colStatus: false },
    { colName: "priceChange1MPercent", UIName: "Price Change 1 Month Percent", colStatus: false },
    { colName: "priceChange1W", UIName: "Price Change 1 Week", colStatus: false },
    { colName: "priceChange1WPercent", UIName: "Price Change 1 Week Percent", colStatus: false },
    { colName: "priceChange3M", UIName: "Price Change 3 Months", colStatus: false },
    { colName: "priceChange3MPercent", UIName: "Price Change 3 Months Percent", colStatus: false },
    { colName: "priceChange6M", UIName: "Price Change 6 Months", colStatus: false },
    { colName: "priceChange6MPercent", UIName: "Price Change 6 Months Percent", colStatus: false },
    { colName: "priceChangeYTD", UIName: "Price Change Year to Date", colStatus: false },
    { colName: "priceChangeYTDPercent", UIName: "Price Change Year to Date Percent", colStatus: false },
    { colName: "priceLastUpdated", UIName: "Price Last Updated", colStatus: false },
    { colName: "psAnnual", UIName: "Annual Price to Sales", colStatus: false },
    { colName: "psTTM", UIName: "Price to Sales TTM", colStatus: false },
    { colName: "ptbvAnnual", UIName: "Annual Price to Tangible Book Value", colStatus: false },
    { colName: "ptbvQuarterly", UIName: "Quarterly Price to Tangible Book Value", colStatus: false },
    { colName: "revenue_estimate_annual", UIName: "Annual Revenue Estimate", colStatus: false },
    { colName: "revenue_estimate_quarterly", UIName: "Quarterly Revenue Estimate", colStatus: false },
    { colName: "revenueGrowthQuarterlyYoy", UIName: "Quarterly Revenue Growth YoY", colStatus: false },
    { colName: "revenueGrowthTTMYoy", UIName: "Revenue Growth TTM YoY", colStatus: false },
    { colName: "revenuePerShareAnnual", UIName: "Annual Revenue Per Share", colStatus: false },
    { colName: "revenuePerShareTTM", UIName: "Revenue Per Share TTM", colStatus: false },
    { colName: "revenueShareGrowth5Y", UIName: "Revenue Share Growth 5 Years", colStatus: false },
    { colName: "roa5Y", UIName: "Return on Assets 5 Years", colStatus: false },
    { colName: "roaRfy", UIName: "Return on Assets Rfy", colStatus: false },
    { colName: "roaTTM", UIName: "Return on Assets TTM", colStatus: false },
    { colName: "ROE", UIName: "Return on Equity", colStatus: false },
    { colName: "roe5Y", UIName: "Return on Equity 5 Years", colStatus: false },
    { colName: "roiAnnual", UIName: "Annual Return on Investment", colStatus: false },
    { colName: "sharesOutStanding", UIName: "Shares Outstanding", colStatus: false },
    { colName: "sharia_compliance", UIName: "Sharia Compliance", colStatus: false },
    { colName: "status", UIName: "Status", colStatus: false },
    { colName: "tangibleBookValuePerShareAnnual", UIName: "Annual Tangible Book Value Per Share", colStatus: false },
    { colName: "tangibleBookValuePerShareQuarterly", UIName: "Quarterly Tangible Book Value Per Share", colStatus: false },
    { colName: "total_debt_growth_1y", UIName: "Total Debt Growth 1 Year", colStatus: false },
    { colName: "total_debt_growth_3y", UIName: "Total Debt Growth 3 Years", colStatus: false },
    { colName: "total_debt_growth_5y", UIName: "Total Debt Growth 5 Years", colStatus: false },
    { colName: "totalDebt_totalEquityAnnual", UIName: "Annual Total Debt to Equity", colStatus: false },
    { colName: "totalDebt_totalEquityQuarterly", UIName: "Quarterly Total Debt to Equity", colStatus: false },
    { colName: "totalReturn_1W", UIName: "Total Return 1 Week", colStatus: false },
    { colName: "totalReturn_3Y", UIName: "Total Return 3 Years", colStatus: false },
    { colName: "totalReturn_5Y", UIName: "Total Return 5 Years", colStatus: false },
    { colName: "totalReturn_YTD", UIName: "Total Return Year to Date", colStatus: false },
    //  { "colName": "currentPrice", UIName: "Current Price", colStatus: false },
    //   { "colName": "high", UIName: "High Price", colStatus: false },
    //   { "colName": "low", UIName: "Low Price", colStatus: false },
    //   { colName: "roaQuarterly", UIName: "Return on Assets Quarterly", colStatus: false },
    //   { colName: "ev_ebit", UIName: "EV to EBIT", colStatus: false },
    //   { colName: "ev_ebitda", UIName: "EV to EBITDA", colStatus: false },
    //   { colName: "ev_revenue", UIName: "EV to Revenue", colStatus: false },
    //   { colName: "fcfMargin", UIName: "Free Cash Flow Margin", colStatus: false },
    //   { colName: "free_cashflow_margin_annual", UIName: "Annual Free Cash Flow Margin", colStatus: false },
    //   { colName: "free_cashflow_margin_quarterly", UIName: "Quarterly Free Cash Flow Margin", colStatus: false },
    //   { colName: "gross_margin_annual", UIName: "Annual Gross Margin", colStatus: false },
    //   { colName: "grossIncome_annual", UIName: "Annual Gross Income", colStatus: false },
    //   { colName: "grossIncome_quarterly", UIName: "Quarterly Gross Income", colStatus: false },
    //   { colName: "grossMarginAnnual", UIName: "Annual Gross Margin", colStatus: false },
    //   { colName: "grossMarginTTM", UIName: "Trailing Gross Margin", colStatus: false },
    //   { colName: "ebit", UIName: "EBIT", colStatus: false },
    //   { colName: "ebit_annual", UIName: "Annual EBIT", colStatus: false },
    //   { colName: "ebit_estimate_quarterly", UIName: "Quarterly EBIT Estimate", colStatus: false },
    //   { colName: "ebit_quarterly", UIName: "Quarterly EBIT", colStatus: false },
    //   { colName: "ebitda_annual", UIName: "Annual EBITDA", colStatus: false },
    //   { colName: "ebitda_estimate_annual", UIName: "Annual EBITDA Estimate", colStatus: false },
    //   { colName: "ebitda_estimate_quarterly", UIName: "Quarterly EBITDA Estimate", colStatus: false },
    //   { colName: "ebitda_growth_1y", UIName: "EBITDA Growth 1 Year", colStatus: false },
    //   { colName: "ebitda_growth_3y", UIName: "EBITDA Growth 3 Years", colStatus: false },
    //   { colName: "ebitda_growth_5y", UIName: "EBITDA Growth 5 Years", colStatus: false },
    //   { colName: "ebitda_margin", UIName: "EBITDA Margin", colStatus: false },
    //   { colName: "ebitda_quarterly", UIName: "Quarterly EBITDA", colStatus: false },
    //   { colName: "ebitPerShare", UIName: "EBIT Per Share", colStatus: false },
    //   { colName: "ebitPerShare_annual", UIName: "Annual EBIT Per Share", colStatus: false },
    //   { colName: "ebitPerShare_quarterly", UIName: "Quarterly EBIT Per Share", colStatus: false },
    //   { colName: "eps_estimate_annual", UIName: "Annual EPS Estimate", colStatus: false },
    //   { colName: "eps_estimate_quarterly", UIName: "Quarterly EPS Estimate", colStatus: false },
    //   { colName: "eps_growth5Y", UIName: "EPS Growth 5 Years", colStatus: false },
    //   { colName: "eps_growth_1y", UIName: "EPS Growth 1 Year", colStatus: false },
    //   { colName: "eps_growth_3Y", UIName: "EPS Growth 3 Years", colStatus: false },
    //   { colName: "epsAvg", UIName: "Average EPS", colStatus: false },
    //   { colName: "halal_rating", UIName: "Halal Rating", colStatus: false },
    //   { colName: "roeQuarterly", UIName: "Return on Equity Quarterly", colStatus: false },
    //   { colName: "netinterest_coverage", UIName: "Net Interest Coverage", colStatus: false },
    //   { colName: "netInterestCoverageAnnual", UIName: "Annual Net Interest Coverage", colStatus: false },
    //   { colName: "netInterestCoverageTTM", UIName: "Net Interest Coverage TTM", colStatus: false },
    //   { colName: "netprofit_margin", UIName: "Net Profit Margin", colStatus: false },
    //   { colName: "nonCompliantRatio", UIName: "Non-Compliant Ratio", colStatus: false },
    //   { colName: "of_insider_buys", UIName: "Insider Buys Percent", colStatus: false },
    //   { colName: "of_insider_sells", UIName: "Insider Sells Percent", colStatus: false },
    //   { colName: "of_shares_buys", UIName: "Shares Buys Percent", colStatus: false },
    //   { colName: "of_shares_sells", UIName: "Shares Sells Percent", colStatus: false },
    //   { colName: "OperatingIncome_annual", UIName: "Annual Operating Income", colStatus: false },
    //   { colName: "interestbearing_debt_percent", UIName: "Interest Bearing Debt Percent", colStatus: false },
    //   { colName: "asset_turnover", UIName: "Asset Turnover", colStatus: false },
    //   { colName: "assetTurnoverAnnual", UIName: "Annual Asset Turnover", colStatus: false },
    //   { colName: "assetTurnoverTTM", UIName: "Trailing Asset Turnover", colStatus: false },
    //   { colName: "beta_1y", UIName: "Beta 1 Year", colStatus: false },
    //   { colName: "bookvalue_pershare", UIName: "Book Value Per Share", colStatus: false },
    //   { colName: "cash_to_debt_annual", UIName: "Annual Cash to Debt", colStatus: false },
    //   { colName: "cashRatio_annual", UIName: "Annual Cash Ratio", colStatus: false },
    //   { colName: "change1M", UIName: "1 Month Change", colStatus: false },
    //   { colName: "change1MPercent", UIName: "1 Month Change Percent", colStatus: false },
    //   { colName: "change1Y", UIName: "1 Year Change", colStatus: false },
    //   { colName: "change1YPercent", UIName: "1 Year Change Percent", colStatus: false },
    //   { colName: "change3Y", UIName: "3 Year Change", colStatus: false },
    //   { colName: "change3YPercent", UIName: "3 Year Change Percent", colStatus: false },
    //   { colName: "change5Y", UIName: "5 Year Change", colStatus: false },
    //   { colName: "change5YPercent", UIName: "5 Year Change Percent", colStatus: false },
    //   { colName: "compliantRatio", UIName: "Compliant Ratio", colStatus: false },
    //   { colName: "current_ratio_annual", UIName: "Annual Current Ratio", colStatus: false },
    //   { colName: "current_ratio_quarterly", UIName: "Quarterly Current Ratio", colStatus: false },
    //   { colName: "currentRatioAnnual", UIName: "Annual Current Ratio", colStatus: false },
    //   { colName: "currentRatioQuarterly", UIName: "Quarterly Current Ratio", colStatus: false },
    //   { colName: "debt_to_ebitda", UIName: "Debt to EBITDA", colStatus: false },
    //   { colName: "debt_to_equity_annual", UIName: "Annual Debt to Equity", colStatus: false },
    //   { colName: "debt_to_equity_quarterly", UIName: "Quarterly Debt to Equity", colStatus: false },
    //   { colName: "depreciationAmortization", UIName: "Depreciation & Amortization", colStatus: false },
    //   { colName: "dividend_payment_freq", UIName: "Dividend Payment Frequency", colStatus: false },
    //   { colName: "dividend_per_share", UIName: "Dividend Per Share", colStatus: false },
    //   { colName: "inventory_turnover", UIName: "Inventory Turnover", colStatus: false },
    //   { colName: "inventoryTurnoverAnnual", UIName: "Annual Inventory Turnover", colStatus: false },
    //   { colName: "isMainTicker", UIName: "Is Main Ticker", colStatus: false },
    //   { colName: "longtermDebtTotalAsset_annual", UIName: "Annual Longterm Debt to Total Asset", colStatus: false },
    //   { colName: "marketCapChange1y", UIName: "Market Cap Change 1 Year", colStatus: false },
    //   { colName: "marketCapChange3y", UIName: "Market Cap Change 3 Years", colStatus: false },
    //   { colName: "operatingMargin", UIName: "Operating Margin", colStatus: false },
    //   { colName: "pb_ratio_annual", UIName: "Annual PB Ratio", colStatus: false },
    //   { colName: "pb_ratio_quarterly", UIName: "Quarterly PB Ratio", colStatus: false },
    //   { colName: "priceTargetMedian", UIName: "Price Target Median", colStatus: false },
    //   { colName: "priceTargetUpside", UIName: "Price Target Upside", colStatus: false },
    //   { colName: "ps_annual_ratio", UIName: "Annual Price to Sales Ratio", colStatus: false },
    //   { colName: "questionableRatio", UIName: "Questionable Ratio", colStatus: false },
    //   { colName: "quick_ratio_annual", UIName: "Annual Quick Ratio", colStatus: false },
    //   { colName: "quick_ratio_quarterly", UIName: "Quarterly Quick Ratio", colStatus: false },
    //   { colName: "quickRatioAnnual", UIName: "Annual Quick Ratio", colStatus: false },
    //   { colName: "pfcp_ratio_annual", UIName: "Annual PFCP Ratio", colStatus: false },
    //   { colName: "preTaxMargin", UIName: "Pre-Tax Margin", colStatus: false },
    //   { colName: "quickRatioQuarterly", UIName: "Quarterly Quick Ratio", colStatus: false },
    //   { colName: "receivablesTurnoverTTM", UIName: "Receivables Turnover TTM", colStatus: false },
    //   { colName: "revenue_annual", UIName: "Annual Revenue", colStatus: false },
    //   { colName: "revenue_quarterly", UIName: "Quarterly Revenue", colStatus: false },
    //   { colName: "revenueGrowth1Y", UIName: "Revenue Growth 1 Year", colStatus: false },
    //   { colName: "revenueGrowth3Y", UIName: "Revenue Growth 3 Years", colStatus: false },
    //   { colName: "revenueGrowth5Y", UIName: "Revenue Growth 5 Years", colStatus: false },
    //   { colName: "roi", UIName: "Return on Investment", colStatus: false },
    //   { colName: "rotc_annual", UIName: "Annual Return on Tangible Capital", colStatus: false },
    //   { colName: "salesPerShare_annual", UIName: "Annual Sales Per Share", colStatus: false },
    //   { colName: "shariahCompliantStatus", UIName: "Shariah Compliant Status", colStatus: false },
    //   { colName: "surprisePercent", UIName: "Earnings Surprise Percent", colStatus: false },
    //   { colName: "targetMedian", UIName: "Target Median Price", colStatus: false },
    //   { colName: "total_value_bought", UIName: "Total Value Bought", colStatus: false },
    //   { colName: "total_value_sold", UIName: "Total Value Sold", colStatus: false },
    //   { colName: "totalReturn_1M", UIName: "Total Return 1 Month", colStatus: false },
    //   { colName: "totalReturn_1Y", UIName: "Total Return 1 Year", colStatus: false },
    //   { colName: "totalReturn_3M", UIName: "Total Return 3 Months", colStatus: false },
    //   { colName: "totalReturn_6M", UIName: "Total Return 6 Months", colStatus: false },
    //   { colName: "viewCount", UIName: "View Count", colStatus: false },
  ]);

  const [additionalColCheckedNames, setAdditionalColCheckedNames] = useState(
    additionalColumnConfig
      .filter((col) => col.colStatus)
      .map((col) => col.colName),
  );
  const [tempCheckedNames, setTempCheckedNames] = useState(
    additionalColCheckedNames,
  );
  const [showAdditionalColumnsDropdown, setShowAdditionalColumnsDropdown] =
    useState(false);
  const [additionalColumnsDropdownPos, setAdditionalColumnsDropdownPos] =
    useState({
      top: 0,
      left: 0,
    });
  const additionalColumnsButtonRef = useRef(null);
  const additionalColumnsDropdownRef = useRef(null);
  const [activeHeaderMenu, setActiveHeaderMenu] = useState(null);
  const [sortConfig, setSortConfig] = useState(null);
  const [pinnedColumns, setPinnedColumns] = useState({});
  const [columnWidths, setColumnWidths] = useState({});

  // table search
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedStock, setSelectedStock] = useState(null);

  const handleRowClick = (holding) => {
    setSelectedStock(holding);
  };


  //----------------------------// useEffects //----------------------------//

  useEffect(() => {
    const userId = Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
    if (!userId || !activeBrokerFlag) {
      setIsLoading(false);
      setHoldings([]);
      return;
    }
    // React StrictMode in dev runs effects twice on mount — skip duplicate within 1.2s
    const runKey = `${userId}:${activeBrokerFlag}:${updatePortfolioTrigger}`;
    const now = Date.now();
    if (
      lastPortfolioFetchRunRef.current.key === runKey &&
      now - lastPortfolioFetchRunRef.current.at < 1200
    ) {
      return;
    }
    lastPortfolioFetchRunRef.current = { key: runKey, at: now };
    fetchHoldings(
      sharedData.accessToken,
      sharedData.ClientCode,
      sharedData.api_key,
      [],
    );
  }, [updatePortfolioTrigger, activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") {
      return undefined;
    }

    wsStatusUnsubRef.current = fivePaisaWebSocketService.onStatus((status) => {
      setConnected(status?.type === "connected");
    });

    wsPriceUnsubRef.current = fivePaisaWebSocketService.onPrice((tick) => {
      if (tick) handleLivePriceUpdate(tick);
    });

    mdStoreUnsubRef.current = fivePaisaMarketDepthStore.subscribe((snap) => {
      const details = Array.isArray(snap?.activeDepthData)
        ? snap.activeDepthData
        : [];
      setMarketDepth(details);
    });

    wsErrorUnsubRef.current = fivePaisaWebSocketService.onError((error) => {
      console.error("❌ WS service error:", error);
    });

    fivePaisaWebSocketService.connect();

    return () => {
      if (wsStatusUnsubRef.current) wsStatusUnsubRef.current();
      if (wsPriceUnsubRef.current) wsPriceUnsubRef.current();
      if (wsErrorUnsubRef.current) wsErrorUnsubRef.current();
      if (mdStoreUnsubRef.current) mdStoreUnsubRef.current();
      fivePaisaWebSocketService.disconnect({ manual: false });
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "angelone") {
      if (angelPortfolioSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelPortfolioSubscriptionsRef.current,
        );
        angelPortfolioSubscriptionsRef.current = [];
      }
      return undefined;
    }

    angelWsPriceUnsubRef.current = angelOneWebSocketService.onPrice((tick) => {
      if (tick) handleLivePriceUpdate(tick);
    });
    angelWsErrorUnsubRef.current = angelOneWebSocketService.onError((error) => {
      console.error("❌ AngelOne WS service error:", error);
    });

    angelOneWebSocketService.connect();

    return () => {
      angelWsPriceUnsubRef.current?.();
      angelWsErrorUnsubRef.current?.();
      angelWsPriceUnsubRef.current = null;
      angelWsErrorUnsubRef.current = null;

      if (angelPortfolioSubscriptionsRef.current.length) {
        angelOneWebSocketService.unsubscribeMarketFeed(
          angelPortfolioSubscriptionsRef.current,
        );
        angelPortfolioSubscriptionsRef.current = [];
      }
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "angelone") {
      angelOneMarketDepthService.closeDepth();
      return undefined;
    }

    angelMdServiceUnsubRef.current = angelOneMarketDepthService.subscribe(
      (depth) => {
        setMarketDepth(Array.isArray(depth) ? depth : []);
      },
    );

    return () => {
      angelMdServiceUnsubRef.current?.();
      angelMdServiceUnsubRef.current = null;
      angelOneMarketDepthService.closeDepth();
    };
  }, [activeBrokerFlag]);

  useEffect(() => {
    if (activeBrokerFlag !== "grow") {
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
      return undefined;
    }

    const symbols = holdings
      .map((holding) => {
        const symbol = String(holding?.Symbol || "").trim().toUpperCase();
        if (!symbol) return "";
        const exchangePrefix = holding?.Exch === "B" ? "BSE" : "NSE";
        return `${exchangePrefix}_${symbol}`;
      })
      .filter(Boolean);

    growwLivePriceService.setSymbols(GROW_POLL_SOURCE, symbols);
    const unsubscribe = growwLivePriceService.subscribe((payload) => {
      let updated = false;

      holdings.forEach((holding) => {
        const symbol = String(holding?.Symbol || "").trim().toUpperCase();
        if (!symbol) return;

        const exchangePrefix = holding?.Exch === "B" ? "BSE" : "NSE";
        const key = `${exchangePrefix}_${symbol}`;
        const ltp = Number(payload?.[key]);

        if (Number.isFinite(ltp)) {
          livePriceCache.current[holding.ScripCode] = ltp;
          updated = true;
        }
      });

      if (updated) {
        setLiveUpdateTrigger((prev) => prev + 1);
      }
    });

    growwLivePriceService.startPolling(2000);

    return () => {
      unsubscribe?.();
      growwLivePriceService.stopPolling(GROW_POLL_SOURCE);
    };
  }, [activeBrokerFlag, holdings]);

  useEffect(() => {
    document.title = tabName;
  }, [tabName]);

  // MarketDepth
  useEffect(() => {
    const modalEl = document.getElementById("MarketDepthModal");
    if (modalEl) {
      // Attach event listener
      modalEl.addEventListener("hidden.bs.modal", handleModalClose);
    }
    // Cleanup when component unmounts
    return () => {
      if (modalEl) {
        modalEl.removeEventListener("hidden.bs.modal", handleModalClose);
      }
    };
  }, []);

  // Keep ref updated
  useEffect(() => {
    currentMDModalRef.current = currentMDModal;
  }, [currentMDModal]);

  // Assuming you have a ref or a way to detect modal open, for example:

  useEffect(() => {
    const modalElement = document.getElementById("AdditionalColumnListModal");

    const handleModalShow = () => {
      // Reset to current additionalColumnConfig states when modal opens
      setTempCheckedNames(
        additionalColumnConfig
          .filter((col) => col.colStatus)
          .map((col) => col.colName),
      );
    };

    if (modalElement) {
      modalElement.addEventListener("show.bs.modal", handleModalShow);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener("show.bs.modal", handleModalShow);
      }
    };
  }, [additionalColumnConfig]);

  useEffect(() => {
    console.log("📉New Modal Data: ", currentMDModal);
  }, [currentMDModal]);

  useEffect(() => {
    console.log(
      "📉New additionalColCheckedNames Data: ",
      additionalColCheckedNames,
    );
  }, [additionalColCheckedNames]);

  useEffect(() => {
    console.log("📉New additionalColumnConfig Data: ", additionalColumnConfig);
  }, [additionalColumnConfig]);

  useEffect(() => {
    if (!showAdditionalColumnsDropdown) return undefined;
    const handleOutside = (event) => {
      if (
        !event.target.closest(".watchlist-columns-dropdown") &&
        !event.target.closest(".watchlist-columns-btn")
      ) {
        setShowAdditionalColumnsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", updateAdditionalColumnsDropdownPosition);
    window.addEventListener("scroll", updateAdditionalColumnsDropdownPosition, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", updateAdditionalColumnsDropdownPosition);
      window.removeEventListener("scroll", updateAdditionalColumnsDropdownPosition, true);
    };
  }, [showAdditionalColumnsDropdown, updateAdditionalColumnsDropdownPosition]);

  useEffect(() => {
    if (!activeHeaderMenu) return undefined;
    const handleClose = () => setActiveHeaderMenu(null);
    document.addEventListener("mousedown", handleClose);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [activeHeaderMenu]);

  //----------------------------// Functions //----------------------------//

  // Notification sound function
  const pingNotification = () => {
    try {
      const audio = new Audio("/sounds/notifications.mp3");
      audio.play().catch((error) => {
        console.error("Failed to play notification sound:", error);
      });
    } catch (error) {
      console.error("Error creating audio:", error);
    }
  };

  // Common toast options
  const toastOptions = {
    position: "top-center",
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
  };

  // Success toast function
  const showSuccessToast = (message) => {
    toast.success(message, {
      ...toastOptions,
      icon: "✅",
    });
    pingNotification();
  };

  // Error toast function
  const showErrorToast = (message) => {
    toast.error(message, {
      ...toastOptions,
      icon: "⚠️",
    });
    pingNotification();
  };

  // Error toast function
  const showWarningToast = (message) => {
    toast.warn(message, {
      ...toastOptions,
      icon: "⚠️",
    });
    pingNotification();
  };

  const [columnSearch, setColumnSearch] = useState("");
  const additionalColumnsScrollRef = useRef(null);

  const sortedColumns = [...additionalColumnConfig].sort((a, b) => {
    const aChecked = tempCheckedNames.includes(a.colName);
    const bChecked = tempCheckedNames.includes(b.colName);

    if (aChecked === bChecked) return 0;
    return aChecked ? -1 : 1; // checked first
  });

  const filteredColumns = sortedColumns.filter((col) =>
    col.UIName.toLowerCase().includes(columnSearch.toLowerCase().trim()),
  );

  //----------------------------//  Additional columns Functions //----------------------------//

  const openAdditionalColumnsDropdown = (event) => {
    const anchor = event?.currentTarget || additionalColumnsButtonRef.current;
    if (!anchor) return;
    setColumnSearch("");
    const rect = anchor.getBoundingClientRect();
    const popoverWidth = 320;
    const viewportPadding = 10;
    const left = Math.min(
      Math.max(viewportPadding, rect.right - popoverWidth),
      window.innerWidth - popoverWidth - viewportPadding,
    );
    setAdditionalColumnsDropdownPos({
      top: rect.bottom + 8,
      left,
    });
    setShowAdditionalColumnsDropdown(true);
  };

  function updateAdditionalColumnsDropdownPosition() {
    const anchor = additionalColumnsButtonRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const popoverWidth = 320;
    const viewportPadding = 10;
    const left = Math.min(
      Math.max(viewportPadding, rect.right - popoverWidth),
      window.innerWidth - popoverWidth - viewportPadding,
    );
    setAdditionalColumnsDropdownPos({
      top: rect.bottom + 8,
      left,
    });
  }

  const applyAdditionalColumnsSelection = async (checkedNames) => {
    try {
      const orderedDefaultCols = columnOrder.filter((col) =>
        defaultColumns.includes(col),
      );
      const additionalCols = checkedNames.filter(
        (col) => !defaultColumns.includes(col),
      );
      const newColOrder = [...orderedDefaultCols, ...additionalCols];

      setTempCheckedNames(checkedNames);
      setAdditionalColCheckedNames(checkedNames);
      setColumnOrder(newColOrder);
      setAdditionalColumnConfig((prev) =>
        prev.map((col) => ({
          ...col,
          colStatus: checkedNames.includes(col.colName),
        })),
      );

      await appendSelectedExtraColumns(checkedNames);
      await saveColumnOrder(newColOrder);
      setUpdatePortfolioTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("❌ Error applying columns:", error);
    }
  };

  const handleCheckboxChange = (colName, isChecked) => {
    const nextChecked = isChecked
      ? [...tempCheckedNames, colName]
      : tempCheckedNames.filter((name) => name !== colName);
    applyAdditionalColumnsSelection(Array.from(new Set(nextChecked)));
  };

  const handleAddColumnsAndClose = async () => {
    await applyAdditionalColumnsSelection(Array.from(new Set(tempCheckedNames)));
  };

  const resetColumnOrder = async () => {
    try {
      const userId =
        Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
      const userEmail =
        sharedData?.Email_ID ||
        sharedData?.email ||
        localStorage.getItem("email") ||
        "";
      const pmsBrokerFlag = toPmsBrokerFlag(activeBrokerFlag);
      if (!userId || !userEmail) {
        console.warn("resetColumnOrder: missing user context");
        return;
      }

      const response = await fetch(`${pmsApiBaseUrl}/api/v1/columns/order/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          broker_flag: pmsBrokerFlag,
          module_name: "portfolio",
          user_id: userId,
          email: userEmail,
        }),
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            errorMsg = errorData.message;
          }
        } catch { }
        throw new Error(errorMsg);
      }

      const data = await response.json();

      if (data?.data?.status === "success") {
        console.log("✅ Column order reset successfully");
        setUpdatePortfolioTrigger((prev) => prev + 1);
      } else {
        console.error("❌ Failed to reset columns: ", data);
      }
    } catch (error) {
      console.error("❌ Error resetting column order:", error);
    }
  };

  //----------------------------//  END Additional columns Functions //----------------------------//

  //----------------------------// table Search Functions //----------------------------//

  // Add this before your tbody
  const filteredHoldings = holdings.filter((holding) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const name = (holding.name || "").toLowerCase();
    const fullName = (holding.FullName || "").toLowerCase();

    return name.includes(searchLower) || fullName.includes(searchLower);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredHoldings.length]);

  const getPortfolioSortableValue = useCallback((holding, columnKey) => {
    if (!holding) return "";
    const normalized = String(columnKey || "").toLowerCase();
    if (normalized === "name") {
      return String(holding.FullName || holding.name || "").toLowerCase();
    }
    if (normalized === "trade price") return Number(holding.AvgRate || 0);
    if (normalized === "quantity") return Number(holding.DPQty || 0);
    if (normalized === "current price") {
      const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
      return Number(
        livePriceCache.current?.[scripCode] ??
        holding.CurrentPrice ??
        holding.initialPrice ??
        0,
      );
    }
    if (normalized === "profit/loss" || normalized === "profit %") {
      const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
      const currentPrice = Number(
        livePriceCache.current?.[scripCode] ?? holding.CurrentPrice ?? 0,
      );
      const avgRate = Number(holding.AvgRate || 0);
      const qty = Number(holding.DPQty || 0);
      if (normalized === "profit/loss") return (currentPrice - avgRate) * qty;
      return avgRate ? ((currentPrice - avgRate) / avgRate) * 100 : 0;
    }
    if (normalized === "volume") {
      return Number(
        liveVolumeCache.current?.[holding.scripcode] ?? holding.volume ?? 0,
      );
    }
    const raw =
      holding?.[columnKey] ??
      holding?.[String(columnKey || "").replace(/\s+/g, "")] ??
      "";
    const num = Number(raw);
    return Number.isFinite(num) && raw !== "" ? num : String(raw).toLowerCase();
  }, []);

  const sortedHoldings = useMemo(() => {
    if (!sortConfig?.column || !sortConfig?.direction) return filteredHoldings;
    const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;
    return [...filteredHoldings].sort((a, b) => {
      const aVal = getPortfolioSortableValue(a, sortConfig.column);
      const bVal = getPortfolioSortableValue(b, sortConfig.column);
      if (typeof aVal === "number" && typeof bVal === "number") {
        return (aVal - bVal) * directionMultiplier;
      }
      return String(aVal).localeCompare(String(bVal), undefined, {
        sensitivity: "base",
        numeric: true,
      }) * directionMultiplier;
    });
  }, [filteredHoldings, getPortfolioSortableValue, sortConfig]);

  //----------------------------// End table Search Functions //----------------------------//

  //----------------------------// target % notification Functions //----------------------------//
  const checkPortfolioTargets = () => {
    holdings.forEach((stock) => {
      const target = parseFloat(stock.targetPrice);
      const initial = parseFloat(stock.initialPrice);
      // const currentPrice = parseFloat(stock.CurrentPrice);

      const scripCode = stock.ScripCode;
      const livePrice = parseFloat(livePriceCache.current[scripCode]);
      const currentPrice = !isNaN(livePrice)
        ? livePrice
        : parseFloat(stock["Current Price"]);

      // Skip if any values are invalid
      if (
        isNaN(target) ||
        isNaN(initial) ||
        target === initial ||
        isNaN(currentPrice)
      ) {
        return;
      }

      // const targetLogic = stock.target_logic;
      let percent;

      // Calculate target percentage
      // if (targetLogic === "lower_target") {
      //   percent = 100 - (((currentPrice - target) / target) * 100);
      // } else {
      // }
      percent = ((currentPrice - target) / target) * 100 + 100;

      // Check if target reached (>= 100%) and not yet notified
      if (percent >= 100 && stock.isNotified === 0) {
        // Call API to send notification
        const payload = {
          userId: sharedData.ID, // Add userId to your portfolio data
          source: "portfolio",
          // scripcode: stock.Exch === "B" ? stock.BseCode : stock.NseCode, // Use NseCode or BseCode as scripcode
          scripcode: stock.ScripCode, // Use NseCode or BseCode as scripcode
          exch: stock.Exch, // Already single character 'N' or 'B'
          exchType: stock.ExchType, // Already single character 'C' or 'D'
          name: stock.Symbol,
          FullName: stock.FullName,
          targetPrice: stock.targetPrice,
          initialPrice: stock.initialPrice,
          target_logic: stock.target_logic,
        };

        fetch(
          "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=notifyPortfolioTarget",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((data) => {
            if (data.status === "success") {
              console.log(
                `✅ Portfolio notification sent successfully for ${stock.Symbol}`,
              );
              showSuccessToast(
                `Hurry!, ${stock.Symbol} has reached the target percentage`,
              );

              // Update local state to reflect isNotified = 1
              stock.isNotified = 1;
              setUpdatePortfolioTrigger((prev) => prev + 1); // Trigger portfolio refresh
            } else if (data.status === "warning") {
              console.warn(
                "⚠️ Notification sent but database not updated:",
                data.message,
              );
            } else {
              console.error("❌ Notification failed:", data.message);
            }
          })
          .catch((error) => {
            console.error("❌ Error sending portfolio notification:", error);
            // Optional: Show error toast to user
            // showErrorToast('Failed to send notification. Please try again.');
          });

        console.log(
          `✅ Portfolio target notification triggered for ${stock.Symbol}`,
        );
      }
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      checkPortfolioTargets();
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval); // Cleanup
  }, [holdings, livePriceCache]);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        marketDepthPopoverRef.current &&
        !marketDepthPopoverRef.current.contains(event.target) &&
        marketDepthButtonRef.current &&
        !marketDepthButtonRef.current.contains(event.target)
      ) {
        closeMarketDepthPopover();
      }
    };

    if (showMarketDepth) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMarketDepth]);

  //----------------------------// marketdepth Functions //----------------------------//

  const handleMarketDepthClick = (holding, e) => {
    const button = e.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    const popoverWidth = 480;
    const popoverHeight = 500;
    const padding = 16;

    let left = buttonRect.left - popoverWidth - 12;
    let top = buttonRect.top - 20;

    // Check if popover goes off-screen on the left
    if (left < padding) {
      left = buttonRect.right + 12;
    }

    // Check if popover goes off-screen on top
    if (top < padding) {
      top = buttonRect.top + buttonRect.height + 12;
    }

    // Check if popover goes off-screen on bottom
    if (top + popoverHeight > window.innerHeight - padding) {
      top = window.innerHeight - popoverHeight - padding;
    }

    // Check if popover goes off-screen on right
    if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding;
    }

    setMarketDepthPosition({
      top: Math.max(padding, top),
      left: Math.max(padding, left),
    });
    setShowMarketDepth(true);
    setcurrentMDModal(holding);
    if (isAngelOneBroker) {
      const cachedDepth = Array.isArray(holding?.marketDepth)
        ? holding.marketDepth
        : normalizeAngelMarketDepth(holding?.market_quote?.depth);
      if (cachedDepth.length) {
        setMarketDepth(cachedDepth);
      }
    }
    subscribeToMarketDepth(holding);
  };

  //----------------------------//  pagination Functions //----------------------------//

  // Pagination calculations
  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentItems = sortedHoldings.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(sortedHoldings.length / pageSize);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= 3; i++) pageNumbers.push(i);
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - 2; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  //----------------------------//  END pagination Functions //----------------------------//

  const SettingBuyModalDetails = (stockData, Type) => {
    setBuySellModalDetails((prevState) => ({
      ...prevState,
      FullName: stockData.FullName || stockData.fullName || "",
      Symbol: stockData.Symbol || stockData.symbol || stockData.name || "",
      Type: Type,
      OrderQty: 1,
      LimitPrice: 0,
      order: stockData, // store complete order object
    }));
  };

  const openBuySellFromStockPreview = (stockData, Type) => {
    if (!stockData) return;
    setIsPreview(false);
    SettingBuyModalDetails(stockData, Type);
    const el = document.getElementById("BuySellModal");
    if (!el) return;
    el.style.zIndex = "4100";
    const modal = Modal.getOrCreateInstance(el);
    modal.show();
    requestAnimationFrame(() => {
      document.querySelectorAll(".modal-backdrop").forEach((backdrop) => {
        backdrop.style.zIndex = "4050";
      });
    });
  };

  const handlePreview = () => setIsPreview(true);
  const handleGoBack = () => setIsPreview(false);

  const subscribePortfolioFeeds = (portfolioHoldings = []) => {
    const marketFeedItems = portfolioHoldings
      .map((holding) => ({
        Exch: holding?.Exch,
        ExchType: holding?.ExchType,
        ScripCode: holding?.ScripCode,
      }))
      .filter((item) => item.Exch && item.ExchType && item.ScripCode);

    if (marketFeedItems.length) {
      fivePaisaWebSocketService.subscribeMarketFeed(marketFeedItems);
    }
  };

  const subscribeAngelPortfolioFeeds = (portfolioHoldings = []) => {
    if (angelPortfolioSubscriptionsRef.current.length) {
      angelOneWebSocketService.unsubscribeMarketFeed(
        angelPortfolioSubscriptionsRef.current,
      );
      angelPortfolioSubscriptionsRef.current = [];
    }

    const seenTokens = new Set();
    const feedItems = [];

    portfolioHoldings.forEach((holding) => {
      const token = String(
        holding?.token || holding?.symboltoken || holding?.ScripCode || "",
      ).trim();
      if (!token || seenTokens.has(token)) return;

      const exchangeType = getAngelExchangeTypeFromHolding({
        Exch: holding?.Exch,
        ExchSeg: holding?.ExchSeg || holding?.exchange,
        ExchType: holding?.ExchType,
        InstrumentType: holding?.instrumenttype || holding?.InstrumentType || "",
      });

      if (!exchangeType) return;

      seenTokens.add(token);
      feedItems.push({ exchangeType, token });
    });

    if (!feedItems.length) return;

    angelOneWebSocketService.connect();
    for (let i = 0; i < feedItems.length; i += 50) {
      angelOneWebSocketService.subscribeMarketFeed(feedItems.slice(i, i + 50));
    }
    angelPortfolioSubscriptionsRef.current = feedItems;
  };

  // Handle Live Price Updates
  // const handleLivePriceUpdate = (data) => {
  //   const { Token: scriptCode, LastRate: lastRateValue, PClose, Volume } = data;

  //   if (!scriptCode || lastRateValue === undefined) return;

  //   if (!livePriceCache.current) livePriceCache.current = {};
  //   if (!livePCloseCache.current) livePCloseCache.current = {};
  //   if (!liveDiffCache.current) liveDiffCache.current = {};
  //   if (!liveChangePercentageCache.current) liveChangePercentageCache.current = {};
  //   if (!liveVolumeCache.current) liveVolumeCache.current = {};
  //   if (!livePLCache.current) livePLCache.current = {};

  //   livePriceCache.current[scriptCode] = lastRateValue;
  //   livePCloseCache.current[scriptCode] = PClose;
  //   liveDiffCache.current[scriptCode] = lastRateValue - PClose;
  //   liveChangePercentageCache.current[scriptCode] = ((lastRateValue - PClose) / PClose) * 100;
  //   liveVolumeCache.current[scriptCode] = Volume;

  //   const totalPL = lastRateValue - PClose;
  //   livePLCache.current[scriptCode] = `${totalPL.toFixed(2)} (${liveChangePercentageCache.current[scriptCode].toFixed(2)}%)`;

  //   setLiveUpdateTrigger((prev) => prev + 1);
  //   console.log(`📈 Price Update: ${scriptCode} = ₹${lastRateValue}`);
  // };

  const handleLivePriceUpdate = (data) => {
    // Destructure the fields you need from the incoming tick
    const {
      Token: scriptCode,
      LastRate: lastRateValue,
      PClose,
      Volume,
      High,
      Low,
    } = data;

    // Safety check for valid tick data
    if (!scriptCode || lastRateValue === undefined) return;

    // Initialize caches if not present
    if (!livePriceCache.current) livePriceCache.current = {};
    if (!livePCloseCache.current) livePCloseCache.current = {};
    if (!liveDiffCache.current) liveDiffCache.current = {};
    if (!liveChangePercentageCache.current)
      liveChangePercentageCache.current = {};
    if (!liveVolumeCache.current) liveVolumeCache.current = {};
    if (!livePLCache.current) livePLCache.current = {};
    if (!liveHighCache.current) liveHighCache.current = {};
    if (!liveLowCache.current) liveLowCache.current = {};

    // Save current live tick values
    livePriceCache.current[scriptCode] = lastRateValue;
    livePCloseCache.current[scriptCode] = PClose;
    liveDiffCache.current[scriptCode] = lastRateValue - PClose;
    liveChangePercentageCache.current[scriptCode] =
      ((lastRateValue - PClose) / PClose) * 100;
    liveVolumeCache.current[scriptCode] = Volume;
    liveHighCache.current[scriptCode] = High;
    liveLowCache.current[scriptCode] = Low;

    // Compute and store P&L summary text
    const totalPL = lastRateValue - PClose;
    livePLCache.current[scriptCode] =
      `${totalPL.toFixed(2)} (${liveChangePercentageCache.current[scriptCode].toFixed(2)}%)`;

    // Trigger a state update for re-render or UI refresh
    setLiveUpdateTrigger((prev) => prev + 1);

    // Optional: include High/Low in your console for debugging
    console.log(
      `📈 [${scriptCode}] LTP ₹${lastRateValue} | High ₹${High} | Low ₹${Low}`,
    );
  };

  // API: Fetch Holding
  // const fetchHoldings = async (AccessToken, ClientCode, api_key, columns) => {
  //     const url =
  //       "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getHoldingData";

  //     const data = {
  //       AccessToken,
  //       ClientCode,
  //       api_key,
  //       columns, // <-- ✅ This sends selected filters
  //       userId: sharedData?.ID,
  //     };

  //     try {
  //       const response = await fetch(url, {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(data),
  //       });

  //       let responseData;
  //       try {
  //         responseData = await response.json();
  //       } catch (parseError) {
  //         console.warn("Non-JSON response received:", await response.text());
  //         setHoldings([]); // fallback
  //         return;
  //       }

  //       const holdings = responseData.data || [];
  //       setHoldings(holdings);
  //       console.log("Initial Holdings --> :", holdings);

  //       setProgress(100); // ✅ always set progress to 100

  //     } catch (error) {
  //       console.error("Error fetching holdings:", error);
  //       setHoldings([]); // fallback
  //       setProgress(100); // ✅ don’t leave stuck
  //     }
  //   };



  // CLASS MAPPING
  // Broker mapping reference (update mappings here as APIs evolve):
  // Normalized output keys:
  // AvgRate, BseCode, CurrentPrice, DPQty, Exch, ExchType, FullName, NseCode,
  // POASigned, PoolQty, Quantity, ScripMultiplier, Symbol, ScripCode, ISIN
  //
  // 5Paisa source keys used:
  // AvgRate, BseCode, CurrentPrice, DPQty, Exch, ExchType, FullName, NseCode,
  // POASigned, PoolQty, Quantity, ScripMultiplier, Symbol
  //
  // Groww source keys used:
  // tradable_exchanges, exchange, exch, nse_code, instrument_token,
  // instrumentToken, bse_code, quantity, average_price, trading_symbol, symbol,
  // isin, ltp, last_price, current_price, exchange_type, company_name, full_name,
  // poa_signed, pool_qty, scrip_multiplier
  class BasePortfolioAdapter {
    numberOrZero = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    stringOrDefault = (value, fallback = "") =>
      value === null || value === undefined || value === "" ? fallback : String(value);

    pickFirst = (...values) =>
      values.find((v) => v !== null && v !== undefined && v !== "");

    pickLatestTarget = (targetData = []) => {
      if (!Array.isArray(targetData) || targetData.length === 0) return {};
      return [...targetData]
        .filter((item) => item && typeof item === "object")
        .sort(
          (a, b) =>
            new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime(),
        )[0] || {};
    };

    sanitizeAdditionalData = (value) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return null;
      return value;
    };

    numberOrNull = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    pick52WeekRange = (holding = {}, stocksData = {}, additionalData = null) => {
      const marketQuote = holding?.market_quote || {};
      const high = this.numberOrNull(
        additionalData?.["52WeekHigh"] ??
        additionalData?.week_52_high ??
        marketQuote?.["52WeekHigh"] ??
        stocksData?.week_52_high ??
        stocksData?.["52WeekHigh"] ??
        holding?.["52WeekHigh"],
      );
      const low = this.numberOrNull(
        additionalData?.["52WeekLow"] ??
        additionalData?.week_52_low ??
        marketQuote?.["52WeekLow"] ??
        stocksData?.week_52_low ??
        stocksData?.["52WeekLow"] ??
        holding?.["52WeekLow"],
      );
      return { high52Week: high, low52Week: low };
    };

    pickTodayRange = (holding = {}, stocksData = {}, additionalData = null) => {
      const marketQuote = holding?.market_quote || {};
      const ohlc = stocksData?.ohlc || {};
      const todayHigh = this.numberOrNull(
        additionalData?.today_high ??
        additionalData?.TodaysHigh ??
        marketQuote?.high ??
        stocksData?.High ??
        stocksData?.high ??
        stocksData?.day_high ??
        stocksData?.high_trade_range ??
        ohlc?.high ??
        holding?.TodaysHigh ??
        holding?.day_high ??
        holding?.high,
      );
      const todayLow = this.numberOrNull(
        additionalData?.today_low ??
        additionalData?.TodaysLow ??
        marketQuote?.low ??
        stocksData?.Low ??
        stocksData?.low ??
        stocksData?.day_low ??
        stocksData?.low_trade_range ??
        ohlc?.low ??
        holding?.TodaysLow ??
        holding?.day_low ??
        holding?.low,
      );
      return { todayHigh, todayLow };
    };

    normalizeHoldings = (holdings = []) => holdings;
  }

  class FivePaisaPortfolioAdapter extends BasePortfolioAdapter {
    normalizeHoldings = (holdings = []) =>
      holdings.map((holding = {}) => {
        const stocksData = holding?.stocks_data || {};
        const additionalData = this.sanitizeAdditionalData(holding?.nova_extra_data);
        const hasTargetData = Array.isArray(holding?.target_data) && holding.target_data.length > 0;
        const target = this.pickLatestTarget(holding?.target_data);
        const exch = this.stringOrDefault(holding?.Exch, "N").toUpperCase();
        const nseCode = this.numberOrZero(holding?.NseCode);
        const bseCode = this.numberOrZero(holding?.BseCode);
        const currentPrice = this.numberOrZero(
          stocksData?.LastRate ??
          stocksData?.LastTradedPrice ??
          holding?.CurrentPrice,
        );
        const { high52Week, low52Week } = this.pick52WeekRange(
          holding,
          stocksData,
          additionalData,
        );
        return {
          AvgRate: this.numberOrZero(holding?.AvgRate),
          BseCode: bseCode,
          CurrentPrice: currentPrice,
          DPQty: this.numberOrZero(holding?.DPQty),
          Exch: exch,
          ExchType: this.stringOrDefault(holding?.ExchType, "C"),
          FullName: this.stringOrDefault(holding?.FullName, "N/A"),
          NseCode: nseCode,
          POASigned: this.stringOrDefault(holding?.POASigned, "N"),
          PoolQty: this.numberOrZero(holding?.PoolQty),
          Quantity: this.numberOrZero(holding?.Quantity),
          ScripMultiplier: this.numberOrZero(holding?.ScripMultiplier || 1),
          Symbol: this.stringOrDefault(holding?.Symbol, "N/A"),
          hasTargetData,
          initialPrice: this.numberOrZero(
            target?.initialPrice ?? target?.initial_price,
          ),
          targetPrice: this.numberOrZero(target?.targetPrice),
          target_logic: this.stringOrDefault(target?.target_logic, ""),
          targetReason: this.stringOrDefault(
            target?.targetReason ?? target?.target_reason,
            "",
          ),
          targetReasonDetails: this.stringOrDefault(
            target?.targetReasonDetails ?? target?.target_reason_details,
            "",
          ),
          source: this.stringOrDefault(target?.source, "portfolio"),
          source_watchlist: this.stringOrDefault(target?.source_watchlist, ""),
          createdDate: this.stringOrDefault(target?.created_at, ""),
          ScripCode: exch === "N" ? nseCode || bseCode : bseCode || nseCode,
          "52WeekHigh": high52Week,
          "52WeekLow": low52Week,
          additionalData,
        };
      });
  }

  class GrowwPortfolioAdapter extends BasePortfolioAdapter {
    normalizeHoldings = (holdings = []) =>
      holdings.map((holding = {}) => {
        const stocksData = holding?.stocks_data || {};
        const ohlc = stocksData?.ohlc || {};
        const additionalData = this.sanitizeAdditionalData(holding?.nova_extra_data);
        const hasTargetData = Array.isArray(holding?.target_data) && holding.target_data.length > 0;
        const target = this.pickLatestTarget(holding?.target_data);
        const primaryExchange = Array.isArray(holding?.tradable_exchanges)
          ? String(holding.tradable_exchanges[0] || "")
          : "";
        const exchRaw = this.stringOrDefault(
          primaryExchange || holding?.exchange || holding?.exch,
          "NSE",
        ).toUpperCase();
        const exch = exchRaw.startsWith("B")
          ? "B"
          : exchRaw.startsWith("N")
            ? "N"
            : "N";
        const nseCode = this.numberOrZero(
          holding?.nse_code ?? holding?.instrument_token ?? holding?.instrumentToken,
        );
        const bseCode = this.numberOrZero(holding?.bse_code); //No code right now
        const qty = this.numberOrZero(holding?.quantity);
        const avgPrice = this.numberOrZero(holding?.average_price);
        const symbol = this.stringOrDefault(
          holding?.trading_symbol || holding?.symbol,
          "N/A",
        );
        const uniqueCode = this.stringOrDefault(holding?.isin || symbol, symbol);
        const currentPrice = this.numberOrZero(
          stocksData?.last_price ??
          stocksData?.LastRate ??
          stocksData?.ltp ??
          stocksData?.close ??
          ohlc?.close ??
          stocksData?.open ??
          ohlc?.open ??
          holding?.ltp ??
          holding?.last_price ??
          holding?.current_price ??
          holding?.today_price ??
          holding?.live_price ??
          0,
        );
        const { high52Week, low52Week } = this.pick52WeekRange(
          holding,
          stocksData,
          additionalData,
        );

        return {
          AvgRate: avgPrice,
          BseCode: bseCode,
          CurrentPrice: currentPrice,
          DPQty: qty,
          Exch: exch,
          ExchType: this.stringOrDefault(holding?.exchange_type, "C"),
          FullName: this.stringOrDefault(
            holding?.name || holding?.company_name || holding?.full_name || symbol,
            symbol,
          ),
          NseCode: nseCode || (exch === "N" ? uniqueCode : 0),
          POASigned: this.stringOrDefault(holding?.poa_signed, "N"),
          PoolQty: this.numberOrZero(holding?.pool_qty),
          Quantity: qty,
          ScripMultiplier: this.numberOrZero(holding?.scrip_multiplier || 1),
          Symbol: symbol,
          ISIN: this.stringOrDefault(holding?.isin, ""),
          hasTargetData,
          initialPrice: this.numberOrZero(
            target?.initialPrice ?? target?.initial_price,
          ),
          targetPrice: this.numberOrZero(target?.targetPrice),
          target_logic: this.stringOrDefault(target?.target_logic, ""),
          targetReason: this.stringOrDefault(
            target?.targetReason ?? target?.target_reason,
            "",
          ),
          targetReasonDetails: this.stringOrDefault(
            target?.targetReasonDetails ?? target?.target_reason_details,
            "",
          ),
          source: this.stringOrDefault(target?.source, "portfolio"),
          source_watchlist: this.stringOrDefault(target?.source_watchlist, ""),
          createdDate: this.stringOrDefault(target?.created_at, ""),
          PreviousClose: this.numberOrZero(
            stocksData?.PClose ??
            stocksData?.previous_close ??
            stocksData?.pc ??
            stocksData?.close ??
            ohlc?.close ??
            stocksData?.open ??
            ohlc?.open,
          ),
          TodaysHigh: this.numberOrZero(
            stocksData?.high ?? stocksData?.High ?? ohlc?.high,
          ),
          TodaysLow: this.numberOrZero(
            stocksData?.low ?? stocksData?.Low ?? ohlc?.low,
          ),
          volume: this.numberOrZero(stocksData?.volume),
          "52WeekHigh": high52Week,
          "52WeekLow": low52Week,
          additionalData,
          ScripCode:
            exch === "N"
              ? nseCode || uniqueCode
              : bseCode || uniqueCode,
        };
      });
  }

  class AngelOnePortfolioAdapter extends BasePortfolioAdapter {
    parseTradingSymbol = (tradingsymbol) => {
      const raw = this.stringOrDefault(tradingsymbol, "N/A");
      const base = raw.includes("-") ? raw.split("-")[0] : raw;
      return { raw, base: this.stringOrDefault(base, raw) };
    };

    normalizeHoldings = (holdings = []) =>
      holdings.map((holding = {}) => {
        const marketQuote = holding?.market_quote || {};
        const stocksData = holding?.stocks_data || {};
        const quoteStocksData = {
          ltp: marketQuote.ltp,
          last_price: marketQuote.ltp,
          previous_close: marketQuote.close,
          close: marketQuote.close,
          high: marketQuote.high,
          low: marketQuote.low,
          open: marketQuote.open,
          volume: marketQuote.tradeVolume,
          week_52_high: marketQuote["52WeekHigh"],
          week_52_low: marketQuote["52WeekLow"],
          "52WeekHigh": marketQuote["52WeekHigh"],
          "52WeekLow": marketQuote["52WeekLow"],
          net_change: marketQuote.netChange,
          day_change: marketQuote.netChange,
          day_change_perc: marketQuote.percentChange,
        };
        const mergedStocksData = { ...stocksData, ...quoteStocksData };
        const additionalData = this.sanitizeAdditionalData(holding?.nova_extra_data);
        const hasTargetData =
          Array.isArray(holding?.target_data) && holding.target_data.length > 0;
        const target = this.pickLatestTarget(holding?.target_data);
        const token = this.stringOrDefault(
          holding?.symboltoken || marketQuote.symbolToken,
          "",
        );
        const scripCode = this.numberOrZero(token);
        const { raw: tradingSymbol, base: symbolBase } = this.parseTradingSymbol(
          holding?.tradingsymbol ||
            marketQuote.tradingSymbol ||
            stocksData?.symbol,
        );
        const fields = buildAngelInstrumentFields({
          name: symbolBase,
          symbol: tradingSymbol,
          token,
          exch_seg: holding?.exchange || marketQuote.exchange,
        });
        const exchRaw = this.stringOrDefault(
          holding?.exchange || marketQuote.exchange,
          "NSE",
        ).toUpperCase();
        const exch = exchRaw.startsWith("B")
          ? "B"
          : exchRaw.startsWith("M")
            ? "M"
            : "N";
        const productType = this.stringOrDefault(
          holding?.product,
          "DELIVERY",
        ).toUpperCase();
        const exchType =
          productType.includes("DELIVERY") || productType === "CNC" ? "C" : "D";
        const qty = this.numberOrZero(
          this.pickFirst(holding?.quantity, mergedStocksData?.quantity),
        );
        const avgPrice = this.numberOrZero(
          this.pickFirst(holding?.averageprice, mergedStocksData?.averageprice),
        );
        const currentPrice = this.numberOrZero(
          this.pickFirst(
            marketQuote.ltp,
            holding?.ltp,
            mergedStocksData?.ltp,
            holding?.close,
            mergedStocksData?.close,
          ),
        );
        const previousClose = this.numberOrZero(
          this.pickFirst(
            marketQuote.close,
            holding?.close,
            mergedStocksData?.close,
          ),
        );
        const nseCode = exch === "N" ? scripCode : 0;
        const bseCode = exch === "B" ? scripCode : 0;
        const quoteRow = {
          ...holding,
          "52WeekHigh": marketQuote["52WeekHigh"],
          "52WeekLow": marketQuote["52WeekLow"],
          high: marketQuote.high,
          low: marketQuote.low,
        };
        const { high52Week, low52Week } = this.pick52WeekRange(
          quoteRow,
          mergedStocksData,
          additionalData,
        );
        const { todayHigh, todayLow } = this.pickTodayRange(
          quoteRow,
          mergedStocksData,
          additionalData,
        );
        const marketDepth = normalizeAngelMarketDepth(marketQuote.depth);

        return {
          ...holding,
          AvgRate: avgPrice,
          BseCode: bseCode,
          CurrentPrice: currentPrice,
          DPQty: qty,
          Exch: exch,
          ExchType: exchType,
          ExchSeg: exchRaw,
          FullName: fields.fullName || symbolBase,
          name: fields.instrumentName || symbolBase,
          NseCode: nseCode,
          POASigned: "N",
          PoolQty: this.numberOrZero(holding?.t1quantity),
          Quantity: qty,
          ScripMultiplier: 1,
          Symbol: symbolBase || tradingSymbol,
          TradingSymbol: tradingSymbol,
          tradingsymbol: tradingSymbol,
          ISIN: this.stringOrDefault(holding?.isin, ""),
          token,
          Token: token,
          symboltoken: token,
          scripcode: token,
          InstrumentType: fields.instrumentType,
          market_quote: marketQuote,
          marketDepth,
          stocks_data: mergedStocksData,
          hasTargetData,
          initialPrice: this.numberOrZero(
            target?.initialPrice ?? target?.initial_price,
          ),
          targetPrice: this.numberOrZero(target?.targetPrice),
          target_logic: this.stringOrDefault(target?.target_logic, ""),
          targetReason: this.stringOrDefault(
            target?.targetReason ?? target?.target_reason,
            "",
          ),
          targetReasonDetails: this.stringOrDefault(
            target?.targetReasonDetails ?? target?.target_reason_details,
            "",
          ),
          source: this.stringOrDefault(target?.source, "portfolio"),
          source_watchlist: this.stringOrDefault(target?.source_watchlist, ""),
          createdDate: this.stringOrDefault(target?.created_at, ""),
          PreviousClose: previousClose,
          TodaysHigh: this.numberOrZero(
            todayHigh ?? mergedStocksData?.high ?? mergedStocksData?.High,
          ),
          TodaysLow: this.numberOrZero(
            todayLow ?? mergedStocksData?.low ?? mergedStocksData?.Low,
          ),
          volume: this.numberOrZero(
            marketQuote.tradeVolume ?? mergedStocksData?.volume,
          ),
          netChange: this.numberOrZero(marketQuote.netChange),
          netChangePercent: this.numberOrZero(marketQuote.percentChange),
          ProfitAndLoss: this.numberOrZero(
            this.pickFirst(holding?.profitandloss, mergedStocksData?.pnl),
          ),
          ProfitPct: this.numberOrZero(holding?.pnlpercentage),
          "52WeekHigh": high52Week,
          "52WeekLow": low52Week,
          additionalData,
          ScripCode: scripCode || (exch === "N" ? nseCode : bseCode),
        };
      });
  }

  const getPortfolioAdapter = (brokerFlag) => {
    const normalizedFlag = String(brokerFlag || "").toLowerCase();
    if (normalizedFlag === "grow") return new GrowwPortfolioAdapter();
    if (normalizedFlag === "5paisa") return new FivePaisaPortfolioAdapter();
    if (normalizedFlag === "angelone") return new AngelOnePortfolioAdapter();
    return new BasePortfolioAdapter();
  };

  const fetchHoldings = async (
    AccessToken,
    ClientCode,
    api_key,
    AdditionalColumns,
  ) => {
    const fetchId = ++portfolioFetchIdRef.current;
    let columnsToUse = AdditionalColumns || columns; // fallback to existing columns state
    const userId = Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;

    const userEmail =
      sharedData?.Email_ID ||
      sharedData?.email ||
      localStorage.getItem("email") ||
      "";
    const brokerFlag = toPmsBrokerFlag(activeBrokerFlag);

    // Column order from PMS (module_name must stay lowercase: "portfolio")
    try {
      if (userEmail) {
        try {
          const extraColsResponse = await fetch(
            `${pmsApiBaseUrl}/api/v1/columns/extra`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                email: userEmail,
                broker_flag: brokerFlag,
                module_name: "portfolio",
              }),
            },
          );
          const extraColsPayload = await extraColsResponse.json();
          const extraColsData = extraColsPayload?.data;
          const extraCols = Array.isArray(extraColsData?.columns)
            ? extraColsData.columns
            : [];
          if (extraColsData?.status === "success" && extraCols.length) {
            const mappedAdditionalCols = extraCols
              .map((c) => {
                const colName = String(c?.column_key || "").trim();
                if (!colName) return null;
                return {
                  colName,
                  UIName: String(c?.column_label || colName).trim(),
                  colStatus: Number(c?.is_selected) === 1,
                };
              })
              .filter(Boolean);

            setAdditionalColumnConfig(mappedAdditionalCols);
            const selectedExtraCols = mappedAdditionalCols
              .filter((c) => c.colStatus)
              .map((c) => c.colName);
            setAdditionalColCheckedNames(selectedExtraCols);
            setTempCheckedNames(selectedExtraCols);
          }
        } catch (extraErr) {
          console.warn("Failed to fetch extra portfolio columns from PMS", extraErr);
        }

        const colOrderResponse = await fetch(
          `${pmsApiBaseUrl}/api/v1/columns/list`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: userId,
              email: userEmail,
              broker_flag: brokerFlag,
              module_name: "portfolio",
            }),
          },
        );

        const colOrderPayload = await colOrderResponse.json();
        const colData = colOrderPayload?.data;
        const apiColumns = colData?.columns;
        if (colData?.status === "success" && Array.isArray(apiColumns)) {
          const nextOrder = buildPortfolioTableColumnOrderFromPms(apiColumns);
          if (nextOrder?.length) {
            setColumnOrder(nextOrder);
            setColumns(nextOrder);
            columnsToUse = nextOrder;

            const additionalColsFromAPI = columnsToUse.filter(
              (col) => !defaultColumns.includes(col),
            );
            setAdditionalColumnConfig((prevConfig) =>
              prevConfig.map((col) => ({
                ...col,
                colStatus: false,
              })),
            );
            setAdditionalColumnConfig((prevConfig) =>
              prevConfig.map((col) => ({
                ...col,
                colStatus: additionalColsFromAPI.includes(col.colName)
                  ? true
                  : col.colStatus,
              })),
            );
          } else {
            console.warn(
              "PMS portfolio columns empty after mapping, using default",
            );
          }
        } else {
          console.warn("Failed to fetch column order from PMS, using default");
        }
      }
    } catch (error) {
      console.error("Error fetching column order:", error);
    }

    console.log("✅📉Columns is: ", columnsToUse);
    const adapter = getPortfolioAdapter(brokerFlag);

    const data = {
      email: userEmail,
      broker_flag: brokerFlag,
    };
    console.log("✅📉Portfolio request payload: ", data);

    const url = `${pmsApiBaseUrl}/api/v1/portfolio`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        console.warn("Non-JSON response received:", await response.text());
        if (fetchId !== portfolioFetchIdRef.current) return;
        setHoldings([]);
        setIsLoading(false);
        return;
      }

      if (fetchId !== portfolioFetchIdRef.current) {
        return;
      }

      const apiPayload = responseData?.data || {};
      const apiStatus = String(apiPayload?.status || "").toLowerCase();

      if (apiStatus === "error") {
        const errMsg =
          apiPayload?.message ||
          apiPayload?.errors?.message ||
          "Failed to load portfolio.";
        console.error("Portfolio API error:", apiPayload);
        showErrorToast(errMsg);
        setHoldings([]);
        setIsLoading(false);
        setProgress(100);
        return;
      }

      if (!response.ok || (apiStatus && apiStatus !== "success")) {
        console.warn("Unexpected portfolio API response:", responseData);
        setHoldings([]);
        setIsLoading(false);
        setProgress(100);
        return;
      }

      const rawHoldings = Array.isArray(apiPayload?.holdings)
        ? apiPayload.holdings
        : [];
      const holdings = adapter.normalizeHoldings(rawHoldings);

      holdings.forEach((holding) => {
        const cacheKey = getOrderLivePriceCacheKey(holding);
        if (cacheKey) {
          const ltp =
            Number(holding.CurrentPrice) ||
            Number(holding?.market_quote?.ltp) ||
            0;
          livePriceCache.current[cacheKey] = ltp;
        }
      });

      setHoldings(holdings);
      // setIsLoading(false);
      setTimeout(() => {
        if (fetchId !== portfolioFetchIdRef.current) return;
        setIsLoading(false);
      }, 1500);
      console.log("Initial Holdings --> :", holdings);

      if (
        brokerFlag === "5paisa" &&
        apiStatus === "success" &&
        holdings.length > 0
      ) {
        subscribePortfolioFeeds(holdings);
      } else if (
        brokerFlag === "angelone" &&
        apiStatus === "success" &&
        holdings.length > 0
      ) {
        subscribeAngelPortfolioFeeds(holdings);
      }

      setProgress(100);
    } catch (error) {
      console.error("Error fetching holdings:", error);
      if (fetchId !== portfolioFetchIdRef.current) return;
      setHoldings([]);
      setIsLoading(false);
      setProgress(100);
    }
  };

  //----------------------------//  Buy-Sell Functions //----------------------------//

  // Placing Order steps:
  // CDSL Record Count Function
  // CDSL Record Count Function with Debug Logging
  async function getCDSLRecordCount(userId) {
    console.log("🔍 getCDSLRecordCount - Starting function");
    console.log("📋 Input userId:", userId);

    const apiUrl =
      "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getCDSLRecord";
    console.log("🌐 API URL:", apiUrl);

    try {
      console.log("📤 Preparing to send request...");

      const requestBody = { userId: userId };
      console.log("📦 Request body:", JSON.stringify(requestBody));

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      console.log("📥 Response received");
      console.log("✅ Response status:", response.status);
      console.log("📊 Response statusText:", response.statusText);
      console.log("🔗 Response OK:", response.ok);

      if (!response.ok) {
        console.error("❌ Response not OK - throwing error");
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      console.log("🔄 Parsing response as JSON...");
      const data = await response.json();
      console.log("📋 Raw response data:", data);

      const count = data.count;
      console.log("🔢 Extracted count:", count);
      console.log("✅ getCDSLRecordCount - Function completed successfully");

      return count;
    } catch (error) {
      console.error("💥 Error in getCDSLRecordCount:");
      console.error("❌ Error message:", error.message);
      console.error("📊 Full error object:", error);
      console.log("🔄 Returning default value: 0");

      // Return 0 as default if error occurs
      return 0;
    }
  }

  const fetchDataPortfolio = (watchlist, transactionSide, status) => {
    console.log("🚀 fetchDataPortfolio - Starting function");
    console.log("📊 Input parameters:");
    console.log("   - watchlist:", watchlist);
    console.log("   - transactionSide:", transactionSide);
    console.log("   - status:", status);

    const Exch = watchlist.Exch;
    console.log("🏛️ Exchange (Exch):", Exch);

    const ExchType = Exch === "M" ? "D" : "C";
    console.log(
      "📈 Exchange Type (ExchType):",
      ExchType,
      `(${Exch === "M" ? "Derivative" : "Cash"})`,
    );

    const Name = watchlist.FullName;
    console.log("🏢 Company Name:", Name);

    let Scripcode = "";

    if (status === "Portfolio") {
      // ✨ Use Number conversion for ScripCode with fallback
      Scripcode = Number(watchlist.ScripCode) || Number(watchlist.scripcode);
      console.log("📋 Portfolio mode - Using pre-calculated ScripCode:");
      console.log(
        `   - Exchange is ${watchlist.Exch}, ScripCode: ${Scripcode}`,
      );
      console.log(
        `   - NseCode: ${watchlist.NseCode}, BseCode: ${watchlist.BseCode}`,
      );
      console.log(`   - Selected Scripcode: ${Scripcode}`);
    } else {
      Scripcode = watchlist.Scripcode;
      console.log("📝 Watchlist mode - Using direct Scripcode:", Scripcode);
    }

    let watchlist_price = "0";
    let min_qty = "0";

    // ✅ Get values from modal state instead of DOM
    watchlist_price =
      orderType === "Market Order"
        ? "0"
        : buySellModalDetails.LimitPrice.toString();
    min_qty = buySellModalDetails.OrderQty.toString();

    console.log("💰 Price & Quantity from modal:");
    console.log("   - Execution (Market/Limit):", orderType);
    console.log("   - Price (watchlist_price):", watchlist_price);
    console.log("   - Quantity (min_qty):", min_qty);
    console.log("   - buySellModalDetails:", buySellModalDetails);

    const userId = sharedData.ID;
    console.log("👤 User ID:", userId);
    console.log("📱 sharedData object:", sharedData);

    const order_type = transactionSide;
    console.log("📊 Final order_type (Buy/Sell):", order_type);

    // Create a JavaScript object with the request data
    const requestData = {
      ExchType,
      order_type,
      Exch,
      Name,
      Scripcode,
      watchlist_price,
      min_qty,
      userId,
    };

    console.log("📦 Complete request data object:");
    console.log(JSON.stringify(requestData, null, 2));

    // ✅ CDSL validation for 5paisa Sell only (not Grow / AngelOne)
    if (transactionSide === "Sell" && isAngelOneBroker) {
      console.log("🛑 SELL order (AngelOne) - skipping CDSL validation");
      executeBuySellOrderPortfolio(
        requestData,
        watchlist,
        transactionSide,
        status,
      );
    } else if (transactionSide === "Sell") {
      console.log("🛑 SELL order detected - checking CDSL validation");
      getCDSLRecordCount(userId)
        .then((count) => {
          console.log("✅ CDSL validation completed");
          console.log("🔢 CDSL Record Count:", count);

          if (count === 0) {
            console.error(
              "❌ CDSL Record Count is 0 - cannot place sell order",
            );
            console.log("🚫 Sell order blocked - CDSL setup required");
            // TODO: Show error modal - user needs to complete CDSL setup
            // document.getElementById("modalClick").click();
          } else {
            console.log(
              "✅ CDSL validation passed - proceeding with sell order",
            );
            executeBuySellOrderPortfolio(
              requestData,
              watchlist,
              transactionSide,
              status,
            );
          }
        })
        .catch((error) => {
          console.error("💥 Error getting CDSL count:", error);
          console.log(
            "⚠️ CDSL validation failed - proceeding with order anyway",
          );
          // Proceed with order anyway or show error
          executeBuySellOrderPortfolio(
            requestData,
            watchlist,
            transactionSide,
            status,
          );
        });
    } else if (transactionSide === "Buy") {
      console.log("💚 BUY order detected - no CDSL validation needed");
      console.log("🚀 Proceeding directly to order execution");
      executeBuySellOrderPortfolio(
        requestData,
        watchlist,
        transactionSide,
        status,
      );
    } else {
      console.error("❓ Unknown transaction side:", transactionSide);
    }

    console.log("🏁 fetchDataPortfolio - Function end");
  };

  // Function to handle the checkSell value
  // const executeBuySellOrderPortfolio = (requestData, watchlist, orderType, status) => {
  //   console.log("🔧 executeBuySellOrderPortfolio - Starting function");
  //   console.log("📥 Input parameters:");
  //   console.log("   - requestData:", requestData);
  //   console.log("   - watchlist:", watchlist);
  //   console.log("   - orderType:", orderType);
  //   console.log("   - status:", status);

  //   const apiUrl = "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=addFivePaisaOrder";
  //   console.log("🌐 API URL:", apiUrl);
  //   console.log("📤 Preparing to send order to database...");

  //   // Send a POST request using fetch
  //   fetch(apiUrl, {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify(requestData),
  //   })
  //     .then((response) => {
  //       console.log("📥 Database response received");
  //       console.log("✅ Response status:", response.status);
  //       console.log("📊 Response statusText:", response.statusText);
  //       console.log("🔗 Response OK:", response.ok);

  //       if (!response.ok) {
  //         console.error("❌ Database response not OK");
  //         throw new Error("Network response was not ok");
  //       }

  //       console.log("🔄 Parsing database response as JSON...");
  //       return response.json();
  //     })
  //     .then((data) => {
  //       console.log("📋 Database response data:", data);

  //       // Handle the response data here
  //       if (data.message === "No data found in table") {
  //         console.log("⚠️ No data found in order table");
  //         console.log("🛑 Order execution stopped");
  //       } else {
  //         console.log("✅ Database order saved successfully");
  //         console.log("🚀 Proceeding to place order with 5paisa API");
  //         clickPlaceOrderPortfolio(watchlist, orderType, status);

  //         console.log("🔄 Starting holdings refresh...");

  //         const holdingsRefreshData = {
  //           AccessToken: sharedData.AccessToken,
  //           ClientCode: sharedData.ClientCode,
  //           api_key: sharedData.api_key,
  //           userId: sharedData.ID,
  //         };

  //         console.log("📦 Holdings refresh request data:", holdingsRefreshData);

  //         // ✅ Refresh holdings in DB
  //         fetch("https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getHoldingData", {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify(holdingsRefreshData),
  //         })
  //           .then((res) => {
  //             console.log("📥 Holdings refresh response received");
  //             console.log("✅ Holdings refresh status:", res.status);
  //             console.log("🔗 Holdings refresh OK:", res.ok);
  //             return res.json();
  //           })
  //           .then((holdingsData) => {
  //             console.log("✅ Holdings refreshed successfully");
  //             console.log("📊 Updated holdings data:", holdingsData);
  //           })
  //           .catch((err) => {
  //             console.error("💥 Error refreshing holdings:");
  //             console.error("❌ Holdings refresh error:", err);
  //           });
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("💥 Database operation failed:");
  //       console.error("❌ Fetch error message:", error.message);
  //       console.error("📊 Full error object:", error);
  //       console.log("🛑 Order execution terminated due to database error");
  //     });

  //   console.log("🏁 executeBuySellOrderPortfolio - Function end (async operations continue)");
  // };

  const executeBuySellOrderPortfolio = (
    requestData,
    watchlist,
    transactionSide,
    status,
  ) => {
    console.log("🔧 executeBuySellOrderPortfolio - Starting function");
    console.log("📥 Input parameters:");
    console.log("   - requestData:", requestData);
    console.log("   - watchlist:", watchlist);
    console.log("   - transactionSide:", transactionSide);
    console.log("   - status:", status);

    // ✅ REMOVED: Database call - now happens AFTER order placement
    console.log("🚀 Proceeding to PMS place_order");
    clickPlaceOrderPortfolio(requestData, watchlist, transactionSide, status);

    console.log("🏁 executeBuySellOrderPortfolio - Function end");
  };

  // const clickPlaceOrderPortfolio = (watchlist, orderType, status) => {
  //   console.log("🎯 clickPlaceOrderPortfolio - Starting 5paisa API call");
  //   console.log("📥 Input parameters:");
  //   console.log("   - watchlist:", watchlist);
  //   console.log("   - orderType:", orderType);
  //   console.log("   - status:", status);

  //   const token = sharedData.accessToken;
  //   const client = sharedData.ClientCode;
  //   const exch = watchlist.Exch;
  //   const exchType = exch === "M" ? "D" : "C";

  //   console.log("🔑 Authentication data:");
  //   console.log("   - token:", token ? `${token.substring(0, 20)}...` : "MISSING");
  //   console.log("   - client:", client);
  //   console.log("   - sharedData.api_key:", sharedData.api_key ? `${sharedData.api_key.substring(0, 20)}...` : "MISSING");

  //   console.log("🏛️ Exchange information:");
  //   console.log("   - exch:", exch);
  //   console.log("   - exchType:", exchType, `(${exch === "M" ? "Derivative" : "Cash"})`);

  //   let scripCode = "";
  //   if (status === "Portfolio") {
  //     scripCode = Number(watchlist.ScripCode) || Number(watchlist.scripcode);
  //     console.log("📋 Portfolio mode - Using pre-calculated ScripCode:");
  //     console.log(`   - Exchange: ${watchlist.Exch}, ScripCode: ${scripCode}`);
  //     console.log(`   - NseCode: ${watchlist.NseCode}, BseCode: ${watchlist.BseCode}`);
  //     console.log(`   - Final scripCode: ${scripCode}`);
  //   } else {
  //     scripCode = watchlist.Scripcode;
  //     console.log("📝 Watchlist mode - Direct scripCode:", scripCode);
  //   }

  //   let min_qty = "0";
  //   let price = "0";

  //   min_qty = buySellModalDetails.OrderQty.toString();
  //   price = orderType === "Market Order" ? "0" : buySellModalDetails.LimitPrice.toString();

  //   console.log("💰 Order details from modal:");
  //   console.log("   - orderType:", orderType);
  //   console.log("   - min_qty:", min_qty);
  //   console.log("   - price:", price);
  //   console.log("   - buySellModalDetails:", buySellModalDetails);

  //   const type = orderType;
  //   const key = sharedData.api_key;

  //   console.log("🍪 Cookie information:");
  //   console.log("COOKIE : " + document.cookie);

  //   const apiUrl = "https://openapi.5paisa.com/VendorsAPI/Service1.svc/V1/PlaceOrderRequest";
  //   console.log("🌐 5paisa API URL:", apiUrl);

  //   const requestBody = {
  //     head: {
  //       key: sharedData.api_key,
  //     },
  //     body: {
  //       ClientCode: sharedData.ClientCode,
  //       Exchange: exch,
  //       ExchangeType: exchType,
  //       Qty: min_qty,
  //       Price: orderType === "Market Order" ? 0 : parseFloat(buySellModalDetails.LimitPrice),
  //       AtMarket: orderType === "Market Order" ? "Y" : "N",
  //       OrderType: type,
  //       ScripCode: scripCode,
  //       IsIntraday: false,
  //       IsAHOrder: "N",
  //     },
  //   };

  //   console.log("📦 Complete 5paisa request body:");
  //   console.log(JSON.stringify(requestBody, null, 2));

  //   const requestHeaders = {
  //     "Content-Type": "application/json",
  //     Authorization: `Bearer ${sharedData.accessToken}`,
  //     Cookie: "5paisacookie=j2usr4wuddgegs14hcjrh3d4; NSC_JOh0em50e1pajl5b5jvyafempnkehc3=ffffffffaf103e0f45525d5f4f58455e445a4a423660; 5paisacookie=vthjiv5kqxuwrq1zizi3alt0; NSC_JOh0em50e1pajl5b5jvyafempnkehc3=ffffffffaf103e0f45525d5f4f58455e445a4a423660",
  //   };

  //   console.log("📤 Request headers:");
  //   console.log("   - Content-Type:", requestHeaders["Content-Type"]);
  //   console.log("   - Authorization:", requestHeaders.Authorization ? `Bearer ${requestHeaders.Authorization.substring(7, 27)}...` : "MISSING");
  //   console.log("   - Cookie:", requestHeaders.Cookie ? `${requestHeaders.Cookie.substring(0, 50)}...` : "MISSING");

  //   console.log("🚀 Sending request to 5paisa API...");

  //   fetch(apiUrl, {
  //     method: "POST",
  //     headers: requestHeaders,
  //     body: JSON.stringify(requestBody),
  //   })
  //     .then((response) => {
  //       console.log("📥 5paisa API response received");
  //       console.log("✅ Response status:", response.status);
  //       console.log("📊 Response statusText:", response.statusText);
  //       console.log("🔗 Response OK:", response.ok);

  //       if (!response.ok) {
  //         console.error("❌ 5paisa API response not OK");
  //         console.error("💥 HTTP Error:", response.status, response.statusText);
  //       }

  //       console.log("🔄 Parsing 5paisa response as JSON...");
  //       return response.json();
  //     })
  //     .then((data) => {
  //       console.log("📋 5paisa API Response Data:");
  //       console.log("Place Order : " + data.body.Message);
  //       console.log("Place Order : " + JSON.stringify(data, null, 2));

  //       const message = data.body.Message || "";
  //       const rmsResponseCode = data.body.RMSResponseCode;

  //       // 🔒 CHECK FOR CDSL AUTHORIZATION ERROR - MUST BE FIRST
  //       console.log("🔍 Checking for CDSL authorization error...");
  //       console.log("   - RMSResponseCode:", rmsResponseCode);
  //       console.log("   - Message:", message);

  //       if (
  //         rmsResponseCode === -162 ||
  //         message.toLowerCase().includes("sell authorization not received") ||
  //         message.toLowerCase().includes("authorization not received")
  //       ) {
  //         console.log("🚨 CDSL Authorization Required - Opening Modal");

  //         // Show error toast
  //         // toast.error
  //         showErrorToast("CDSL authorization required to place sell orders");

  //         // Close the buy/sell modal FIRST
  //         console.log("🔄 Closing buy/sell modal...");
  //         if (orderType === "Buy") {
  //           document.getElementById("buyModalClick")?.click();
  //         } else if (orderType === "Sell") {
  //           document.getElementById("sellModalClick")?.click();
  //         }

  //         // Wait for modal to close, then open CDSL modal
  //         setTimeout(() => {
  //           const triggerButton = document.getElementById('openCDSLModalBtn');
  //           console.log("🔍 Looking for openCDSLModalBtn button:", triggerButton);

  //           if (triggerButton) {
  //             triggerButton.click();
  //             console.log("✅ CDSL Authorization Modal opened successfully");
  //           } else {
  //             console.error("❌ openCDSLModalBtn button NOT FOUND in DOM");
  //             console.error("   Make sure the button exists in Portfolio.jsx with id='openCDSLModalBtn'");
  //           }
  //         }, 500);

  //         console.log("🛑 Order placement stopped - CDSL authorization required");
  //         return; // EXIT EARLY - THIS IS CRITICAL - DO NOT REMOVE THIS LINE
  //       }

  //       // 📊 HANDLE OTHER RESPONSE MESSAGES (only reached if NOT CDSL error)
  //       console.log("✅ No CDSL error detected - processing normal response");

  //       if (message.includes("Pending") || message.includes("pending")) {
  //         showWarningToast(message);
  //       } else if (message.includes("rejected") || message.includes("Rejected")) {
  //         showErrorToast(message);
  //       } else if (message.includes("partially") || message.includes("Partially")) {
  //         toast.success(message, { style: { background: "lightgreen", color: "#000" } });
  //         pingNotification()
  //       } else if (message.includes("Fully") || message.includes("fully")) {
  //         toast.success(message, { style: { background: "darkgreen", color: "#fff" } });
  //         pingNotification()
  //       } else if (message.includes("Cancelled") || message.includes("cancelled")) {
  //         toast(message, { style: { background: "gray", color: "#fff" } });
  //         pingNotification()
  //       } else if (message.includes("Accepted") || message.includes("accepted")) {
  //         showSuccessToast(message);
  //       } else {
  //         toast.info(message);
  //         pingNotification()
  //       }

  //       console.log("🔄 Triggering post-order actions...");

  //       if (orderType === "Buy") {
  //         console.log("💚 Buy order - triggering buyModalClick");
  //         document.getElementById("buyModalClick")?.click();
  //       } else if (orderType === "Sell") {
  //         console.log("🔴 Sell order - triggering sellModalClick");
  //         document.getElementById("sellModalClick")?.click();
  //       }

  //       console.log("✅ Order process completed successfully");
  //       console.log("END");
  //     })
  //     .catch((error) => {
  //       console.error("💥 5paisa API call failed:");
  //       console.error("❌ Error type:", error.name);
  //       console.error("❌ Error message:", error.message);
  //       console.error("📊 Full error object:", error);
  //       console.log("🛑 Order placement terminated due to API error");

  //       // toast.error
  //       showErrorToast("Failed to place order. Please try again.");
  //     });

  //   console.log("🏁 clickPlaceOrderPortfolio - Function end (async 5paisa call continues)");
  // };

  // const clickPlaceOrderPortfolio = (requestData, watchlist, orderType, status) => {
  //   console.log("🎯 clickPlaceOrderPortfolio - Starting 5paisa API call");
  //   console.log("📥 Input parameters:");
  //   console.log("   - requestData:", requestData);
  //   console.log("   - watchlist:", watchlist);
  //   console.log("   - orderType:", orderType);
  //   console.log("   - status:", status);

  //   const token = sharedData.accessToken;
  //   const client = sharedData.ClientCode;
  //   const exch = watchlist.Exch;
  //   const exchType = exch === "M" ? "D" : "C";

  //   console.log("🔑 Authentication data:");
  //   console.log("   - token:", token ? `${token.substring(0, 20)}...` : "MISSING");
  //   console.log("   - client:", client);
  //   console.log("   - sharedData.api_key:", sharedData.api_key ? `${sharedData.api_key.substring(0, 20)}...` : "MISSING");

  //   console.log("🏛️ Exchange information:");
  //   console.log("   - exch:", exch);
  //   console.log("   - exchType:", exchType, `(${exch === "M" ? "Derivative" : "Cash"})`);

  //   let scripCode = "";
  //   if (status === "Portfolio") {
  //     scripCode = Number(watchlist.ScripCode) || Number(watchlist.scripcode);
  //     console.log("📋 Portfolio mode - Using pre-calculated ScripCode:");
  //     console.log(`   - Exchange: ${watchlist.Exch}, ScripCode: ${scripCode}`);
  //     console.log(`   - NseCode: ${watchlist.NseCode}, BseCode: ${watchlist.BseCode}`);
  //     console.log(`   - Final scripCode: ${scripCode}`);
  //   } else {
  //     scripCode = watchlist.Scripcode;
  //     console.log("📝 Watchlist mode - Direct scripCode:", scripCode);
  //   }

  //   let min_qty = "0";
  //   let price = "0";

  //   min_qty = buySellModalDetails.OrderQty.toString();
  //   price = orderType === "Market Order" ? "0" : buySellModalDetails.LimitPrice.toString();

  //   console.log("💰 Order details from modal:");
  //   console.log("   - orderType:", orderType);
  //   console.log("   - min_qty:", min_qty);
  //   console.log("   - price:", price);
  //   console.log("   - buySellModalDetails:", buySellModalDetails);

  //   const type = orderType;
  //   const key = sharedData.api_key;

  //   console.log("🍪 Cookie information:");
  //   console.log("COOKIE : " + document.cookie);

  //   const apiUrl = "https://openapi.5paisa.com/VendorsAPI/Service1.svc/V1/PlaceOrderRequest";
  //   console.log("🌐 5paisa API URL:", apiUrl);

  //   const requestBody = {
  //     head: {
  //       key: sharedData.api_key,
  //     },
  //     body: {
  //       ClientCode: sharedData.ClientCode,
  //       Exchange: exch,
  //       ExchangeType: exchType,
  //       Qty: min_qty,
  //       Price: orderType === "Market Order" ? 0 : parseFloat(buySellModalDetails.LimitPrice),
  //       AtMarket: orderType === "Market Order" ? "Y" : "N",
  //       OrderType: type,
  //       ScripCode: scripCode,
  //       IsIntraday: false,
  //       IsAHOrder: "N",
  //     },
  //   };

  //   console.log("📦 Complete 5paisa request body:");
  //   console.log(JSON.stringify(requestBody, null, 2));

  //   const requestHeaders = {
  //     "Content-Type": "application/json",
  //     Authorization: `Bearer ${sharedData.accessToken}`,
  //     Cookie: "5paisacookie=j2usr4wuddgegs14hcjrh3d4; NSC_JOh0em50e1pajl5b5jvyafempnkehc3=ffffffffaf103e0f45525d5f4f58455e445a4a423660; 5paisacookie=vthjiv5kqxuwrq1zizi3alt0; NSC_JOh0em50e1pajl5b5jvyafempnkehc3=ffffffffaf103e0f45525d5f4f58455e445a4a423660",
  //   };

  //   console.log("📤 Request headers:");
  //   console.log("   - Content-Type:", requestHeaders["Content-Type"]);
  //   console.log("   - Authorization:", requestHeaders.Authorization ? `Bearer ${requestHeaders.Authorization.substring(7, 27)}...` : "MISSING");
  //   console.log("   - Cookie:", requestHeaders.Cookie ? `${requestHeaders.Cookie.substring(0, 50)}...` : "MISSING");

  //   console.log("🚀 Sending request to 5paisa API...");

  //   fetch(apiUrl, {
  //     method: "POST",
  //     headers: requestHeaders,
  //     body: JSON.stringify(requestBody),
  //   })
  //     .then((response) => {
  //       console.log("📥 5paisa API response received");
  //       console.log("✅ Response status:", response.status);
  //       console.log("📊 Response statusText:", response.statusText);
  //       console.log("🔗 Response OK:", response.ok);

  //       if (!response.ok) {
  //         console.error("❌ 5paisa API response not OK");
  //         console.error("💥 HTTP Error:", response.status, response.statusText);
  //       }

  //       console.log("🔄 Parsing 5paisa response as JSON...");
  //       return response.json();
  //     })
  //     .then((data) => {
  //       console.log("📋 5paisa API Response Data:");
  //       console.log("Place Order : " + data.body.Message);
  //       console.log("Place Order : " + JSON.stringify(data, null, 2));

  //       const message = data.body.Message || "";
  //       const rmsResponseCode = data.body.RMSResponseCode;
  //       const brokerOrderID = data.body.BrokerOrderID || "";
  //       const exchangeOrderID = data.body.ExchOrderID || "";

  //       // 🆕 Determine order status based on Message content
  //       let orderStatus = "Unknown";
  //       let orderReason = message;

  //       console.log("🔍 Analyzing message for order status...");
  //       console.log("   - Message:", message);
  //       console.log("   - RMSResponseCode:", rmsResponseCode);

  //       // 🔒 CHECK FOR CDSL AUTHORIZATION ERROR FIRST
  //       if (
  //         rmsResponseCode === -162 ||
  //         message.toLowerCase().includes("sell authorization not received") ||
  //         message.toLowerCase().includes("authorization not received")
  //       ) {
  //         console.log("🚨 CDSL Authorization Required");
  //         orderStatus = "CDSL Authorization Required";

  //         // ✅ Save to database with CDSL error status
  //         saveOrderToDatabase(requestData, orderStatus, orderReason, brokerOrderID, exchangeOrderID, data);

  //         showErrorToast("CDSL authorization required to place sell orders");

  //         // Close the buy/sell modal FIRST
  //         console.log("🔄 Closing buy/sell modal...");
  //         if (orderType === "Buy") {
  //           document.getElementById("buyModalClick")?.click();
  //         } else if (orderType === "Sell") {
  //           document.getElementById("sellModalClick")?.click();
  //         }

  //         // Wait for modal to close, then open CDSL modal
  //         setTimeout(() => {
  //           const triggerButton = document.getElementById('openCDSLModalBtn');
  //           console.log("🔍 Looking for openCDSLModalBtn button:", triggerButton);

  //           if (triggerButton) {
  //             triggerButton.click();
  //             console.log("✅ CDSL Authorization Modal opened successfully");
  //           } else {
  //             console.error("❌ openCDSLModalBtn button NOT FOUND in DOM");
  //           }
  //         }, 500);

  //         console.log("🛑 Order placement stopped - CDSL authorization required");
  //         return; // Exit early
  //       }

  //       // 📊 Determine order status from message keywords
  //       const messageLower = message.toLowerCase();

  //       if (messageLower.includes("fully") || messageLower.includes("complete") || messageLower.includes("Fully") || messageLower.includes("Executed") || messageLower.includes("success")  ) {
  //         orderStatus = "Fully Executed";
  //         console.log("✅ Order Status: Fully Executed");
  //       } else if (messageLower.includes("partially") || messageLower.includes("Partially") ) {
  //         orderStatus = "Partially Executed";
  //         console.log("⚠️ Order Status: Partially Executed");
  //       } else if (messageLower.includes("accepted") || messageLower.includes("Accepted")) {
  //         orderStatus = "Accepted";
  //         console.log("✅ Order Status: Accepted");
  //       } else if (messageLower.includes("pending") || messageLower.includes("Pending")) {
  //         orderStatus = "Pending";
  //         console.log("⏳ Order Status: Pending");
  //       } else if (messageLower.includes("rejected") || messageLower.includes("cannot place")  || messageLower.includes("Rejected")  || messageLower.includes("Cannot place")) {
  //         orderStatus = "Rejected";
  //         console.log("❌ Order Status: Rejected");
  //       } else if (messageLower.includes("cancelled")  || messageLower.includes("Cancelled")) {
  //         orderStatus = "Cancelled";
  //         console.log("🚫 Order Status: Cancelled");
  //       } else if (messageLower.includes("closed") || messageLower.includes("exchange is closed")  || messageLower.includes("Closed")) {
  //         orderStatus = "Exchange Closed";
  //         console.log("🔒 Order Status: Exchange Closed");
  //       } else if (messageLower.includes("insufficient")  || messageLower.includes("Insufficient")) {
  //         orderStatus = "Insufficient Funds";
  //         console.log("💰 Order Status: Insufficient Funds");
  //       } else if (messageLower.includes("invalid")  || messageLower.includes("Invalid") ) {
  //         orderStatus = "Invalid Order";
  //         console.log("⚠️ Order Status: Invalid Order");
  //       } else if (messageLower.includes("timeout") || messageLower.includes("timed out")  || messageLower.includes("Timeout")  || messageLower.includes("Timed out")) {
  //         orderStatus = "Timeout";
  //         console.log("⏱️ Order Status: Timeout");
  //       } else {
  //         orderStatus = "Unknown";
  //         console.log("❓ Order Status: Unknown");
  //       }

  //       console.log("📊 Final Determined Order Status:", orderStatus);
  //       console.log("📝 Order Reason:", orderReason);

  //       // ✅ 🆕 SAVE ORDER TO DATABASE WITH STATUS
  //       console.log("💾 Saving order to database with status...");
  //       saveOrderToDatabase(requestData, orderStatus, orderReason, brokerOrderID, exchangeOrderID, data);

  //       // 📊 HANDLE UI NOTIFICATIONS BASED ON STATUS
  //       console.log("🔔 Showing notification based on status...");

  //       if (orderStatus === "Pending") {
  //         showWarningToast(message);
  //       } else if (orderStatus === "Rejected" || orderStatus === "Exchange Closed" || orderStatus === "Invalid Order") {
  //         showErrorToast(message);
  //       } else if (orderStatus === "Partially Executed") {
  //         toast.success(message, { style: { background: "lightgreen", color: "#000" } });
  //         pingNotification();
  //       } else if (orderStatus === "Fully Executed") {
  //         toast.success(message, { style: { background: "darkgreen", color: "#fff" } });
  //         pingNotification();
  //       } else if (orderStatus === "Cancelled") {
  //         toast(message, { style: { background: "gray", color: "#fff" } });
  //         pingNotification();
  //       } else if (orderStatus === "Accepted") {
  //         showSuccessToast(message);
  //       } else if (orderStatus === "Insufficient Funds") {
  //         showErrorToast(message);
  //       } else if (orderStatus === "Timeout") {
  //         showWarningToast(message);
  //       } else {
  //         toast.info(message);
  //         pingNotification();
  //       }

  //       console.log("🔄 Triggering post-order actions...");

  //       if (orderType === "Buy") {
  //         console.log("💚 Buy order - triggering buyModalClick");
  //         document.getElementById("buyModalClick")?.click();
  //       } else if (orderType === "Sell") {
  //         console.log("🔴 Sell order - triggering sellModalClick");
  //         document.getElementById("sellModalClick")?.click();
  //       }

  //       // 🔄 Refresh holdings only if order was successful
  //       if (orderStatus === "Fully Executed" || orderStatus === "Partially Executed" || orderStatus === "Accepted") {
  //         console.log("🔄 Starting holdings refresh...");
  //         updateMargin();
  //         refreshHoldings();

  //       } else {
  //         console.log("⏭️ Skipping holdings refresh - order not executed");
  //       }

  //       setTimeout(() => {
  //         // navigate('/Order');
  //       }, 1000); // Wait 1 second for modal close and toast

  //       console.log("✅ Order process completed successfully");
  //       console.log("END");
  //     })
  //     .catch((error) => {
  //       console.error("💥 5paisa API call failed:");
  //       console.error("❌ Error type:", error.name);
  //       console.error("❌ Error message:", error.message);
  //       console.error("📊 Full error object:", error);
  //       console.log("🛑 Order placement terminated due to API error");

  //       // ✅ Save failed order to database
  //       saveOrderToDatabase(requestData, "API Error", error.message, "", "", null);

  //       showErrorToast("Failed to place order. Please try again.");
  //       setTimeout(() => {
  //         // navigate('/Order');
  //       }, 1500); // Wait 1.5 seconds for error toast
  //     });

  //   console.log("🏁 clickPlaceOrderPortfolio - Function end (async 5paisa call continues)");
  // };

  const clickPlaceOrderPortfolio = (
    requestData,
    watchlist,
    transactionSide,
    status,
  ) => {
    const executionType = orderType;

    console.log("🎯 clickPlaceOrderPortfolio — PMS /api/v1/place_order");
    console.log("   transactionSide:", transactionSide, "executionType:", executionType);

    placeOrderViaPms({
      pmsApiBaseUrl,
      sharedData,
      watchlist,
      listStatus: status,
      transactionSide,
      executionType,
      qty: buySellModalDetails.OrderQty,
      limitPrice: buySellModalDetails.LimitPrice,
    }).then((result) => {
      if (result.error) {
        console.error("💥 place_order:", result.error);
        showErrorToast("Failed to place order. Please try again.");
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      const data = result.data;
      console.log("📋 place_order response:", JSON.stringify(data, null, 2));

      const normalized = normalizePlaceOrderResponse(data);

      if (!result.ok) {
        const msg = normalized.message || "Failed to place order";
        showErrorToast(msg);
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      const message = normalized.message || "";
      const rmsResponseCode = normalized.rmsResponseCode;
      const brokerOrderID = normalized.brokerOrderID;
      const exchangeOrderID = normalized.exchangeOrderID;

      if (
        isFivePaisaBroker(sharedData) &&
        (rmsResponseCode === -162 ||
          message.toLowerCase().includes("sell authorization not received") ||
          message.toLowerCase().includes("authorization not received"))
      ) {
        showErrorToast("CDSL authorization required to place sell orders");
        if (transactionSide === "Buy") {
          document.getElementById("buyModalClick")?.click();
        } else if (transactionSide === "Sell") {
          document.getElementById("sellModalClick")?.click();
        }
        setTimeout(() => {
          document.getElementById("openCDSLModalBtn")?.click();
        }, 500);
        return;
      }

      if (normalized.headStatus !== "0") {
        const rejectionMsg = String(message || "");
        const isGrowwTpinError =
          !isFivePaisaBroker(sharedData) &&
          String(transactionSide || "").toLowerCase() === "sell" &&
          rejectionMsg
            .toLowerCase()
            .includes("verify your holdings first via tpin");

        if (isGrowwTpinError) {
          setGrowwTpinErrorText(
            rejectionMsg ||
            "Verify your holdings first via TPIN from Groww Dashboard.",
          );
          if (transactionSide === "Buy") {
            document.getElementById("buyModalClick")?.click();
          } else if (transactionSide === "Sell") {
            document.getElementById("sellModalClick")?.click();
          }
          setTimeout(() => {
            document.getElementById("openGrowwTPINModalBtn")?.click();
          }, 500);
          return;
        }

        showErrorToast(rejectionMsg || "Failed to place order");
        setTimeout(() => navigate("/Order"), 1500);
        return;
      }

      runPlaceOrderAcceptedFlow({
        transactionSide,
        tr,
        showSuccessToast,
        navigate,
      });
    });

    console.log(
      "🏁 clickPlaceOrderPortfolio - Function end (async PMS call continues)",
    );
  };

  const refreshHoldings = () => {
    console.log("🔄 Starting holdings refresh...");
  };

  // const updateMargin = () => {
  //   console.log("💰 updateMargin - Starting margin update");

  //   // Get sharedData from localStorage
  //   const saved = JSON.parse(localStorage.getItem("sharedData"));

  //   if (!saved || !saved.accessToken || !saved.api_key || !saved.ClientCode) {
  //     console.error("❌ Missing required data for margin update");
  //     return;
  //   }

  //   const apiUrl = "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V4/Margin";
  //   console.log("🌐 Margin API URL:", apiUrl);

  //   const requestBody = {
  //     head: {
  //       key: saved.api_key
  //     },
  //     body: {
  //       ClientCode: saved.ClientCode
  //     }
  //   };

  //   console.log("📦 Margin request body:", requestBody);

  //   const requestHeaders = {
  //     "Authorization": `Bearer ${saved.accessToken}`,
  //     "Content-Type": "application/json"
  //   };

  //   console.log("📤 Sending margin update request...");

  //   fetch(apiUrl, {
  //     method: "POST",
  //     headers: requestHeaders,
  //     body: JSON.stringify(requestBody)
  //   })
  //     .then((response) => {
  //       console.log("📥 Margin API response received");
  //       console.log("✅ Response status:", response.status);
  //       console.log("✅ Response", response);

  //       if (!response.ok) {
  //         console.error("❌ Margin API response not OK");
  //         throw new Error("Margin API response was not ok");
  //       }

  //       return response.json();
  //     })
  //     .then((data) => {
  //       console.log("📋 Margin API Response Data:");
  //       console.log(JSON.stringify(data, null, 2));

  //       // Extract NetAvailableMargin from response
  //       const equityMargin = data.body?.EquityMargin?.[0];

  //       if (equityMargin && equityMargin.NetAvailableMargin !== undefined) {
  //         const netAvailableMargin = equityMargin.NetAvailableMargin;
  //         console.log("💵 NetAvailableMargin:", netAvailableMargin);

  //         // Update sharedData in localStorage
  //         const currentSharedData = JSON.parse(localStorage.getItem("sharedData"));
  //         currentSharedData.margin = netAvailableMargin;

  //         localStorage.setItem("sharedData", JSON.stringify(currentSharedData));
  //         console.log("✅ Margin updated in localStorage:", netAvailableMargin);

  //         // 🆕 Optional: Update state if you have margin in component state
  //         // setSharedData(currentSharedData); // Uncomment if you use state

  //         // Show success toast
  //         console.log("💰 Available Margin: ₹" + netAvailableMargin.toFixed(2));

  //       } else {
  //         console.error("❌ NetAvailableMargin not found in response");
  //         console.error("📊 EquityMargin data:", equityMargin);
  //       }
  //     })
  //     .catch((error) => {
  //       console.error("💥 Margin API call failed:");
  //       console.error("❌ Error type:", error.name);
  //       console.error("❌ Error message:", error.message);
  //       console.error("📊 Full error object:", error);
  //     });

  //   console.log("🏁 updateMargin - Function end (async call continues)");
  // };

  const updateMargin = () => {
    console.log("💰 updateMargin - Starting margin update");

    // Get sharedData from localStorage
    const saved = JSON.parse(localStorage.getItem("sharedData"));

    if (!saved || !saved.accessToken || !saved.api_key || !saved.ClientCode) {
      console.error("❌ Missing required data for margin update");
      return;
    }

    const apiUrl =
      "https://Openapi.5paisa.com/VendorsAPI/Service1.svc/V4/Margin";
    console.log("🌐 Margin API URL:", apiUrl);

    const requestBody = {
      head: {
        key: saved.api_key,
      },
      body: {
        ClientCode: saved.ClientCode,
      },
    };

    console.log("📦 Margin request body:", requestBody);

    const requestHeaders = {
      Authorization: `Bearer ${saved.accessToken}`,
      "Content-Type": "application/json",
    };

    console.log("📤 Sending margin update request...");

    fetch(apiUrl, {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        console.log("📥 Margin API response received");
        console.log("✅ Response status:", response.status);
        console.log("✅ Response", response);

        if (!response.ok) {
          console.error("❌ Margin API response not OK");
          throw new Error("Margin API response was not ok");
        }

        return response.json();
      })
      .then((data) => {
        console.log("📋 Margin API Response Data:");
        console.log(JSON.stringify(data, null, 2));

        // Extract NetAvailableMargin from response
        const equityMargin = data.body?.EquityMargin?.[0];

        if (equityMargin && equityMargin.NetAvailableMargin !== undefined) {
          const netAvailableMargin = equityMargin.NetAvailableMargin;
          console.log("💵 NetAvailableMargin:", netAvailableMargin);

          // Update sharedData in localStorage
          const currentSharedData = JSON.parse(
            localStorage.getItem("sharedData"),
          );

          // ✅ Convert number to comma-separated string (Indian format)
          currentSharedData.margin = netAvailableMargin.toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          );

          localStorage.setItem("sharedData", JSON.stringify(currentSharedData));
          console.log(
            "✅ Margin updated in localStorage:",
            currentSharedData.margin,
          );

          // 🆕 Optional: Update state if you have margin in component state
          // setSharedData(currentSharedData); // Uncomment if you use state

          // Show success toast
          console.log("💰 Available Margin: ₹" + currentSharedData.margin);
        } else {
          console.error("❌ NetAvailableMargin not found in response");
          console.error("📊 EquityMargin data:", equityMargin);
        }
      })
      .catch((error) => {
        console.error("💥 Margin API call failed:");
        console.error("❌ Error type:", error.name);
        console.error("❌ Error message:", error.message);
        console.error("📊 Full error object:", error);
      });

    console.log("🏁 updateMargin - Function end (async call continues)");
  };

  const getPriceForCalculation = () => {
    if (orderType === "Market Order") {
      const cacheKey = getOrderLivePriceCacheKey(buySellModalDetails.order);
      const livePrice = livePriceCache.current[cacheKey];
      return (
        livePrice ||
        buySellModalDetails.order?.CurrentPrice ||
        buySellModalDetails.order?.initialPrice ||
        0
      );
    } else {
      // Use limit price for Limit orders
      return buySellModalDetails.LimitPrice || 0;
    }
  };

  //----------------------------// End Buy-Sell Functions //----------------------------//`

  //----------------------------//  Market Depth Functions //----------------------------//`
  // Function to subscribe to MarketDepth for a given stock
  // 🔥 Subscribe to Market Depth (Order Book) for a stock
  const subscribeToMarketDepth = (holding) => {
    if (!holding) return;

    if (activeBrokerFlag === "5paisa") {
      fivePaisaMarketDepthStore.openDepth(holding);
      return;
    }

    if (activeBrokerFlag === "angelone") {
      const userId =
        Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
      angelOneMarketDepthService.openDepth(holding, {
        pmsApiBaseUrl,
        userId,
      });
    }
  };

  const unsubscribeFromMarketDepth = () => {
    if (activeBrokerFlag === "5paisa") {
      fivePaisaMarketDepthStore.closeDepth();
      return;
    }

    if (activeBrokerFlag === "angelone") {
      angelOneMarketDepthService.closeDepth();
    }
  };

  // Function to run when modal closes

  const handleModalClose = () => {
    console.log("📉📉📉 MarketDepth modal closed!");

    if (currentMDModalRef.current) {
      console.log("📉 Unsubscribing to:", currentMDModalRef.current);
      unsubscribeFromMarketDepth();
      console.log("📉 Unsubscribed");

      // Clear the ref so it doesn't hold stale data
      currentMDModalRef.current = null;
      setMarketDepth([]);
    }
  };

  
  const closeMarketDepthPopover = () => {
    unsubscribeFromMarketDepth();
    setcurrentMDModal(null);
    setMarketDepth([]);
    setShowMarketDepth(false);
  };

  //----------------------------// End market Depth Functions //----------------------------//

  //----------------------------//  Set Target in Watchlist Functions //----------------------------//

  const openSetTargetModal = (holding, fallbackCurrentPrice) => {
    const scripCode = holding?.ScripCode || holding?.scripcode;
    const livePrice = scripCode ? livePriceCache.current?.[scripCode] : undefined;
    const previousClose = scripCode
      ? livePCloseCache.current?.[scripCode]
      : undefined;
    const resolvedCurrentPrice = livePrice ?? fallbackCurrentPrice ?? 0;

    setTargetDetails({
      ...holding,
      initialPrice: resolvedCurrentPrice,
      currentPrice: resolvedCurrentPrice,
      modalCurrentPrice: resolvedCurrentPrice,
      modalPreviousClose:
        previousClose ??
        holding?.["Current Price"] ??
        holding?.previousClose ??
        fallbackCurrentPrice ??
        0,
    });

    setTimeout(() => {
      const modalElement = document.getElementById("SetTargetModal");
      if (!modalElement) return;

      const modal = Modal.getOrCreateInstance(modalElement);
      modal.show();
    }, 0);
  };

  //----------------------------// END of Set Target in Watchlist Functions //----------------------------//

  //----------------------------// Drag-N-Drop Functions //----------------------------//
  // Save column order to PMS (module_name must stay lowercase: "portfolio")
  const saveColumnOrder = async (newOrder) => {
    try {
      const userId =
        Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
      const userEmail =
        sharedData?.Email_ID ||
        sharedData?.email ||
        localStorage.getItem("email") ||
        "";
      if (!userEmail || !userId) {
        console.warn("saveColumnOrder: missing user context, skipping PMS reorder");
        return;
      }

      const pmsBrokerFlag = toPmsBrokerFlag(activeBrokerFlag);
      const columnsPayload = [];
      let sortOrder = 1;
      for (const label of newOrder) {
        if (label === "Actions") continue;
        const columnKey = PORTFOLIO_TABLE_COL_TO_PMS_COLUMN_KEY[label] || label;
        columnsPayload.push({ column_key: columnKey, sort_order: sortOrder });
        sortOrder += 1;
      }
      if (columnsPayload.length === 0) return;

      const response = await fetch(`${pmsApiBaseUrl}/api/v1/columns/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          email: userEmail,
          broker_flag: pmsBrokerFlag,
          module_name: "portfolio",
          columns: columnsPayload,
        }),
      });
      const result = await response.json();
      console.log("Portfolio column reorder (PMS):", result);
    } catch (err) {
      console.error("Error saving column order:", err);
    }
  };

  const appendSelectedExtraColumns = async (checkedNames = []) => {
    try {
      const userId =
        Number(localStorage.getItem("id")) || Number(sharedData?.ID) || 0;
      const userEmail =
        sharedData?.Email_ID ||
        sharedData?.email ||
        localStorage.getItem("email") ||
        "";
      if (!userId || !userEmail) {
        console.warn(
          "appendSelectedExtraColumns: missing user context, skipping PMS extra append",
        );
        return;
      }
      const pmsBrokerFlag = toPmsBrokerFlag(activeBrokerFlag);
      const columns = Array.from(
        new Set(
          (Array.isArray(checkedNames) ? checkedNames : []).filter(
            (col) => col && !defaultColumns.includes(col) && col !== "Actions",
          ),
        ),
      );

      const response = await fetch(
        `${pmsApiBaseUrl}/api/v1/columns/extra/append`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            broker_flag: pmsBrokerFlag,
            module_name: "portfolio",
            user_id: userId,
            email: userEmail,
            columns,
          }),
        },
      );
      const result = await response.json();
      console.log("Portfolio extra columns append (PMS):", result);
    } catch (err) {
      console.error("Error appending selected extra columns:", err);
    }
  };

  // Drag Start
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);

    const dragPreview = document.createElement("div");
    dragPreview.style.position = "absolute";
    dragPreview.style.top = "-9999px";
    dragPreview.style.padding = "6px 16px";
    dragPreview.style.background = "#C9E0FF";
    dragPreview.style.border = "1px solid #99bde0";
    dragPreview.style.borderRadius = "4px";
    dragPreview.style.color = "#000";
    dragPreview.style.fontSize = "16px";
    dragPreview.style.fontWeight = "bold";
    dragPreview.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.15)";
    dragPreview.textContent = columnKey;

    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 0, 0);

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  // Drag Over
  const handleDragOver = (e) => {
    e.preventDefault(); // Allow drop
  };

  // Drop
  // const handleDrop = (e, targetColumnKey) => {
  //   e.preventDefault();

  //   const draggedIndex = columnOrder.indexOf(draggedColumn);
  //   const targetIndex = columnOrder.indexOf(targetColumnKey);

  //   if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
  //     setDraggedColumn(null);
  //     return;
  //   }

  //   const tableContainer = document.querySelector(".table").closest("div");
  //   if (tableContainer) tableContainer.style.overflowY = "hidden";

  //   const newColumnOrder = [...columnOrder];
  //   const [movedColumn] = newColumnOrder.splice(draggedIndex, 1);
  //   newColumnOrder.splice(targetIndex, 0, movedColumn);

  //   setColumnOrder(newColumnOrder);
  //   setDraggedColumn(null);
  //   saveColumnOrder(newColumnOrder);

  //   requestAnimationFrame(() => {
  //     const cells = document.querySelectorAll(`[data-column-id="${movedColumn}"]`);
  //     const table = document.querySelector("table");
  //     const tableHeight = table.offsetHeight;
  //     table.style.height = `${tableHeight}px`;

  //     cells.forEach((cell) => {
  //       void cell.offsetWidth;
  //       cell.classList.add("column-drop-animate");
  //     });

  //     setTimeout(() => {
  //       cells.forEach((cell) => {
  //         cell.classList.remove("column-drop-animate");
  //       });
  //       table.style.height = "";
  //       if (tableContainer) tableContainer.style.overflowY = "";

  //       table.classList.add("table-highlight-column");
  //       setTimeout(() => {
  //         table.classList.remove("table-highlight-column");
  //       }, 600);
  //     }, 850);
  //   });
  // };

  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();

    const draggedIndex = columnOrder.indexOf(draggedColumn);
    const targetIndex = columnOrder.indexOf(targetColumnKey);

    if (
      draggedIndex === -1 ||
      targetIndex === -1 ||
      draggedIndex === targetIndex
    ) {
      setDraggedColumn(null);
      return;
    }

    const newColumnOrder = [...columnOrder];
    const [movedColumn] = newColumnOrder.splice(draggedIndex, 1);
    newColumnOrder.splice(targetIndex, 0, movedColumn);

    setColumnOrder(newColumnOrder);
    setDraggedColumn(null);
    saveColumnOrder(newColumnOrder);

    // Double RAF to ensure layout is complete before animations
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const cells = document.querySelectorAll(
          `[data-column-id="${movedColumn}"]`,
        );

        if (cells.length > 0) {
          // Start animation
          cells.forEach((cell) => cell.classList.add("column-drop-animate"));

          // Remove animation after timeout
          setTimeout(() => {
            cells.forEach((cell) =>
              cell.classList.remove("column-drop-animate"),
            );

            // Temporary highlight of table
            const table = document.querySelector("table");
            if (table) {
              table.classList.add("table-highlight-column");
              setTimeout(() => {
                table.classList.remove("table-highlight-column");
              }, 600);
            }
          }, 850);
        }
      });
    });
  };

  // Drag End
  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const getColumnDisplayWidth = useCallback(
    (column) => {
      if (columnWidths[column]) return columnWidths[column];
      if (column === "Name") return 220;
      if (column === "Actions") return 150;
      return 130;
    },
    [columnWidths],
  );

  const pinnedOffsets = useMemo(() => {
    const left = {};
    const right = {};
    let leftOffset = 0;
    columnOrder.forEach((column) => {
      if (pinnedColumns[column] === "left") {
        left[column] = leftOffset;
        leftOffset += getColumnDisplayWidth(column);
      }
    });
    let rightOffset = getColumnDisplayWidth("Actions");
    [...columnOrder].reverse().forEach((column) => {
      if (column === "Actions") return;
      if (pinnedColumns[column] === "right") {
        right[column] = rightOffset;
        rightOffset += getColumnDisplayWidth(column);
      }
    });
    return { left, right };
  }, [columnOrder, pinnedColumns, getColumnDisplayWidth]);

  const applyPinnedHeaderPriority = useCallback(
    (el, col) => {
      if (!el) return;
      el.style.removeProperty("z-index");
      el.style.removeProperty("left");
      el.style.removeProperty("right");
      if (pinnedColumns[col] === "left") {
        el.style.setProperty("z-index", "1300", "important");
        el.style.setProperty("left", `${pinnedOffsets.left[col] || 0}px`, "important");
        return;
      }
      if (col === "Actions") {
        el.style.setProperty("z-index", "1200", "important");
        el.style.setProperty("right", "0px", "important");
        return;
      }
      if (pinnedColumns[col] === "right") {
        el.style.setProperty("z-index", "1250", "important");
        el.style.setProperty(
          "right",
          `${pinnedOffsets.right[col] || 0}px`,
          "important",
        );
      }
    },
    [pinnedColumns, pinnedOffsets],
  );

  const handlePortfolioHeaderSort = useCallback((column, direction) => {
    setSortConfig({ column, direction });
    setCurrentPage(1);
    setActiveHeaderMenu(null);
  }, []);

  const handlePinColumn = useCallback((column, pinSide) => {
    if (column === "Actions") return;
    setPinnedColumns((prev) => ({ ...prev, [column]: pinSide }));
    setActiveHeaderMenu(null);
  }, []);

  const handleUnpinColumn = useCallback((column) => {
    setPinnedColumns((prev) => {
      if (!prev[column]) return prev;
      const next = { ...prev };
      delete next[column];
      return next;
    });
    setActiveHeaderMenu(null);
  }, []);

  const handleAutoSizeColumn = useCallback(
    (column) => {
      if (column === "Actions") return;
      const headerLen = String(column || "").length;
      const sampleMaxLen = sortedHoldings.slice(0, 200).reduce((maxLen, row) => {
        const rawValue = getPortfolioSortableValue(row, column);
        const len = String(rawValue ?? "").length;
        return Math.max(maxLen, len);
      }, headerLen);
      const computedWidth = Math.min(380, Math.max(110, sampleMaxLen * 9 + 40));
      setColumnWidths((prev) => ({ ...prev, [column]: computedWidth }));
      setActiveHeaderMenu(null);
    },
    [getPortfolioSortableValue, sortedHoldings],
  );

  const renderPortfolioHeaderMenu = useCallback(
    (col) => {
      if (col === "Actions") return null;
      const isOpen = activeHeaderMenu?.column === col;
      const selectedDirection = sortConfig?.column === col ? sortConfig?.direction : null;
      const isPinned = Boolean(pinnedColumns[col]);
      const isPinnedLeft = pinnedColumns[col] === "left";
      const isPinnedRight = pinnedColumns[col] === "right";
      const menu = isOpen ? (
        <div
          className="wl-th-menu-popover"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            position: "fixed",
            top: activeHeaderMenu?.top ?? 0,
            left: activeHeaderMenu?.left ?? 0,
            width: 184,
            background: "#ffffff",
            borderRadius: 12,
            border: "1px solid #dde3ea",
            boxShadow: "0 10px 24px rgba(18, 34, 51, 0.18)",
            padding: "7px",
            zIndex: 1200,
          }}
        >
          <button type="button" onClick={() => handlePortfolioHeaderSort(col, "asc")} style={{ width: "100%", border: "none", background: selectedDirection === "asc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowUp size={14} /><span>Sort Ascending</span></span>
          </button>
          <button type="button" onClick={() => handlePortfolioHeaderSort(col, "desc")} style={{ width: "100%", border: "none", background: selectedDirection === "desc" ? "#f4f8ff" : "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#44546a", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ArrowDown size={14} /><span>Sort Descending</span></span>
          </button>
          <button type="button" onClick={() => handlePinColumn(col, "left")} disabled={isPinnedLeft} style={{ width: "100%", border: "none", background: "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: isPinnedLeft ? "#b2bcc8" : "#6f7f92", fontWeight: 500, fontSize: 13, cursor: isPinnedLeft ? "not-allowed" : "pointer" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Pin size={14} /><span>Pin Left</span></span>
          </button>
          <button type="button" onClick={() => handlePinColumn(col, "right")} disabled={isPinnedRight} style={{ width: "100%", border: "none", background: "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: isPinnedRight ? "#b2bcc8" : "#6f7f92", fontWeight: 500, fontSize: 13, cursor: isPinnedRight ? "not-allowed" : "pointer" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Pin size={14} /><span>Pin Right</span></span>
          </button>
          <button type="button" onClick={() => handleUnpinColumn(col)} disabled={!isPinned} style={{ width: "100%", border: "none", background: "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: isPinned ? "#6f7f92" : "#d0d7e0", fontWeight: 500, fontSize: 13, cursor: isPinned ? "pointer" : "not-allowed" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><PinOff size={14} /><span>Unpin</span></span>
          </button>
          <button type="button" onClick={() => handleAutoSizeColumn(col)} style={{ width: "100%", border: "none", background: "transparent", textAlign: "left", padding: "7px 9px", borderRadius: 8, color: "#6f7f92", fontWeight: 500, fontSize: 13, cursor: "pointer" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Expand size={14} /><span>Auto-size Column</span></span>
          </button>
        </div>
      ) : null;

      return (
        <div style={{ position: "absolute", right: col === "Name" ? 4 : 6, top: "50%", transform: "translateY(-50%)" }}>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              const menuWidth = 184;
              const viewportPadding = 8;
              const computedLeft = Math.min(
                Math.max(viewportPadding, rect.right - menuWidth),
                window.innerWidth - menuWidth - viewportPadding,
              );
              setActiveHeaderMenu((prev) =>
                prev?.column === col
                  ? null
                  : { column: col, top: rect.bottom + 6, left: computedLeft },
              );
            }}
            style={{ border: "none", background: "transparent", width: 24, height: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#7f8a99", lineHeight: 1, cursor: "pointer", borderRadius: 6 }}
            aria-label={`Open ${col} menu`}
          >
            <EllipsisVertical size={14} strokeWidth={2.2} />
          </button>
          {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
        </div>
      );
    },
    [
      activeHeaderMenu,
      handleAutoSizeColumn,
      handlePinColumn,
      handlePortfolioHeaderSort,
      handleUnpinColumn,
      pinnedColumns,
      sortConfig,
    ],
  );

  //----------------------------// End Drag-N-Drop Functions //----------------------------//

  const holdingsTotalInvested = holdings.reduce((total, holding) => {
    return total + parseFloat(holding.AvgRate || 0) * parseFloat(holding.DPQty || 0);
  }, 0);
  const holdingsToday = holdings.reduce(
    (acc, holding) => {
      const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
      const livePrice = livePriceCache.current[scripCode];
      const pClose = livePCloseCache.current[scripCode];
      const currentPrice = parseFloat(livePrice || holding.CurrentPrice || 0);
      const previousClose = parseFloat(pClose || holding.CurrentPrice || 0);
      const qty = parseFloat(holding.DPQty || 0);
      const dailyPL = (currentPrice - previousClose) * qty;
      const previousDayValue = previousClose * qty;
      acc.totalPL += Number.isFinite(dailyPL) ? dailyPL : 0;
      acc.totalPreviousDayValue += Number.isFinite(previousDayValue)
        ? previousDayValue
        : 0;
      return acc;
    },
    { totalPL: 0, totalPreviousDayValue: 0 },
  );
  const holdingsCurrentValue = holdings.reduce((total, holding) => {
    const qty = parseFloat(holding.DPQty || 0);
    const scripCode = holding.Exch === "N" ? holding.NseCode : holding.BseCode;
    const livePrice = livePriceCache.current[scripCode];
    const currentPrice = parseFloat(livePrice || holding.CurrentPrice || 0);
    return total + (Number.isFinite(currentPrice) ? qty * currentPrice : 0);
  }, 0);
  const holdingsOverallPL = holdingsCurrentValue - holdingsTotalInvested;
  const holdingsOverallPercent =
    holdingsTotalInvested !== 0 ? (holdingsOverallPL / holdingsTotalInvested) * 100 : 0;
  const normalizeMarginNumber = (value) => {
    if (value === null || value === undefined || value === "") return Number.NaN;
    if (typeof value === "string") {
      const parsed = Number(value.replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : Number.NaN;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  };
  const sharedMarginValue = Number.isFinite(normalizeMarginNumber(sharedData?.margin))
    ? normalizeMarginNumber(sharedData?.margin)
    : normalizeMarginNumber(sharedData?.broker?.margin);

  const cardsTotalInvested =
    portfolioStats?.total_investment_amount === null ||
      portfolioStats?.total_investment_amount === undefined
      ? holdingsTotalInvested
      : Number(portfolioStats?.total_investment_amount) || 0;
  const cardsTodayPL =
    portfolioStats?.today_profit_loss === null ||
      portfolioStats?.today_profit_loss === undefined
      ? holdingsToday.totalPL
      : Number(portfolioStats?.today_profit_loss) || 0;
  const cardsTodayPercent =
    portfolioStats?.today_profit_loss_percentage === null ||
      portfolioStats?.today_profit_loss_percentage === undefined
      ? holdingsToday.totalPreviousDayValue !== 0
        ? (holdingsToday.totalPL / holdingsToday.totalPreviousDayValue) * 100
        : 0
      : Number(portfolioStats?.today_profit_loss_percentage) || 0;
  const cardsOverallPL =
    portfolioStats?.overall_profit_loss === null ||
      portfolioStats?.overall_profit_loss === undefined
      ? holdingsOverallPL
      : Number(portfolioStats?.overall_profit_loss) || 0;
  const cardsOverallPercent =
    portfolioStats?.total_investment_amount === null ||
      portfolioStats?.total_investment_amount === undefined
      ? holdingsOverallPercent
      : cardsTotalInvested !== 0
        ? (cardsOverallPL / cardsTotalInvested) * 100
        : 0;
  const cardsMargin =
    portfolioStats?.margin === null || portfolioStats?.margin === undefined
      ? (Number.isFinite(sharedMarginValue) ? sharedMarginValue : 0)
      : Number(portfolioStats?.margin) || 0;

  //----------------------------// Return Main //----------------------------//
  return (
    <>
      {/* <Header /> */}
      <Header tabName={tabName} />
      <div className="wrapper__page pb-5">
        <Sidebar />

        <div className="content_col">
          <h1 className="app-page-title">{tr("portfolio")}</h1>

          <div
            className="chart-cards-row"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "20px",
              marginTop: "24px",
              marginBottom: "30px",
            }}
          >
            <ChartCard
              title={tr("totalInvestedAmount")}
              iconSrc={iconWalet}
              value={
                isLoading || !portfolioStatsReady ? (
                  <Placeholder.Graph active height={24} width={100} />
                ) : (
                  `₹${cardsTotalInvested.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                )
              }
              chartColor="#3B82F6"
              isHovered={isHovered1}
              onMouseEnter={() => setIsHovered1(true)}
              onMouseLeave={() => setIsHovered1(false)}
              showExpandButton={false}
            />

            <ChartCard
              title={tr("margin")}
              iconSrc={iconMargin}
              value={
                isLoading || !portfolioStatsReady ? (
                  <Placeholder.Graph active height={24} width={100} />
                ) : (
                  <span style={{ color: cardsMargin < 0 ? "#F44336" : "#111827" }}>
                    ₹{formatNumber(cardsMargin)}
                  </span>
                )
              }
              chartColor="#8B5CF6"
              isHovered={isHovered2}
              onMouseEnter={() => setIsHovered2(true)}
              onMouseLeave={() => setIsHovered2(false)}
              showExpandButton={false}
            />

            <ChartCard
              title={tr("unrealizedPLPercent")}
              iconSrc={iconLineChart}
              value={
                isLoading || !portfolioStatsReady ? (
                  <Placeholder.Graph active height={24} width={100} />
                ) : (
                  <span style={{ color: cardsOverallPL < 0 ? "#F44336" : "#1EA355" }}>
                    ₹{cardsOverallPL < 0 ? "-" : ""}
                    {Math.abs(cardsOverallPL).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    <span style={{ fontSize: "14px" }}>
                      ({cardsOverallPL >= 0 ? "+" : ""}
                      {cardsOverallPercent.toFixed(2)}%)
                    </span>
                  </span>
                )
              }
              chartColor="#EF4444"
              isHovered={isHovered3}
              onMouseEnter={() => setIsHovered3(true)}
              onMouseLeave={() => setIsHovered3(false)}
              showExpandButton={false}
            />

            <ChartCard
              title={tr("todaysPLPercent")}
              iconSrc={iconPie}
              value={
                isLoading || !portfolioStatsReady ? (
                  <Placeholder.Graph active height={24} width={100} />
                ) : (
                  <span style={{ color: cardsTodayPL < 0 ? "#F44336" : "#1EA355" }}>
                    ₹{cardsTodayPL < 0 ? "-" : ""}
                    {Math.abs(cardsTodayPL).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    <span style={{ fontSize: "14px" }}>
                      ({cardsTodayPL >= 0 ? "+" : ""}
                      {cardsTodayPercent.toFixed(2)}%)
                    </span>
                  </span>
                )
              }
              chartColor="#10B981"
              isHovered={isHovered4}
              onMouseEnter={() => setIsHovered4(true)}
              onMouseLeave={() => setIsHovered4(false)}
              showExpandButton={false}
            />
          </div>

          {/* Table */}
          <div className="table_main">
            <div className="table_title_with_view_all_row">
              <h5>{tr("portfolio")}
                {/* {tr("valueOverTime")} */}
              </h5>
              <button
                ref={additionalColumnsButtonRef}
                type="button"
                className="watchlist-columns-btn"
                onClick={(e) => openAdditionalColumnsDropdown(e)}
                aria-label="Additional columns"
              >
                <Columns3 size={16} strokeWidth={2} />
                <span>{tr("additionalColumns")}</span>
              </button>
            </div>

            <div className="table-responsive db-portfolio-targets-wrap">
              {isLoading && (
                <Placeholder.Grid
                  rows={20}
                  columns={9}
                  active
                  style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 12 }}
                />
              )}

              {!isLoading && (
                <>
                  <style>{`
      /* Portfolio Targets sticky thead with horizontal scroll support */
      .db-portfolio-targets-wrap{
        overflow-x: auto !important;
        overflow-y: auto !important;
        max-height: calc(100vh - 280px);
        position: relative;
        width: 100%;
        min-width: 0;
      }
      .db-portfolio-targets-table{
        border-collapse: separate;
        border-spacing: 0;
        width: max-content;
        min-width: 100%;
        max-width: none;
      }
      .db-portfolio-targets-table thead{
        position: sticky;
        top: 0;
        z-index: 50;
      }
      .db-portfolio-targets-table thead tr{
        position: relative;
        background: #f8fbff !important;
      }
      .db-portfolio-targets-table thead th{
        position: -webkit-sticky !important;
        position: sticky !important;
        top: 0 !important;
        z-index: 55 !important;
        background: #f8fbff !important;
      }
      /* Actions column sticky on horizontal scroll */
      .db-portfolio-targets-table thead th[data-column-id="Actions"]{
        z-index: 60 !important;
        background: #f8fbff !important;
      }
    .db-portfolio-targets-table tbody td.action-cell{
      position: sticky !important;
      right: 0 !important;
      z-index: 35 !important;
      background: #fff !important;
    }
      /* Match Watchlist premium action button look */
      .wl-premium-action-btn{
        transition: all 0.2s ease;
      }
      .wl-premium-action-btn:hover{
        transform: translateY(-1px);
      }
      .wl-premium-buy:hover{
        background: #C8F8E8 !important;
        border-color: transparent !important;
        color: #1DAA7F !important;
        box-shadow: 0 4px 10px rgba(35,183,137,0.16) !important;
      }
      .wl-premium-sell:hover{
        background: #F8D3D3 !important;
        border-color: transparent !important;
        color: #BD4545 !important;
        box-shadow: 0 4px 10px rgba(201,74,74,0.14) !important;
      }
      .wl-premium-depth:hover{
        background: #F3F7FF !important;
        box-shadow: 0 5px 14px rgba(20,38,71,0.20) !important;
      }
      .wl-premium-action-btn:disabled{
        transform: none !important;
        box-shadow: none !important;
      }
    `}</style>
                  <table className="table db-portfolio-targets-table">
                    <thead>
                      <tr>
                        {columnOrder.map((col) => (
                          <th
                            key={col}
                            ref={(el) => applyPinnedHeaderPriority(el, col)}
                            className={`${pinnedColumns[col] === "left"
                              ? "wl-pinned-left"
                              : pinnedColumns[col] === "right"
                                ? "wl-pinned-right"
                                : ""
                              } ${col === "Actions" ? "wl-pinned-actions" : ""}`.trim()}
                            draggable="true"
                            onDragStart={(e) => handleDragStart(e, col)}
                            onDragOver={(e) => handleDragOver(e)}
                            onDrop={(e) => handleDrop(e, col)}
                            onDragEnd={handleDragEnd}
                            data-column-id={col}
                            style={{
                              boxSizing: "border-box",
                              ...(pinnedColumns[col] || col === "Actions" || col === "Name"
                                ? {
                                  width: `${getColumnDisplayWidth(col)}px`,
                                  minWidth: `${getColumnDisplayWidth(col)}px`,
                                  maxWidth: `${getColumnDisplayWidth(col)}px`,
                                }
                                : {}),
                              transition: "all 0.3s ease",
                              transform:
                                draggedColumn === col
                                  ? "scale(1.03)"
                                  : "scale(1)",
                              boxShadow:
                                draggedColumn === col
                                  ? "0 0 10px #C9E0FF"
                                  : "none",
                              cursor: "grab",
                              textAlign:
                                col === "Name"
                                  ? "left"
                                  : ["Profit %", "Volume", "Exchange"].includes(
                                    col,
                                  )
                                    ? "center"
                                    : "center",
                              minWidth: col === "Name" ? "220px" : undefined,
                              width:
                                col === "Name"
                                  ? "220px"
                                  : columnWidths[col]
                                    ? `${columnWidths[col]}px`
                                    : undefined,
                              maxWidth:
                                col === "Name"
                                  ? "220px"
                                  : columnWidths[col]
                                    ? `${columnWidths[col]}px`
                                    : undefined,
                              overflow: col === "Name" ? "hidden" : undefined,
                              whiteSpace: col === "Name" ? "nowrap" : undefined,
                              paddingLeft: col === "Actions" ? 0 : undefined,
                              paddingRight: col === "Actions" ? 0 : undefined,
                              display: [
                                "TodaysLow",
                                "Profit %",
                                "52WeekLow",
                                "Target Price",
                                "Quantity",
                              ].includes(col)
                                ? "none"
                                : undefined,
                              // Always keep thead sticky (including non-Actions columns)
                              position: "sticky",
                              top: 0,
                              right:
                                col === "Actions"
                                  ? 0
                                  : pinnedColumns[col] === "right"
                                    ? `${pinnedOffsets.right[col] || 0}px`
                                    : undefined,
                              left:
                                pinnedColumns[col] === "left"
                                  ? `${pinnedOffsets.left[col] || 0}px`
                                  : undefined,
                              zIndex:
                                pinnedColumns[col]
                                  ? 1200
                                  : col === "Actions"
                                    ? 1100
                                    : 40,
                              background:
                                "#f8fbff",
                              paddingRight: col === "Actions" ? 0 : 28,
                              overflow:
                                col === "Name" || pinnedColumns[col]
                                  ? "hidden"
                                  : undefined,
                            }}
                          >
                            {col === "Name" ? (
                              <div
                                className="table_head_search"
                                style={{
                                  width: "200px",
                                  minWidth: "200px",
                                  maxWidth: "200px",
                                }}
                              >
                                {/* <p style={{padding:"10px"}}>Name</p> */}
                                <div style={{ padding: "2px", width: 200 }}>
                                  <button
                                    style={{
                                      padding: "2px 0 2px 5px",
                                      borderRight: "1px solid #e4e4e4ff",
                                    }}
                                    onClick={() =>
                                      searchInputRef.current?.focus()
                                    } // or searchInputRef4.current?.focus()
                                  >
                                    <i className="icon-search" />
                                  </button>
                                  {/* <input type="text" placeholder="Name" style={{ width: 90 }} /> */}
                                  {/* Name */}
                                  <input
                                    type="text"
                                    placeholder={tr("nameSearchHere")}
                                    style={{
                                      paddingLeft: "10px",
                                      marginLeft: "5px",
                                      maxWidth: "150px",
                                      width: "100%",
                                      boxSizing: "border-box",
                                    }}
                                    value={searchQuery}
                                    onChange={(e) =>
                                      setSearchQuery(e.target.value)
                                    }
                                    ref={searchInputRef} // or ref={searchInputRef4}
                                  />
                                </div>
                                {renderPortfolioHeaderMenu(col)}
                              </div>
                            ) : col === "Actions" ? (
                              <></>
                            ) : //  : col === "TodaysHigh" ? (
                              //     <Whisper
                              //       placement="top"
                              //       trigger="hover"
                              //       speaker={
                              //         <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                              //           Today's High:<br />
                              //           - The highest price at which the stock has traded during the current trading session.
                              //           <br />
                              //           - Note: Highlighted in orange when the live price is near this level
                              //         </Tooltip>
                              //       }
                              //     >
                              //       <>
                              //         Today's High
                              //       </>
                              //     </Whisper>
                              //   )

                              //    : col === "TodaysLow" ? (
                              //     <Whisper
                              //       placement="top"
                              //       trigger="hover"
                              //       speaker={
                              //         <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                              //           Today's Low:<br />
                              //           - The lowest price at which the stock has traded during the current trading session.
                              //           <br />
                              //           - Note: Highlighted in red when the live price is near this level
                              //         </Tooltip>
                              //       }
                              //     >
                              //       <>
                              //         Today's Low
                              //       </>
                              //     </Whisper>
                              //   )

                              col === "TodaysHigh" ? (
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={
                                    <Tooltip
                                      className="common__tooltip"
                                      style={{ maxWidth: "400px" }}
                                    >
                                      {tr("todaysHighLowTitle")}:
                                      <br />
                                      - {tr("todaysHighLowTopDetail")}
                                      <br />- {tr("todaysHighLowBottomDetail")}
                                    </Tooltip>
                                  }
                                >
                                  <>{tr("todaysHighLow")}</>
                                </Whisper>
                              ) : col === "TodaysLow" ? (
                                <></>
                              ) : col === "Current Price" ? (
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={
                                    <Tooltip
                                      className="common__tooltip"
                                      style={{ maxWidth: "400px" }}
                                    >
                                      {tr("currentPrice")}:
                                      <br />- {tr("currentPriceTooltip")}
                                    </Tooltip>
                                  }
                                >
                                  <>{tr("currentPrice")}</>
                                </Whisper>
                              ) : col === "Trade Price" ? (
                                <Whisper
                                  placement="top"
                                  trigger="hover"
                                  speaker={
                                    <Tooltip
                                      className="common__tooltip"
                                      style={{ maxWidth: "400px" }}
                                    >
                                      {tr("tradePriceQuantityTitle")}:
                                      <br />
                                      - {tr("tradePriceQuantityTop")}
                                      <br />- {tr("tradePriceQuantityBottom")}
                                    </Tooltip>
                                  }
                                >
                                  <>{tr("tradePriceQuantityShort")}</>
                                </Whisper>
                              ) : col === "Quantity" ? (
                                <></>
                              ) : // : col === "Profit/Loss" ? (
                                //   <Whisper
                                //     placement="top"
                                //     trigger="hover"
                                //     speaker={
                                //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                //         Profit/Loss:<br />
                                //         - Shows your unrealized gain or loss calculated as (Current Price - Trade Price) × Quantity.
                                //       </Tooltip>
                                //     }
                                //   >
                                //     <>
                                //       Profit/Loss
                                //     </>
                                //   </Whisper>
                                // )

                                // : col === "Profit %" ? (
                                //   <Whisper
                                //     placement="top"
                                //     trigger="hover"
                                //     speaker={
                                //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                //         Profit %:<br />
                                //         - Shows your unrealized return on investment calculated as ((Current Price - Trade Price) / Trade Price) × 100.
                                //       </Tooltip>
                                //     }
                                //   >
                                //     <>
                                //       Profit %
                                //     </>
                                //   </Whisper>
                                // )

                                col === "Profit/Loss" ? (
                                  <Whisper
                                    placement="top"
                                    trigger="hover"
                                    speaker={
                                      <Tooltip
                                        className="common__tooltip"
                                        style={{ maxWidth: "400px" }}
                                      >
                                        {tr("profitLossPercentTitle")}:
                                        <br />
                                        - {tr("profitLossPercentTop")}
                                        <br />- {tr("profitLossPercentBottom")}
                                      </Tooltip>
                                    }
                                  >
                                    <>{tr("profitLossPercent")}</>
                                  </Whisper>
                                ) : col === "Profit %" ? (
                                  <></>
                                ) : // : col === "52WeekHigh" ? (
                                  //   <Whisper
                                  //     placement="top"
                                  //     trigger="hover"
                                  //     speaker={
                                  //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                  //         52 Week High:<br />
                                  //         - The highest price at which the stock has traded during the past 52 weeks (one year).
                                  //       </Tooltip>
                                  //     }
                                  //   >
                                  //     <>
                                  //       52 Week High
                                  //     </>
                                  //   </Whisper>
                                  // )

                                  // : col === "52WeekLow" ? (
                                  //   <Whisper
                                  //     placement="top"
                                  //     trigger="hover"
                                  //     speaker={
                                  //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                  //         52 Week Low:<br />
                                  //         - The lowest price at which the stock has traded during the past 52 weeks (one year).
                                  //       </Tooltip>
                                  //     }
                                  //   >
                                  //     <>
                                  //       52 Week Low
                                  //     </>
                                  //   </Whisper>
                                  // )

                                  col === "52WeekHigh" ? (
                                    <Whisper
                                      placement="top"
                                      trigger="hover"
                                      speaker={
                                        <Tooltip
                                          className="common__tooltip"
                                          style={{ maxWidth: "400px" }}
                                        >
                                          {tr("fiftyTwoWeekHighLowTitle")}:
                                          <br />
                                          - {tr("fiftyTwoWeekHighLowTopDetail")}
                                          <br />- {tr("fiftyTwoWeekHighLowBottomDetail")}
                                        </Tooltip>
                                      }
                                    >
                                      <>{tr("fiftyTwoWeekHighLow")}</>
                                    </Whisper>
                                  ) : col === "52WeekLow" ? (
                                    // <></>
                                    <div style={{ display: "none" }}></div>
                                  ) : // : col === "Initial Price" ? (
                                    //   <Whisper
                                    //     placement="top"
                                    //     trigger="hover"
                                    //     speaker={
                                    //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                    //         Initial Price:<br />
                                    //         - The stock's price at the time you set your target, used as a reference point to track price movement.
                                    //       </Tooltip>
                                    //     }
                                    //   >
                                    //     <>
                                    //       Initial Price
                                    //     </>
                                    //   </Whisper>
                                    // )

                                    //  : col === "Target Price" ? (
                                    //   <Whisper
                                    //     placement="top"
                                    //     trigger="hover"
                                    //     speaker={
                                    //       <Tooltip className="common__tooltip" style={{ maxWidth: '400px' }}>
                                    //         Target Price:<br />
                                    //         - Your manually set price goal at which you plan to exit the position and book profits.
                                    //       </Tooltip>
                                    //     }
                                    //   >
                                    //     <>
                                    //       Target Price
                                    //     </>
                                    //   </Whisper>
                                    // )

                                    col === "Initial Price" ? (
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip
                                            className="common__tooltip"
                                            style={{ maxWidth: "400px" }}
                                          >
                                            {tr("initialTargetPriceTitle")}:
                                            <br />
                                            - {tr("initialTargetPriceTop")}
                                            <br />- {tr("initialTargetPriceBottom")}
                                          </Tooltip>
                                        }
                                      >
                                        <>{tr("initialTargetPriceTitle")}</>
                                      </Whisper>
                                    ) : col === "Target Price" ? (
                                      <></>
                                    ) : col === "Target %" ? (
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip
                                            className="common__tooltip"
                                            style={{ maxWidth: "400px" }}
                                          >
                                            {tr("targetPercent")}:
                                            <br />- {tr("targetPercentTooltip")}
                                          </Tooltip>
                                        }
                                      >
                                        <>{tr("targetPercent")}</>
                                      </Whisper>
                                    ) : defaultColumns.includes(col) ? (
                                      translateColumnLabel(col, currentLanguage)
                                    ) : (
                                      // Additional column, find its UIName
                                      additionalColumnConfig.find(
                                        (config) => config.colName === col,
                                      )?.UIName || col
                                    )}
                            {col !== "Name" && renderPortfolioHeaderMenu(col)}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                    {holdings.length === 0 ? (
  <tr>
    <td
      colSpan={columnOrder.length}
      className="table-empty-state-cell"
    >
      <span className="table-empty-order-text">
        {tr("noHoldingsDataAvailable")}
      </span>
    </td>
  </tr>
) : currentItems.length === 0 ? (
  <tr>
    <td
      colSpan={columnOrder.length}
      className="table-empty-state-cell"
    >
      {tr("noMatchFound")}
    </td>
  </tr>
) : (
                        currentItems.map((holding, index) => (
                          <tr 
                          onClick={() => {
                                if (supportsCandleChart(broker_type)) {
                                  handleRowClick(holding);
                                }
                              }}
                              style={{
                                cursor: supportsCandleChart(broker_type)
                                  ? "pointer"
                                  : "default",
                              }}
                          key={holding.Symbol || index}>
                            {columnOrder.map((col) => {
                              let content;

                              if (col === "Name") {
                                const symbolName = String(
                                  holding.Symbol ||
                                    holding.symbol ||
                                    holding.TradingSymbol ||
                                    holding.name ||
                                    "",
                                ).trim();
                                const exchRaw = String(
                                  holding?.Exch ||
                                    holding?.exch ||
                                    holding?.exchange ||
                                    "",
                                ).toUpperCase();
                                const exchTypeRaw = String(
                                  holding?.ExchType ||
                                    holding?.exchType ||
                                    "C",
                                ).toUpperCase();
                                const exchShort = exchRaw.startsWith("N")
                                  ? "NSE"
                                  : exchRaw.startsWith("B")
                                    ? "BSE"
                                    : exchRaw.startsWith("M")
                                      ? "MCX"
                                      : "BSE";
                                const exchSegment = exchTypeRaw.startsWith("D")
                                  ? "Derivative"
                                  : "Cash";
                                const exchFullLabel = `${exchShort} ${exchSegment}`;

                                content = (
                                  <div
                                    className="main_table_company_cell"
                                    style={{ minWidth: 0 }}
                                  >
                                    <div
                                      style={{ paddingLeft: "10px", minWidth: 0 }}
                                    >
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip className="common__tooltip">
                                            {holding.name || holding.FullName}
                                          </Tooltip>
                                        }
                                      >
                                        <h5
                                          className="uppercase"
                                          style={{
                                            maxWidth: 145,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            margin: 0,
                                          }}
                                        >
                                          {holding.name || holding.FullName}
                                        </h5>
                                      </Whisper>
                                      <ul
                                        className="company_table_tags"
                                        style={{
                                          paddingLeft: 0,
                                          marginTop: 4,
                                          flexWrap: "nowrap",
                                          alignItems: "center",
                                          marginBottom: 0,
                                          gap: "4px",
                                          minWidth: 0,
                                        }}
                                      >
                                        <li
                                          className="csymbol"
                                          style={{
                                            minWidth: 0,
                                            maxWidth: 85,
                                            overflow: "hidden",
                                          }}
                                        >
                                          <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip
                                                className="common__tooltip"
                                                style={{ maxWidth: "320px" }}
                                              >
                                                {symbolName || "-"}
                                              </Tooltip>
                                            }
                                          >
                                            <span
                                              style={{
                                                display: "inline-block",
                                                maxWidth: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                verticalAlign: "bottom",
                                                cursor: "default",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {symbolName || "-"}
                                            </span>
                                          </Whisper>
                                        </li>
                                        <li
                                          className="cbse_nse"
                                          style={{
                                            minWidth: 0,
                                            flexShrink: 0,
                                          }}
                                        >
                                          <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip className="common__tooltip">
                                                {exchFullLabel}
                                              </Tooltip>
                                            }
                                          >
                                            <span
                                              style={{
                                                cursor: "default",
                                                whiteSpace: "nowrap",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {exchShort}
                                            </span>
                                          </Whisper>
                                        </li>
                                        <Whisper
                                          placement="top"
                                          trigger="hover"
                                          speaker={
                                            <Tooltip className="common__tooltip">
                                              {tr("setTarget")}
                                            </Tooltip>
                                          }
                                        >
                                          <li
                                            className="cset__target"
                                            role="button"
                                            tabIndex={0}
                                            aria-label={tr("setTarget")}
                                            style={{
                                              zIndex: 30,
                                              minWidth: "unset",
                                              padding: "2px 6px",
                                              cursor: "pointer",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              lineHeight: 1,
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openSetTargetModal(holding);
                                            }}
                                            onKeyDown={(e) => {
                                              if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                              ) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openSetTargetModal(holding);
                                              }
                                            }}
                                          >
                                            <TbTarget size={16} />
                                          </li>
                                        </Whisper>
                                      </ul>
                                    </div>
                                  </div>
                                );
                              }

                              else if (col === "Profit/Loss") {
                                const scripCode =
                                  holding.ScripCode ||
                                  (holding.Exch === "N"
                                    ? holding.NseCode
                                    : holding.BseCode);
                                const livePrice =
                                  livePriceCache.current[scripCode];
                                const currentPrice =
                                  livePrice || holding.CurrentPrice;

                                // Profit/Loss calculation
                                const change =
                                  parseFloat(currentPrice) * holding.DPQty -
                                  parseFloat(holding.AvgRate) * holding.DPQty;
                                const isPositive = change >= 0;

                                // Profit % calculation
                                const percentChange =
                                  parseFloat(currentPrice) -
                                  parseFloat(holding.AvgRate);
                                const changePercent = (
                                  (percentChange /
                                    parseFloat(holding.AvgRate)) *
                                  100
                                ).toFixed(2);
                                const isPercentPositive = percentChange >= 0;

                                content = (
                                  <div
                                    className="trade__price__cell"
                                    style={{
                                      textAlign: "center",
                                      minWidth: "140px",
                                    }}
                                  >
                                    {/* Profit/Loss Amount (Top) */}
                                    <div
                                      className={
                                        isPositive
                                          ? "text_success_color"
                                          : "text_danger_color"
                                      }
                                    >
                                      {isPositive ? "+" : ""}₹
                                      {Math.abs(change).toFixed(2)}
                                      <i
                                        className={`icon-arrow-${isPositive ? "up" : "down"}`}
                                        style={{ paddingLeft: "4px" }}
                                      ></i>
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* Profit % (Bottom) */}
                                    <div
                                      className={
                                        isPercentPositive
                                          ? "text_success_color"
                                          : "text_danger_color"
                                      }
                                      style={{}}
                                    >
                                      ({isPercentPositive ? "+" : ""}
                                      {changePercent}%)
                                      <i
                                        className={`icon-arrow-${isPercentPositive ? "up" : "down"}`}
                                        style={{ marginLeft: "4px" }}
                                      ></i>
                                    </div>
                                  </div>
                                );
                              } else if (col === "Profit %") {
                                content = <></>;
                              }


                              else if (col === "TodaysHigh") {
                                const scripCode =
                                  holding.Exch === "N"
                                    ? holding.NseCode
                                    : holding.BseCode;
                                const stocksData = holding?.stocks_data || {};
                                const ohlc = stocksData?.ohlc || {};

                                const liveDayHigh = Number(
                                  liveHighCache.current?.[scripCode],
                                );
                                const mappedDayHigh = Number(
                                  holding.TodaysHigh ??
                                  holding.DayHigh ??
                                  stocksData?.high ??
                                  stocksData?.High ??
                                  ohlc?.high,
                                );
                                const currentDayHigh = Number.isFinite(liveDayHigh)
                                  ? liveDayHigh
                                  : Number.isFinite(mappedDayHigh)
                                    ? mappedDayHigh
                                    : 0;

                                const liveDayLow = Number(
                                  liveLowCache.current?.[scripCode],
                                );
                                const mappedDayLow = Number(
                                  holding.TodaysLow ??
                                  holding.DayLow ??
                                  stocksData?.low ??
                                  stocksData?.Low ??
                                  ohlc?.low,
                                );
                                const currentDayLow = Number.isFinite(liveDayLow)
                                  ? liveDayLow
                                  : Number.isFinite(mappedDayLow)
                                    ? mappedDayLow
                                    : 0;

                                const livePrice = Number(
                                  livePriceCache.current?.[scripCode] ??
                                  holding.CurrentPrice ??
                                  stocksData?.close ??
                                  stocksData?.last_price ??
                                  ohlc?.close ??
                                  stocksData?.open ??
                                  ohlc?.open,
                                );

                                const isNearHigh =
                                  currentDayHigh > 0 &&
                                  Math.abs(
                                    ((livePrice - currentDayHigh) /
                                      currentDayHigh) *
                                    100,
                                  ) < 0.5;
                                const isNearLow =
                                  currentDayLow > 0 &&
                                  Math.abs(
                                    ((livePrice - currentDayLow) /
                                      currentDayLow) *
                                    100,
                                  ) < 0.5;

                                content = (
                                  <div
                                    className="trade__price__cell"
                                    style={{
                                      textAlign: "center",
                                      width: "100px",
                                      margin: "auto",
                                    }}
                                  >
                                    {/* Today's High (Top) */}
                                    <div
                                      className={
                                        isNearHigh ? "text_warning_color" : ""
                                      }
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#16A34A",
                                          fontWeight: "600",
                                        }}
                                      >
                                        H
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        ₹{formatNumber(currentDayHigh)}
                                        {isNearHigh && (
                                          <i
                                            className="icon-arrow-up"
                                            style={{
                                              color: "#FFA726",
                                              marginLeft: "4px",
                                            }}
                                          ></i>
                                        )}
                                      </div>
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* Today's Low (Bottom) */}
                                    <div
                                      className={
                                        isNearLow ? "text_danger_color" : ""
                                      }
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#DC2626",
                                          fontWeight: "600",
                                        }}
                                      >
                                        L
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        ₹{formatNumber(currentDayLow)}
                                        {isNearLow && (
                                          <i
                                            className="icon-arrow-down"
                                            style={{
                                              color: "#EF5350",
                                              marginLeft: "4px",
                                            }}
                                          ></i>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              // TodaysLow - Merged
                              else if (col === "TodaysLow") {
                                content = <></>;





                              } else if (col === "Volume") {
                                const scripCode = holding.scripcode;
                                const liveVolume =
                                  liveVolumeCache.current[scripCode];
                                const volumeValue =
                                  liveVolume || holding.volume || 0;
                                content = (
                                  <div style={{ textAlign: "center" }}>
                                    {formatNumber(volumeValue)}
                                  </div>
                                );
                              } else if (col === "Trade Price") {
                                content = (
                                  <div
                                    className="trade__price__cell"
                                    style={{
                                      textAlign: "center",
                                      width: "100px",
                                      margin: "auto",
                                    }}
                                  >
                                    {/* Trade Price (Top) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#2457BF",
                                          fontWeight: "600",
                                        }}
                                      >
                                        P
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        ₹{formatNumber(holding.AvgRate)}
                                      </div>
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* Quantity (Bottom) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#64748B",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Q
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        {holding.DPQty}
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              else if (col === "Current Price") {
                                const scripCode =
                                  holding.Exch === "N"
                                    ? holding.NseCode
                                    : holding.BseCode;
                                const livePrice =
                                  livePriceCache.current[scripCode];
                                const pClose =
                                  livePCloseCache.current[scripCode];
                                const stocksData = holding?.stocks_data || {};
                                const ohlc = stocksData?.ohlc || {};
                                const fallbackCurrentPrice = parseFloat(
                                  holding.CurrentPrice ??
                                  holding.initialPrice ??
                                  stocksData?.last_price ??
                                  stocksData?.ltp,
                                );
                                const currentPrice = Number.isFinite(
                                  parseFloat(livePrice),
                                )
                                  ? parseFloat(livePrice)
                                  : fallbackCurrentPrice;
                                const fallbackPreviousClose = parseFloat(
                                  holding.PreviousClose ??
                                  holding.PClose ??
                                  stocksData?.previous_close ??
                                  ohlc?.close ??
                                  holding.CurrentPrice ??
                                  holding.initialPrice ??
                                  currentPrice,
                                );
                                const previousClose = Number.isFinite(parseFloat(pClose))
                                  ? parseFloat(pClose)
                                  : fallbackPreviousClose;
                                const isValidPrice =
                                  Number.isFinite(currentPrice) &&
                                  Number.isFinite(previousClose);
                                const isPositive = isValidPrice
                                  ? currentPrice >= previousClose
                                  : true;

                                // Net Change / Net Change % (Dashboard fallback chain)
                                const apiDayChange = Number(
                                  holding?.stocks_data?.day_change,
                                );
                                const apiDayChangePerc = Number(
                                  holding?.stocks_data?.day_change_perc,
                                );
                                const hasApiChangeData =
                                  Number.isFinite(apiDayChange) &&
                                  Number.isFinite(apiDayChangePerc);
                                const fallbackPriceDifference = isValidPrice
                                  ? currentPrice - previousClose
                                  : Number.NaN;
                                const fallbackChangePercent =
                                  Number.isFinite(fallbackPriceDifference) &&
                                    Number.isFinite(previousClose) &&
                                    previousClose !== 0
                                    ? (fallbackPriceDifference / previousClose) * 100
                                    : Number.NaN;
                                const priceDifference = Number.isFinite(
                                  fallbackPriceDifference,
                                )
                                  ? fallbackPriceDifference
                                  : hasApiChangeData
                                    ? apiDayChange
                                    : Number.NaN;
                                const changePercent = Number.isFinite(
                                  fallbackChangePercent,
                                )
                                  ? fallbackChangePercent
                                  : hasApiChangeData
                                    ? apiDayChangePerc
                                    : Number.NaN;
                                const hasChangeData =
                                  Number.isFinite(priceDifference) &&
                                  Number.isFinite(changePercent);
                                const isChangePositive = hasChangeData
                                  ? priceDifference >= 0
                                  : isPositive;

                                content = (
                                  <div
                                    className="trade__price__cell"
                                    style={{
                                      textAlign: "center",
                                      minWidth: "140px",
                                    }}
                                  >
                                    {/* Current Price (Top) */}
                                    <div
                                      className={
                                        isValidPrice && isPositive
                                          ? "text_success_color"
                                          : isValidPrice && !isPositive
                                            ? "text_danger_color"
                                            : ""
                                      }
                                    >
                                      {isValidPrice
                                        ? `₹${formatNumber(currentPrice)}`
                                        : "-"}
                                      {isValidPrice && (
                                        <i
                                          className={`icon-arrow-${isPositive ? "up" : "down"}`}
                                          style={{ paddingLeft: "4px" }}
                                        ></i>
                                      )}
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* Net Change (Bottom) */}
                                    <div
                                      className={
                                        hasChangeData
                                          ? isChangePositive
                                            ? "text_success_color"
                                            : "text_danger_color"
                                          : "text_muted"
                                      }
                                      style={{}}
                                    >
                                      {hasChangeData ? (
                                        <>
                                          {isChangePositive ? "+" : "-"}₹
                                          {Math.abs(priceDifference).toFixed(2)}
                                          <span style={{ paddingLeft: "4px" }}>
                                            ({isChangePositive ? "+" : "-"}
                                            {Math.abs(changePercent).toFixed(2)}%)
                                          </span>
                                          <i
                                            className={`icon-arrow-${isChangePositive ? "up" : "down"}`}
                                            style={{ marginLeft: "4px" }}
                                          ></i>
                                        </>
                                      ) : (
                                        <span style={{}}>0.00 (0.00%)</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              } else if (col === "Quantity") {
                                content = <></>;
                              }

                              else if (col === "52WeekHigh") {
                                const high52Week = holding["52WeekHigh"];
                                const low52Week = holding["52WeekLow"];

                                content = (
                                  <div
                                    className="trade__price__cell"
                                    style={{
                                      textAlign: "center",
                                      width: "100px",
                                      margin: "auto",
                                    }}
                                  >
                                    {/* 52 Week High (Top) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#16A34A",
                                          fontWeight: "600",
                                        }}
                                      >
                                        H
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        {high52Week !== null ? (
                                          `₹${formatNumber(high52Week)}`
                                        ) : (
                                          <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
                                            N/A
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* 52 Week Low (Bottom) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#DC2626",
                                          fontWeight: "600",
                                        }}
                                      >
                                        L
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        {low52Week !== null ? (
                                          `₹${formatNumber(low52Week)}`
                                        ) : (
                                          <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
                                            N/A
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else if (col === "52WeekLow") {
                                // content = <></>;
                                content = (
                                  <div style={{ display: "none" }}></div>
                                );
                              }


                              else if (col === "Initial Price") {
                                const initialPrice = holding["initialPrice"];
                                const targetPrice = holding["targetPrice"];
                                const hasTargetData = !!holding?.hasTargetData;

                                content = (
                                  <div
                                    className="trade__price__cell"
                                    style={{
                                      textAlign: "center",
                                      width: "100px",
                                      margin: "auto",
                                    }}
                                  >
                                    {/* Initial Price (Top) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#2457BF",
                                          fontWeight: "600",
                                        }}
                                      >
                                        I
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        {hasTargetData
                                          ? (formatNumber(initialPrice) || (
                                            <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
                                              N/A
                                            </span>
                                          ))
                                          : (
                                            <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
                                              N/A
                                            </span>
                                          )}
                                      </div>
                                    </div>

                                    {/* Separator Line */}
                                    <div
                                      style={{
                                        width: "30px",
                                        height: "1px",
                                        background:
                                          "linear-gradient(90deg, transparent, #cbd5e1, transparent)",
                                        margin: "4px auto",
                                        opacity: "0.5",
                                      }}
                                    ></div>

                                    {/* Target Price (Bottom) */}
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "20px 1fr",
                                        alignItems: "center",
                                        justifyItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.7em",
                                          opacity: "0.6",
                                          color: "#D67A1F",
                                          fontWeight: "600",
                                        }}
                                      >
                                        T
                                      </span>
                                      <div style={{ textAlign: "center" }}>
                                        {hasTargetData
                                          ? (formatNumber(targetPrice) || (
                                            <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
                                              N/A
                                            </span>
                                          ))
                                          : (
                                            <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>
                                              N/A
                                            </span>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              } else if (col === "Target Price") {
                                // content = <></>;
                                content = (
                                  <div style={{ display: "none" }}></div>
                                );
                              }


                              else if (col === "Target %") {
                                const hasTargetData = !!holding?.hasTargetData;
                                const target = parseFloat(holding.targetPrice);
                                const initial = parseFloat(
                                  holding.initialPrice,
                                );

                                const scripCode = holding.ScripCode;
                                const livePrice =
                                  livePriceCache.current[scripCode];
                                const currentPrice =
                                  livePrice || holding["Current Price"];
                                const reasonText =
                                  holding.targetReason ||
                                  holding.target_reason ||
                                  holding.reason ||
                                  "";
                                const reasonDetailsText =
                                  holding.targetReasonDetails ||
                                  holding.target_reason_details ||
                                  holding.reason_details ||
                                  "";
                                const hasReason = !!String(reasonText).trim();

                                if (
                                  !hasTargetData ||
                                  isNaN(target) ||
                                  target <= 0 ||
                                  isNaN(initial) ||
                                  target === initial
                                ) {
                                  content = (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip className="common__tooltip">
                                            Set Target
                                          </Tooltip>
                                        }
                                      >
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          aria-label="Set Target"
                                          style={{
                                            padding: "5px 10px",
                                            background: "#EBF2FA",
                                            borderRadius: "5px",
                                            border: "1px solid #BAD0FD",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            lineHeight: 1,
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openSetTargetModal(
                                              holding,
                                              livePrice ?? currentPrice,
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (
                                              e.key === "Enter" ||
                                              e.key === " "
                                            ) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              openSetTargetModal(
                                                holding,
                                                livePrice ?? currentPrice,
                                              );
                                            }
                                          }}
                                        >
                                          <TbTarget size={16} />
                                        </div>
                                      </Whisper>
                                    </div>
                                  );
                                } else {
                                  const percent =
                                    ((parseFloat(currentPrice) - target) /
                                      target) *
                                    100 +
                                    100;
                                  if (!Number.isFinite(percent)) {
                                    content = (
                                      <span
                                        className="medium_font dark_grey_color"
                                        style={{ color: "#9CA3AF", fontStyle: "italic" }}
                                      >
                                        N/A
                                      </span>
                                    );
                                  } else {
                                    // Darker gradient with better contrast on white
                                    const getTargetColor = (percentage) => {
                                      const clampedPercent = Math.max(
                                        0,
                                        Math.min(100, percentage),
                                      );

                                      const colors = [
                                        { max: 10, color: "#C62828" }, // Dark Red
                                        { max: 20, color: "#D84315" }, // Dark Orange-Red
                                        { max: 30, color: "#E65100" }, // Dark Orange
                                        { max: 40, color: "#EF6C00" }, // Orange
                                        { max: 50, color: "#F57C00" }, // Amber
                                        { max: 60, color: "#F9A825" }, // Dark Yellow
                                        { max: 70, color: "#AFB42B" }, // Olive/Lime
                                        { max: 80, color: "#7CB342" }, // Light Green
                                        { max: 90, color: "#43A047" }, // Green
                                        { max: 100, color: "#2E7D32" }, // Dark Green
                                      ];

                                      const range = colors.find(
                                        (c) => clampedPercent <= c.max,
                                      );
                                      return range ? range.color : "#1B5E20";
                                    };

                                    const textColor = getTargetColor(percent);

                                    content = (
                                      <div
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 6,
                                        }}
                                      >
                                        <span
                                          className="medium_font"
                                          style={{
                                            color: textColor,
                                            fontWeight: "600",
                                          }}
                                        >
                                          {percent.toFixed(2)}%
                                        </span>

                                        {hasReason ? (
                                          <Whisper
                                            placement="top"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip
                                                className="common__tooltip"
                                                style={{ maxWidth: "320px" }}
                                              >
                                                <div>
                                                  <b>Reason:</b>{" "}
                                                  {String(reasonText).trim()}
                                                </div>
                                                <div style={{ marginTop: "4px" }}>
                                                  <b>Details:</b>{" "}
                                                  {String(
                                                    reasonDetailsText,
                                                  ).trim() ||
                                                    "No additional details provided."}
                                                </div>
                                              </Tooltip>
                                            }
                                          >
                                            <button
                                              type="button"
                                              data-bs-toggle="modal"
                                              data-bs-target="#TargetReasonDetailsModal"
                                              aria-label="View target reason details"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setTargetReasonModalData({
                                                  stockName:
                                                    holding.FullName ||
                                                    holding.Symbol ||
                                                    holding.name ||
                                                    "-",
                                                  reason:
                                                    String(reasonText).trim(),
                                                  details:
                                                    String(
                                                      reasonDetailsText,
                                                    ).trim(),
                                                });
                                              }}
                                              style={{
                                                width: "16px",
                                                height: "16px",
                                                minWidth: "16px",
                                                borderRadius: "50%",
                                                border: "1px solid #CBD5E1",
                                                background: "#FFFFFF",
                                                color: "#475569",
                                                fontSize: "11px",
                                                fontWeight: 700,
                                                lineHeight: 1,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                padding: 0,
                                              }}
                                            >
                                              !
                                            </button>
                                          </Whisper>
                                        ) : null}
                                      </div>
                                    );
                                  }
                                }
                              } else if (col === "Actions") {
                                content = (
                                  <ul
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    className="table__action_hover"
                                    style={{ top: "0", paddingRight: "10px", border: "none", height: "98%" }}
                                  >
                                    <li>
                                      <div className="buy-sell-segmented">
                                        <button
                                          className="btn_buy wl-premium-action-btn wl-premium-buy"
                                          type="button"
                                          data-bs-toggle="modal"
                                          data-bs-target="#BuySellModal"
                                          onClick={() => {
                                            SettingBuyModalDetails(
                                              holding,
                                              "Buy",
                                            );
                                          }}
                                        >
                                          {tr("buy")}
                                        </button>

                                        <button
                                          className="btn_sell wl-premium-action-btn wl-premium-sell"
                                          type="button"
                                          data-bs-toggle="modal"
                                          data-bs-target="#BuySellModal"
                                          onClick={() => {
                                            SettingBuyModalDetails(
                                              holding,
                                              "Sell",
                                            );
                                          }}
                                        >
                                          {tr("sell")}
                                        </button>
                                      </div>
                                    </li>
                                    {activeBrokerFlag !== "grow" && (
                                      <Whisper
                                        placement="top"
                                        trigger="hover"
                                        speaker={
                                          <Tooltip className="common__tooltip">
                                            {tr("marketDepth")}
                                          </Tooltip>
                                        }
                                      >
                                        <li>
                                          <button
                                            ref={marketDepthButtonRef}
                                            onClick={(e) =>
                                              handleMarketDepthClick(holding, e)
                                            }
                                            className="btn_trading wl-premium-action-btn wl-premium-depth"
                                            style={{
                                              width: "38px",
                                              height: "34px",
                                              borderRadius: "8px",
                                              border: "none",
                                              background: "transparent",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              padding: 0,
                                            }}
                                          >
                                            <MarketDepthIcon />
                                          </button>
                                        </li>
                                      </Whisper>
                                    )}
                                  </ul>
                                );
                              } else {
                                const value =
                                  holding[col] ??
                                  resolveAdditionalColumnValue(
                                    holding?.additionalData,
                                    col,
                                  );
                                const hasValue =
                                  value !== undefined &&
                                  value !== null &&
                                  value !== "";
                                const numericValue = Number(value);
                                const isNumericValue =
                                  hasValue &&
                                  Number.isFinite(numericValue) &&
                                  String(value).trim() !== "";
                                content =
                                  hasValue
                                    ? isNumericValue
                                      ? formatNumber(numericValue)
                                      : value
                                    : (
                                      <span
                                        style={{
                                          color: "#9CA3AF",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        N/A
                                      </span>
                                    );
                                content = (
                                  <div
                                    style={{
                                      textAlign: [
                                        "ProfitPct",
                                        "Volume",
                                      ].includes(col)
                                        ? "center"
                                        : "center",
                                    }}
                                  >
                                    {content}
                                  </div>
                                );
                              }

                              return (
                                <td
                                  key={col}
                                  className={`${col === "Actions" ? "action-cell wl-pinned-actions" : ""
                                    } ${pinnedColumns[col] === "left"
                                      ? "wl-pinned-left"
                                      : pinnedColumns[col] === "right"
                                        ? "wl-pinned-right"
                                        : ""
                                    }`.trim()}
                                  style={{
                                    boxSizing: "border-box",
                                    ...(pinnedColumns[col] || col === "Actions" || col === "Name"
                                      ? {
                                        width: `${getColumnDisplayWidth(col)}px`,
                                        minWidth: `${getColumnDisplayWidth(col)}px`,
                                        maxWidth: `${getColumnDisplayWidth(col)}px`,
                                      }
                                      : {}),
                                    textAlign:
                                      col === "Name"
                                        ? "left"
                                        : ["Profit %", "Volume"].includes(col)
                                          ? "center"
                                          : "center",
                                    width: columnWidths[col]
                                      ? `${columnWidths[col]}px`
                                      : undefined,
                                    minWidth: columnWidths[col]
                                      ? `${columnWidths[col]}px`
                                      : undefined,
                                    maxWidth: columnWidths[col]
                                      ? `${columnWidths[col]}px`
                                      : undefined,
                                    paddingLeft:
                                      col === "Actions" ? 0 : undefined,
                                    paddingRight:
                                      col === "Actions" ? 0 : undefined,
                                    display: [
                                      "TodaysLow",
                                      "Profit %",
                                      "52WeekLow",
                                      "Target Price",
                                      "Quantity",
                                    ].includes(col)
                                      ? "none"
                                      : undefined,
                                    background:
                                      col === "Actions"
                                        ? "#fff"
                                        : pinnedColumns[col]
                                          ? "#ffffff"
                                          : undefined,
                                    position:
                                      col === "Actions" || pinnedColumns[col]
                                        ? "sticky"
                                        : undefined,
                                    left:
                                      pinnedColumns[col] === "left"
                                        ? `${pinnedOffsets.left[col] || 0}px`
                                        : undefined,
                                    right:
                                      col === "Actions"
                                        ? "0px"
                                        : pinnedColumns[col] === "right"
                                          ? `${pinnedOffsets.right[col] || 0}px`
                                          : undefined,
                                    zIndex:
                                      col === "Actions"
                                        ? 35
                                        : pinnedColumns[col]
                                          ? 32
                                          : undefined,
                                    overflow:
                                      col === "Name" || pinnedColumns[col]
                                        ? "hidden"
                                        : undefined,
                                  }}
                                >
                                  {content}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            {/* {!isLoading && ( */}
            {!isLoading && holdings.length > 0 && (
              <div className="table__footer_row">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <h6>
                    {indexOfFirstItem + 1} to{" "}
                    {Math.min(indexOfLastItem, filteredHoldings.length)} of{" "}
                    {filteredHoldings.length} Items
                  </h6>
                  <TablePageSizeSelect
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  />
                </div>

                <nav aria-label="Page navigation example">
                  <ul className="pagination justify-content-center">
                    {/* First Page Button */}
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() => currentPage > 1 && handlePageChange(1)}
                        style={{
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        }}
                        title="First Page"
                      >
                        <MdKeyboardDoubleArrowLeft
                          style={{ fontSize: "18px" }}
                        />
                      </a>
                    </li>

                    {/* Previous Page Button */}
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() =>
                          currentPage > 1 && handlePageChange(currentPage - 1)
                        }
                        style={{
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        }}
                        title="Previous Page"
                      >
                        <IoIosArrowBack />
                      </a>
                    </li>

                    {/* Page Numbers */}
                    {getPageNumbers().map((pageNum) => (
                      <li
                        key={pageNum}
                        className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                      >
                        <a
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                          style={{ cursor: "pointer" }}
                        >
                          {pageNum}
                        </a>
                      </li>
                    ))}

                    {/* Next Page Button */}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() =>
                          currentPage < totalPages &&
                          handlePageChange(currentPage + 1)
                        }
                        style={{
                          cursor:
                            currentPage === totalPages
                              ? "not-allowed"
                              : "pointer",
                        }}
                        title="Next Page"
                      >
                        <IoIosArrowForward />
                      </a>
                    </li>

                    {/* Last Page Button */}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                    >
                      <a
                        className="page-link"
                        onClick={() =>
                          currentPage < totalPages &&
                          handlePageChange(totalPages)
                        }
                        style={{
                          cursor:
                            currentPage === totalPages
                              ? "not-allowed"
                              : "pointer",
                        }}
                        title="Last Page"
                      >
                        <MdKeyboardDoubleArrowRight
                          style={{ fontSize: "18px" }}
                        />
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Buy Sell Modal */}
      <div
        className="modal fade modal_md modern-trading-modal"
        id="BuySellModal"
        aria-labelledby="BuySellModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-modern">
          <div className="modal-content modal-content-modern">
            {/* Modal Header */}
            <div className="modal-header modal-header-modern">
              <div className="modal-header-content">
                <div className="modal-title-wrapper">
                  <div className={`trading-type-badge ${buySellModalDetails.Type.toLowerCase()}`}>
                    <img
                      src={
                        buySellModalDetails.Type === "Buy"
                          ? ArrowTrendUp
                          : ArrowTrendDown
                      }
                      className="trading-icon"
                      alt="icon"
                    />
                    <span className="trading-type-text">{buySellModalDetails.Type}</span>
                  </div>
                  <h3 className="modal-title-modern">
                    {buySellModalDetails.Symbol}
                  </h3>
                </div>
                <button
                  type="button"
                  className="btn-close-modern"
                  data-bs-dismiss="modal"
                  aria-label={tr("close")}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body modal-body-modern">
              <AnimatePresence mode="wait">
                {/* Screen 1 */}
                {!isPreview && (
                  <motion.div
                    key="screen1"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <div
                      className="sell__content"
                      style={{
                        backgroundColor:
                          buySellModalDetails.Type === "Buy"
                            ? "#F6FCF9"
                            : "#fff7f7",
                        border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`,
                      }}
                    >
                      <div className="sell__content_inner d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">{buySellModalDetails.Symbol}</h5>
                          <h6 className="mb-0">
                            {buySellModalDetails.FullName}
                          </h6>
                        </div>
                        {/* LivePrice/ Current Price */}
                        <p className="mb-0">
                          {(() => {
                            const cacheKey = getOrderLivePriceCacheKey(
                              buySellModalDetails.order,
                            );

                            const livePrice = livePriceCache.current[cacheKey];
                            const pClose = livePCloseCache.current[cacheKey];
                            const changePercent =
                              liveChangePercentageCache.current[cacheKey];

                            const currentPrice =
                              livePrice ||
                              buySellModalDetails.order?.CurrentPrice ||
                              0;
                            const previousClose =
                              pClose ||
                              buySellModalDetails.order?.CurrentPrice ||
                              0;
                            const actualChangePercent = changePercent || 0;

                            const isPositive =
                              parseFloat(currentPrice) >=
                              parseFloat(previousClose);

                            return (
                              <>
                                ₹ {formatNumber(currentPrice)} <br />
                                <span
                                  className={
                                    isPositive ? "text-success" : "text-danger"
                                  }
                                >
                                  ({isPositive ? "+" : ""}
                                  {actualChangePercent
                                    ? actualChangePercent.toFixed(2)
                                    : "0.00"}
                                  %)
                                  <i
                                    className={`icon-arrow-${isPositive ? "up" : "down"} ms-1`}
                                    style={{ fontSize: "0.8em" }}
                                  ></i>
                                </span>
                              </>
                            );
                          })()}
                        </p>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("orderType")}</label>
                        <SelectPicker
                          data={OrderType}
                          searchable={false}
                          container={document.body}
                          className="custom-select-picker"
                          value={orderType}
                          onChange={setOrderType}
                        />
                      </div>

                      {/* <div className="col-md-6">
                                    <label className="form-label">Qty</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={qty}
                                      onChange={(e) => setQty(Number(e.target.value))}
                                    />
                                  </div> */}
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '6px' }}>{tr("qty")}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={buySellModalDetails.OrderQty}
                          onChange={(e) =>
                            setBuySellModalDetails((prev) => ({
                              ...prev,
                              OrderQty: Number(e.target.value),
                            }))
                          }
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "10px",
                            border: "1px solid #d5dfed",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#0f172a",
                            outline: "none",
                            boxShadow: "none"
                          }}
                        />
                      </div>
                    </div>

                    {/* {orderType === "Limit Order" && (
                                  <div className="mt-3">
                                    <label className="form-label">Limit Price</label>
                                    <input type="number" className="form-control" />
                                  </div>
                                )} */}
                    {orderType === "Limit Order" && (
                      <div className="mt-3">
                        <label className="form-label">{tr("limitPrice")}</label>
                        <input
                          type="number"
                          className="form-control"
                          value={buySellModalDetails.LimitPrice}
                          onChange={(e) =>
                            setBuySellModalDetails((prev) => ({
                              ...prev,
                              LimitPrice: Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                    )}

                    {buySellModalDetails.OrderQty > 0 && (
                      <motion.div
                        className="order-summary mt-4 p-3 border roundedX"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          backgroundColor:
                            buySellModalDetails.Type === "Buy"
                              ? "#F6FCF9"
                              : "#fff7f7",
                          border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`,
                        }}
                      >
                        <h6 className="fw-bold mb-3">{tr("orderSummary")}</h6>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("orderType")}</span>
                          <span className="value">{orderType}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("quantity")}</span>
                          <span className="value">
                            {buySellModalDetails.OrderQty} {tr("shares")}
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("pricePerShare")}</span>
                          <span className="value">
                            ₹{formatNumber(getPriceForCalculation())}
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("subtotal")}</span>
                          <span className="value">
                            ₹
                            {formatNumber(
                              buySellModalDetails.OrderQty *
                              getPriceForCalculation(),
                            )}
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("estFees")}</span>
                          <span className="value">₹0.00</span>
                        </div>
                        <hr />
                        <div className="order-total d-flex justify-content-between fw-bold">
                          <span>{tr("totalAmount")}</span>
                          <span>
                            ₹
                            {formatNumber(
                              buySellModalDetails.OrderQty *
                              getPriceForCalculation(),
                            )}
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {/* Screen 2 */}
                {/* {isPreview && (
                              <motion.div
                                key="screen2"
                                initial={{ y: 100, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -100, opacity: 0 }}
                                transition={{ duration: 0.4, ease: "easeInOut" }}
                              >
                                <div className="text-center mb-3">
                                  <div className="preview_order_icon preview_buy_order_icon">
                                    <img src={ArrowTrendDown} alt="icon" />
                                  </div>
                                  <h5>Confirm Order</h5>
                                  <p>Please review your order details</p>
                                </div>
            
                                <div className="order-summary-content">
                                  <div className="order-row">
                                    <span className="label">Quantity</span>
                                    <span className="value">{buySellModalDetails.OrderQty} shares</span>
                                  </div>
            
                                  <div className="order-row">
                                    <span className="label">Price per share</span>
                                    <span className="value">
                                      {(() => {
                                        const scripCode = buySellModalDetails.order?.Exch === "N" 
                                          ? buySellModalDetails.order?.NseCode 
                                          : buySellModalDetails.order?.BseCode;
                                        const livePrice = livePriceCache.current[scripCode];
                                        const currentPrice = livePrice || buySellModalDetails.order?.CurrentPrice || 0;
                                        
                                        return `₹${formatNumber(currentPrice)}`;
                                      })()}
                                    </span>
                                  </div>
            
                                  <div className="order-row">
                                    <span className="label">Subtotal</span>
                                    <span className="value capitalize">
                                      {(() => {
                                        const scripCode = buySellModalDetails.order?.Exch === "N" 
                                          ? buySellModalDetails.order?.NseCode 
                                          : buySellModalDetails.order?.BseCode;
                                        const livePrice = livePriceCache.current[scripCode];
                                        const currentPrice = livePrice || buySellModalDetails.order?.CurrentPrice || 0;
                                        const subtotal = buySellModalDetails.OrderQty * parseFloat(currentPrice);
                                        
                                        return `₹${subtotal.toFixed(2)}`;
                                      })()}
                                    </span>
                                  </div>
            
            
            
                                 <div className="order-row">
                                  <span className="label">Est. fees</span>
                                  <span className="value capitalize">₹0.00</span>
                                </div>
                                <div className="divider"></div>
                                <div className="order-total">
                                  <span className="total-label">Total Amount</span>
                                  <span className="total-value">
                                    {(() => {
                                      const scripCode = buySellModalDetails.order?.Exch === "N" 
                                        ? buySellModalDetails.order?.NseCode 
                                        : buySellModalDetails.order?.BseCode;
                                      const livePrice = livePriceCache.current[scripCode];
                                      const currentPrice = livePrice || buySellModalDetails.order?.CurrentPrice || 0;
                                      const subtotal = buySellModalDetails.OrderQty * parseFloat(currentPrice);
                                      const total = subtotal + 0.00;
                                      
                                      return `₹${total.toFixed(2)}`;
                                    })()}
                                  </span>
                                </div>
            
                                </div>
                              </motion.div>
                            )} */}
                {/* Screen 2 */}
                {isPreview && (
                  <motion.div
                    key="screen2"
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <div className="text-center mb-3">
                      <div
                        className="preview_order_icon preview_buy_order_icon"
                        style={{
                          backgroundColor:
                            buySellModalDetails.Type === "Buy"
                              ? "#E1FBE9"
                              : "#fff7f7",
                        }}
                      >
                        {/* <img src={ArrowTrendDown} alt="icon" /> */}
                        <img
                          src={
                            buySellModalDetails.Type === "Buy"
                              ? ArrowTrendUp
                              : ArrowTrendDown
                          }
                          alt="icon"
                        />
                      </div>
                      <h5>{tr("confirmOrder")}</h5>
                      <p>{tr("pleaseReviewOrderDetails")}</p>
                    </div>

                    <div className="order-summary-content">
                      <div className="order-row">
                        <span className="label">{tr("orderType")}</span>
                        <span className="value">{orderType}</span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("quantity")}</span>
                        <span className="value">
                          {buySellModalDetails.OrderQty} {tr("shares")}
                        </span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("pricePerShare")}</span>
                        <span className="value">
                          ₹{formatNumber(getPriceForCalculation())}
                        </span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("subtotal")}</span>
                        <span className="value capitalize">
                          ₹
                          {formatNumber(
                            buySellModalDetails.OrderQty *
                            getPriceForCalculation(),
                          )}
                        </span>
                      </div>

                      <div className="order-row">
                        <span className="label">{tr("estFees")}</span>
                        <span className="value capitalize">₹0.00</span>
                      </div>

                      <div className="divider"></div>

                      <div className="order-total">
                        <span className="total-label">{tr("totalAmount")}</span>
                        <span className="total-value">
                          ₹
                          {formatNumber(
                            buySellModalDetails.OrderQty *
                            getPriceForCalculation(),
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="modal-footer modal__footer_btn">
              {!isPreview ? (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    gap: "0.75rem",
                  }}
                >
                  <button
                    type="button"
                    className="btn-modern btn-secondary-modern"
                    data-bs-dismiss="modal"
                  >
                    {tr("cancel")}
                  </button>

                  <button
                    type="button"
                    className="btn-modern"
                    onClick={handlePreview}
                  >
                    {tr("previewOrder")}
                  </button>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      gap: "0.75rem",
                    }}
                  >
                    <button
                      type="button"
                      className="btn-modern btn-secondary-modern"
                      onClick={handleGoBack}
                    >
                      {tr("goBack")}
                    </button>

                    <button
                      type="button"
                      className={`btn-modern btn-success-modern ${buySellModalDetails.Type === 'Buy' ? 'buy' : 'sell'}`}
                      onClick={(e) => {
                        fetchDataPortfolio(
                          buySellModalDetails.order,
                          buySellModalDetails.Type,
                          "Portfolio",
                        );
                        e.target.disabled = true;
                        e.target.textContent = tr("processing");

                        setTimeout(() => {
                          setIsPreview(false); // or handleGoBack()
                          e.target.disabled = false;
                          e.target.textContent = tr("confirmOrder");
                        }, 1000);
                      }}
                      data-bs-dismiss="modal"
                    >
                      {tr("confirmOrder")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MarketDepth Modal */}
      {/* Market Depth Popover */}
      {showMarketDepth && (
        <MarketDepthPopover
          popoverRef={marketDepthPopoverRef}
          position={marketDepthPosition}
          depthRows={currentMDModal ? marketDepth || [] : []}
          onClose={closeMarketDepthPopover}
          tr={tr}
          formatNumber={formatNumber}
        />
      )}

      {showAdditionalColumnsDropdown &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={additionalColumnsDropdownRef}
            className="watchlist-columns-dropdown"
            style={{
              position: "fixed",
              top: additionalColumnsDropdownPos.top,
              left: additionalColumnsDropdownPos.left,
              width: 320,
              background: "#ffffff",
              border: "1px solid #dce6f3",
              borderRadius: 12,
              boxShadow: "0 16px 36px rgba(15, 23, 42, 0.18)",
              padding: 12,
              zIndex: 1500,
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2d42" }}>
                  {tr("additionalColumns")}
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    await resetColumnOrder();
                    setShowAdditionalColumnsDropdown(false);
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#2457bf",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {tr("reset")}
                </button>
              </div>
              <div className="column-picker-search">
                <div className="column-picker-search-inner">
                  <i className="icon-search" style={{ fontSize: "14px", color: "#64748b" }} />
                  <input
                    type="text"
                    placeholder={tr("searchColumnsPlaceholder")}
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    className="column-picker-search-input"
                  />
                </div>
              </div>
            </div>

            <div
              className="column-picker-list-scroll"
              ref={additionalColumnsScrollRef}
              style={{ maxHeight: 300 }}
            >
              {filteredColumns.length === 0 ? (
                <div className="column-picker-empty">{tr("noColumnsMatchSearch")}</div>
              ) : (
                filteredColumns.map((col, index) => (
                  <label
                    key={col.colName}
                    className="column-picker-row"
                    htmlFor={`portfolio-inline-col-check-${index}`}
                  >
                    <input
                      type="checkbox"
                      value={col.colName}
                      id={`portfolio-inline-col-check-${index}`}
                      checked={tempCheckedNames.includes(col.colName)}
                      onChange={(e) =>
                        handleCheckboxChange(col.colName, e.target.checked)
                      }
                    />
                    <span className="column-picker-row-label">{col.UIName}</span>
                  </label>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}

      <SetTargetModal
        modalId="SetTargetModal"
        stock={targetDetails}
        tr={tr}
        formatNumber={formatNumber}
        apiBaseUrl={pmsApiBaseUrl}
        source="portfolio"
        userId={sharedData?.ID}
        brokerType={sharedData?.broker_type || localStorage.getItem("broker_type") || ""}
        onSuccess={() => {
          setUpdatePortfolioTrigger((prev) => prev + 1);
        }}
        showSuccessToast={showSuccessToast}
        showErrorToast={showErrorToast}
        showWarningToast={showWarningToast}
      />

      {/* Additional Columns Modal */}
      <div
        className="modal fade column-picker-modal"
        id="AdditionalColumnListModal"
        aria-labelledby="ScrollableListModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content shadow-lg border-0 rounded-4"
            style={{ backgroundColor: "#FFFFFF" }}
          >
            {/* Header */}
            <div className="modal-header border-0 column-picker-modal-header">
              <h5
                className="modal-title column-picker-title"
                id="ScrollableListModalLabel"
              >
                {tr("showHideColumns")}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body column-picker-modal-body">
              <div className="column-picker-search">
                <div className="column-picker-search-inner">
                  <i
                    className="icon-search"
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  />
                  <input
                    type="text"
                    placeholder={tr("searchColumnsPlaceholder")}
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    className="column-picker-search-input"
                  />
                </div>
              </div>

              <div
                className="column-picker-list-scroll"
                ref={additionalColumnsScrollRef}
              >
                {filteredColumns.length === 0 ? (
                  <div className="column-picker-empty">
                    {tr("noColumnsMatchSearch")}
                  </div>
                ) : (
                  filteredColumns.map((col, index) => (
                    <label
                      key={col.colName}
                      className="column-picker-row"
                      htmlFor={`col-check-portfolio-${index}`}
                    >
                      <input
                        type="checkbox"
                        value={col.colName}
                        id={`col-check-portfolio-${index}`}
                        checked={tempCheckedNames.includes(col.colName)}
                        onChange={(e) =>
                          handleCheckboxChange(col.colName, e.target.checked)
                        }
                      />
                      <span className="column-picker-row-label">
                        {col.UIName}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer border-0 column-picker-modal-footer">
              <button
                type="button"
                className="column-picker-btn column-picker-btn--secondary"
                data-bs-dismiss="modal"
                onClick={async () => {
                  await resetColumnOrder(); // Call the API reset function
                  setTempCheckedNames([]); // Clear checked columns state
                }}
              >
                {tr("reset")}
              </button>

              <button
                type="button"
                className="column-picker-btn column-picker-btn--secondary"
                data-bs-dismiss="modal"
              >
                {tr("cancel")}
              </button>

              <button
                type="button"
                className="column-picker-btn column-picker-btn--primary"
                onClick={handleAddColumnsAndClose} // modal close handled inside
              >
                {tr("applyChanges")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Target Reason Details Modal */}
      <div
        className="modal fade"
        id="TargetReasonDetailsModal"
        aria-labelledby="TargetReasonDetailsModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content border-0"
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "14px",
              boxShadow: "0 18px 40px rgba(0, 0, 0, 0.16)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#f5f6ff",
                padding: "1.2rem 1.3rem",
                position: "relative",
              }}
            >
              <button
                type="button"
                className="btn-close position-absolute"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                style={{ top: "0.9rem", right: "0.9rem", opacity: 0.6, fontSize: "0.7rem" }}
              ></button>
              <h5
                className="mb-1"
                id="TargetReasonDetailsModalLabel"
                style={{
                  color: "#000",
                  fontSize: "1rem",
                  fontWeight: 600,
                  marginRight: "2rem",
                }}
              >
                Target Reason
              </h5>
              <p
                className="mb-0"
                style={{ color: "#757575", fontSize: "0.88rem" }}
              >
                {targetReasonModalData.stockName || "-"}
              </p>
            </div>

            <div style={{ padding: "1.2rem 1.3rem 1.3rem 1.3rem" }}>
              <div style={{ marginBottom: "0.9rem" }}>
                <div
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#475569",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Reason
                </div>
                <div
                  style={{
                    marginTop: "0.35rem",
                    fontSize: "0.95rem",
                    color: "#1F2937",
                    fontWeight: 600,
                  }}
                >
                  {targetReasonModalData.reason || "-"}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "#475569",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Details
                </div>
                <div
                  style={{
                    marginTop: "0.4rem",
                    fontSize: "0.92rem",
                    color: "#334155",
                    lineHeight: 1.5,
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: "10px",
                    padding: "0.75rem 0.85rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {targetReasonModalData.details ||
                    "No additional details provided."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CDSL Authorization Modal */}

      <button
        id="openCDSLModalBtn"
        type="button"
        class="d-none"
        data-bs-toggle="modal"
        data-bs-target="#CDSLAuthorizationModal"
      ></button>

      <div
        className="modal fade"
        id="CDSLAuthorizationModal"
        aria-labelledby="CDSLAuthorizationModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div
            className="modal-content shadow-lg rounded-4"
            style={{
              backgroundColor: "#ffffffff",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              border: "2px solid #ff6b35",
            }}
          >
            <div
              className="modal-header pb-0"
              style={{ borderBottom: "2px solid #ff6b35" }}
            >
              <h5
                className="modal-title"
                id="CDSLAuthorizationModalLabel"
                style={{
                  color: "#212529",
                  fontWeight: "700",
                  fontSize: "1.5rem",
                  paddingBottom: "12px",
                }}
              >
                Authorize CDSL Access
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label={tr("close")}
                style={{ filter: "brightness(0.5)" }}
              />
            </div>

            <div
              className="modal-body py-4 text-center"
              style={{ color: "#2e3031ff" }}
            >
              <div className="mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  fill="#ff6b35"
                  className="bi bi-shield-lock"
                  viewBox="0 0 16 16"
                >
                  <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                  <path d="M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415z" />
                </svg>
              </div>

              <h3
                style={{
                  fontWeight: "600",
                  marginBottom: "1rem",
                  color: "#212529",
                }}
              >
                CDSL Sell Authorization Required
              </h3>

              <p
                style={{
                  fontSize: "1.05rem",
                  marginBottom: "2rem",
                  lineHeight: "1.6",
                }}
              >
                Authorize CDSL access to enable stock selling functionality.
                Without permission this feature will not be available.
              </p>

              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 py-2 fw-semibold"
                  data-bs-dismiss="modal"
                  style={{
                    borderRadius: "30px",
                    fontSize: "1rem",
                    letterSpacing: "0.03em",
                  }}
                >
                  {tr("cancel")}
                </button>

                <form
                  name="CDSLAuthForm"
                  method="post"
                  action="https://dev-openapi.5paisa.com/WebVendorLogin/EDISAuthorization/Authorization"
                  target="_blank"
                  style={{ margin: 0 }}
                >
                  <input
                    type="hidden"
                    name="AccessToken"
                    value={sharedData.accessToken}
                  />
                  <input
                    type="hidden"
                    name="ClientCode"
                    value={sharedData.ClientCode}
                  />
                  <input
                    type="hidden"
                    name="VendorKey"
                    value={sharedData.api_key}
                  />
                  <input
                    type="hidden"
                    name="ResponseURL"
                    value={`https://beta.infomanav.in/zerodhaReact/authorization/${sharedData.ID}`}
                  />

                  <button
                    type="submit"
                    className="btn px-4 py-2 fw-semibold"
                    style={{
                      borderRadius: "30px",
                      fontSize: "1rem",
                      letterSpacing: "0.03em",
                      backgroundColor: "#ff6b35",
                      color: "white",
                      border: "none",
                    }}
                    data-bs-dismiss="modal"
                  >
                    Authorize
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        id="openGrowwTPINModalBtn"
        type="button"
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#GrowwTPINModal"
      ></button>

<div
  className="modal fade"
  id="GrowwTPINModal"
  aria-labelledby="GrowwTPINModalLabel"
  aria-hidden="true"
  data-bs-backdrop="static"
  data-bs-keyboard="false"
>
  <div className="modal-dialog modal-dialog-centered modal-md">
    <div
      className="modal-content shadow rounded-4 border-0"
      style={{
        backgroundColor: "#FFFFFF",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* HEADER */}
      <div
        className="modal-header border-0"
        style={{ backgroundColor: "#EEF4FB", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}
      >
        <h5
          className="modal-title fw-bold"
          id="GrowwTPINModalLabel"
          style={{ color: "#2457BF" }}
        >
          TPIN Verification Required
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label={tr("close")}
        />
      </div>

      {/* BODY */}
      <div className="modal-body text-center px-4 py-4">
        {/* ICON / ACCENT */}
        <div
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backgroundColor: "#FFF3E9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <span style={{ fontSize: "28px", color: "#EE8724" }}>!</span>
        </div>

        <h4 className="fw-semibold mb-2" style={{ color: "#212529" }}>
          Sell Order Failed
        </h4>

        <p style={{ color: "#495057", marginBottom: "10px" }}>
          {growwTpinErrorText ||
            "You need to verify your holdings using TPIN before placing a sell order."}
        </p>

        <p style={{ fontSize: "0.9rem", color: "#6c757d", marginBottom: "24px" }}>
          Complete verification on Groww, then try again.
        </p>

        {/* ACTIONS */}
        <div className="d-flex justify-content-center gap-3">
          <button
            type="button"
            className="btn px-4"
            data-bs-dismiss="modal"
            style={{
              borderRadius: "25px",
              border: "1px solid #ced4da",
              color: "#495057",
              background: "#fff",
            }}
          >
            {tr("cancel")}
          </button>

          <a
            href="https://groww.in/cdslauth"
            target="_blank"
            rel="noopener noreferrer"
            className="btn px-4"
            style={{
              borderRadius: "25px",
              backgroundColor: "#2457BF",
              color: "#fff",
              fontWeight: "600",
            }}
          >
            Verify Now
          </a>
        </div>
      </div>
    </div>
  </div>
</div>

<StockCandleModal
        isOpen={!!selectedStock}
        onClose={() => setSelectedStock(null)}
        stock={selectedStock}
        Exch={selectedStock?.Exch}
        ExchType={selectedStock?.ExchType}
        ExchSeg={selectedStock?.ExchSeg || selectedStock?.exchange}
        ScripCode={
          selectedStock?.ScripCode ||
          selectedStock?.scripcode ||
          selectedStock?.Scripcode
        }
        symbolToken={
          selectedStock?.token ||
          selectedStock?.symboltoken ||
          selectedStock?.Token ||
          selectedStock?.scripcode ||
          selectedStock?.ScripCode
        }
        Name={selectedStock?.FullName || selectedStock?.name}
        Symbol={selectedStock?.Symbol || selectedStock?.tradingsymbol}
        onBuy={(holding) =>
          openBuySellFromStockPreview(holding || selectedStock, "Buy")
        }
        onSell={(holding) =>
          openBuySellFromStockPreview(holding || selectedStock, "Sell")
        }
      />



    </>
  );
}

export default Portfolio;
